import { supabase, formatSupabaseError } from '../../supabase';
import { stampCreatedAt } from '../../utils/dbTimestamps';
import { ensureGymId, getCachedGymId } from './gymContext';
import {
  rowToMember,
  formToMemberRow,
  rowToMembership,
  rowToFreeze,
  rowToAttendance,
  rowToPayment,
  rowToGuestPass,
  rowToLocker,
  rowToEquipment,
  rowToClass,
  rowToTrainer,
  rowToReceptionist,
  rowToPackage,
} from './mappers';
import { fetchBranches } from '../gym/catalogApi';
import {
  combineMethodLabels,
  mergePaymentSplits,
  methodKeyFromLabel,
  normalizeSplitRows,
  parsePaymentSplitsFromRow,
  serializePaymentSplits,
  sumPaymentSplits,
} from '../../utils/splitPaymentUtils';
import { normalizeDateFilterToIso } from '../../components/admin/ColumnFilterHeader';
import { resolveInvoiceId } from './invoiceSequence';
import {
  branchScopeCacheSuffix,
  branchesMatch,
  filterByReceptionistBranch,
  getReceptionistBranch,
  getReceptionistBranchFilter,
  isReceptionistPanelScope,
  normalizeBranchName,
} from '../gym/branchContext';
import { paymentTotalDue, saleRowPaid, saleRowLeft, saleRowTotal, saleRowHasRemainingBalance } from '../../pages/receptionist/sales/salePaymentUtils';
import { isNetworkError } from '../offline/network';
import {
  queueDelete,
  queueInsert,
  queueUpdate,
  shouldQueueWrites,
} from '../offline/runMutation';
import {
  addDurationToDate,
  addMonthsToEndDate,
  durationKeyFromLabel,
  periodStartFromEndDate,
} from '../../pages/receptionist/sales/saleDurationUtils';
import {
  formatSerialDisplay,
  getGymMemberId,
  getMemberPublicSerial,
  isPendingGymMemberId,
} from '../../pages/admin/members/memberData';
import { isWalkInMember, isWalkInMemberName, isWalkInGymMemberId } from './walkInMember';
import { getCurrentReceptionistId, setCurrentReceptionistId } from './receptionistContext';
import { cachedQuery, invalidateCache, writeCache } from '../cache/queryCache';
import { fetchMembersPageRpc } from '../shared/membersRpcApi';
import { attachMembershipSalesFromRows } from './membershipSalesAttach';
import { fetchMembershipsPageRpc } from '../shared/membershipsRpcApi';
import { fetchGuestPassPageRpc } from '../shared/guestPassRpcApi';
import { fetchRenewalSalePageRpc } from '../shared/renewalSaleRpcApi';
import { fetchPtSalePageRpc } from '../shared/ptSaleRpcApi';
import { fetchLockerSalePageRpc } from '../shared/lockerSaleRpcApi';
import { fetchSpaSalePageRpc } from '../shared/spaSaleRpcApi';
import { fetchOxybarSalePageRpc } from '../shared/oxybarSaleRpcApi';
import { fetchZumbaSalePageRpc } from '../shared/zumbaSaleRpcApi';
import { fetchYogaSalePageRpc } from '../shared/yogaSaleRpcApi';
import { fetchGymApparelPageRpc } from '../shared/gymApparelRpcApi';
import { fetchMiscellaneousSalePageRpc } from '../shared/miscellaneousSaleRpcApi';
import { fetchRemainingPaymentsPageRpc } from '../shared/remainingPaymentsRpcApi';
import { fetchCustomerLedgerPageRpc } from '../shared/customerLedgerRpcApi';
import { fetchPaymentsListPageRpc } from '../shared/paymentsListRpcApi';
import { pkTodayKey } from '../../utils/pkTime';
import {
  dedupeMembers,
  dedupePeople,
  dedupeLockers,
  dedupeGuestPasses,
  dedupeByName,
  dedupeAttendance,
  dedupeFreezes,
  dedupeMemberships,
} from '../../utils/dedupeList';
import { isMembershipUnexpired } from '../../utils/membershipEligibility';

export { attachMembershipSalesFromRows } from './membershipSalesAttach';

/** Real member serial for receipts — reject dash / pending placeholders. */
function usableMemberSerial(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw === '—' || raw === '-') return '';
  if (isPendingGymMemberId(raw)) return '';
  return formatSerialDisplay(raw) || raw;
}

async function gymId() {
  const cached = getCachedGymId();
  if (cached) return cached;
  return ensureGymId();
}

async function bustGymCache() {
  invalidateCache(`gym:${await gymId()}:`);
}

function cacheKey(base) {
  return `${base}${branchScopeCacheSuffix()}`;
}

function resolveWriteBranch(explicit = '') {
  if (isReceptionistPanelScope()) {
    return getReceptionistBranchFilter() || getReceptionistBranch() || '';
  }
  return String(explicit || '').trim();
}

function stampBranchFields(row = {}, explicitBranch = '') {
  const branch = resolveWriteBranch(explicitBranch || row.branch || row.branch_name);
  if (!branch) return row;
  return {
    ...row,
    // Prefer branch_name (text). `branch` is only for tables that store the name there
    // (lockers/trainers). payments uses branch_name / branch_id — never invent branch_id.
    branch,
    branch_name: branch,
  };
}

function stampPaymentBranchFields(row = {}, explicitBranch = '') {
  const branch = resolveWriteBranch(explicitBranch || row.branch_name || row.branch);
  if (!branch) return row;
  return {
    ...row,
    branch_name: branch,
  };
}

function isUuidValue(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    .test(String(value || '').trim());
}

function dateOrNull(value) {
  const d = String(value || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

function todayDate() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const CUSTOMER_NOTE_PREFIX = 'Customer: ';

function formatPaymentNote(customer, note, memberId) {
  const trimmed = (note || '').trim();
  if (memberId) return trimmed;
  const label = customer || 'Walk-In Customer';
  return trimmed ? `${CUSTOMER_NOTE_PREFIX}${label} — ${trimmed}` : `${CUSTOMER_NOTE_PREFIX}${label}`;
}

async function insertRowSkippingMissingColumns(table, row, select, { preserveColumns = [], replay = false } = {}) {
  // Self-hosted schemas often have created_at NOT NULL without DEFAULT now()
  const payload = stampCreatedAt({ ...row });
  const selectCols = (select && !String(select).includes('(')) ? select : '*';
  const preserve = new Set(preserveColumns);

  if (!replay && shouldQueueWrites()) {
    return queueInsert(table, payload, { preserveColumns, source: 'receptionist-helper' });
  }

  try {
    for (let attempt = 0; attempt < 25; attempt += 1) {
      const { data, error } = await supabase.from(table).insert(payload).select(selectCols);
      if (!error) {
        const rowData = Array.isArray(data) ? data[0] : data;
        if (rowData) return { data: rowData, error: null };

        // Insert likely succeeded but RLS hid the row — caller should re-fetch
        return { data: { ...payload, __insertedHidden: true }, error: null };
      }

      // If created_at was stripped earlier in this loop, put it back once
      if (
        /null value in column ["']?created_at["']?/i.test(error.message || '')
        && (payload.created_at == null || payload.created_at === '')
      ) {
        payload.created_at = new Date().toISOString();
        continue;
      }

      const missing = getMissingColumn(error.message);
      if (missing && Object.prototype.hasOwnProperty.call(payload, missing) && !preserve.has(missing)) {
        delete payload[missing];
        continue;
      }

      // Retry plain insert with no return, then let caller look up
      if (/multiple \(or no\) rows returned|Results contain 0 rows|JSON object requested|embed|relationship/i.test(error.message || '')) {
        const bare = await supabase.from(table).insert(payload);
        if (!bare.error) {
          return { data: { ...payload, __insertedHidden: true }, error: null };
        }
        if (bare.error && isNetworkError(bare.error) && !replay) {
          return queueInsert(table, payload, { preserveColumns, source: 'receptionist-helper' });
        }
        return { data: null, error: bare.error };
      }

      if (isNetworkError(error) && !replay) {
        return queueInsert(table, payload, { preserveColumns, source: 'receptionist-helper' });
      }
      return { data: null, error };
    }

    return { data: null, error: new Error('Insert failed: too many missing database columns.') };
  } catch (error) {
    if (isNetworkError(error) && !replay) {
      return queueInsert(table, payload, { preserveColumns, source: 'receptionist-helper' });
    }
    throw error;
  }
}

function getMissingColumn(message = '') {
  const text = String(message || '');
  // Do not treat NOT NULL / constraint errors as "missing column"
  if (/violates not-null constraint|null value in column/i.test(text)) return null;
  return text.match(/Could not find the '([^']+)' column/i)?.[1]
    || text.match(/column "([^"]+)" of relation [^"]+ does not exist/i)?.[1]
    || text.match(/column \w+\.([^ ]+) does not exist/i)?.[1]
    || null;
}

async function updateRowSkippingMissingColumns(table, row, id, select, { replay = false } = {}) {
  const payload = { ...row };
  if (!replay && shouldQueueWrites()) {
    return queueUpdate(table, payload, { id }, { source: 'receptionist-helper' });
  }
  try {
    for (let attempt = 0; attempt < 25; attempt += 1) {
      const { data, error } = await supabase.from(table).update(payload).eq('id', id).select(select);
      if (!error) {
        const rowData = Array.isArray(data) ? data[0] : data;
        return { data: rowData || { ...payload, id }, error: null };
      }

      if (/multiple \(or no\) rows returned|Results contain 0 rows|JSON object requested/i.test(error.message || '')) {
        return { data: { ...payload, id }, error: null };
      }

      const missing = getMissingColumn(error.message);
      if (missing && Object.prototype.hasOwnProperty.call(payload, missing)) {
        delete payload[missing];
        continue;
      }

      if (isNetworkError(error) && !replay) {
        return queueUpdate(table, payload, { id }, { source: 'receptionist-helper' });
      }
      return { data: null, error };
    }

    return { data: null, error: new Error('Update failed: too many missing database columns.') };
  } catch (error) {
    if (isNetworkError(error) && !replay) {
      return queueUpdate(table, payload, { id }, { source: 'receptionist-helper' });
    }
    throw error;
  }
}

async function deleteRowsQueued(table, match, { replay = false } = {}) {
  if (!replay && shouldQueueWrites()) {
    return queueDelete(table, match, { source: 'receptionist-helper' });
  }
  let query = supabase.from(table).delete();
  Object.entries(match || {}).forEach(([key, value]) => {
    query = query.eq(key, value);
  });
  const { error } = await query;
  if (error && isNetworkError(error) && !replay) {
    return queueDelete(table, match, { source: 'receptionist-helper' });
  }
  return { error };
}

export async function replayReceptionistMutation(item) {
  if (item.op === 'insert') {
    return insertRowSkippingMissingColumns(item.table, item.payload, '*', {
      preserveColumns: item.preserveColumns || [],
      replay: true,
    });
  }
  if (item.op === 'update') {
    const id = item.match?.id;
    if (!id) return { data: null, error: new Error('Offline update is missing id') };
    return updateRowSkippingMissingColumns(item.table, item.payload, id, '*', { replay: true });
  }
  if (item.op === 'delete') {
    return deleteRowsQueued(item.table, item.match, { replay: true });
  }
  return { data: null, error: new Error(`Unknown offline op: ${item.op}`) };
}

function attendanceDateFromRow(row) {
  return row.attendance_date || row.date || row.log_date || row.created_at?.slice?.(0, 10) || '';
}

function isEmbedError(message = '') {
  return /relationship|embed|schema cache|foreign key/i.test(message);
}

function memberEmbed(member) {
  if (!member) return null;
  return {
    full_name: member.name || member.full_name,
    gym_member_id: member.baseGymId || member.gymId || member.gym_member_id,
    serial_no: member.serialNo ?? member.membershipSerialNo ?? null,
    branch: member.branch || '',
  };
}

async function loadMembershipSerialByMemberId(gid) {
  const map = new Map();
  if (!gid) return map;
  let data = [];
  try {
    data = await fetchAllPaged(() => (
      supabase
        .from('memberships')
        .select('member_id, serial_no, created_at, id')
        .eq('gym_id', gid)
        .order('created_at', { ascending: false })
        .order('id', { ascending: true })
    ));
  } catch (error) {
    if (!/serial_no/i.test(error.message || '')) {
      console.warn('Could not load membership serials', error.message);
    }
    return map;
  }
  data.forEach((row) => {
    if (!row.member_id || map.has(row.member_id)) return;
    if (row.serial_no == null || row.serial_no === '') return;
    const num = Number(row.serial_no);
    map.set(row.member_id, Number.isFinite(num) ? num : String(row.serial_no).trim());
  });
  return map;
}

function withMembershipSerial(member, serial) {
  if (!member) return member;
  const baseGymId = member.baseGymId || member.gymId || member.gym_member_id || '';
  if (serial == null || serial === '') {
    return { ...member, baseGymId };
  }
  return {
    ...member,
    baseGymId,
    serialNo: serial,
    membershipSerialNo: serial,
  };
}

/** Supabase returns max 1000 rows per request unless paged with .range() */
const SUPABASE_PAGE_SIZE = 1000;
const IN_QUERY_CHUNK = 200;

async function fetchAllPaged(buildQuery) {
  const rows = [];
  let from = 0;
  for (;;) {
    const to = from + SUPABASE_PAGE_SIZE - 1;
    const { data, error } = await buildQuery().range(from, to);
    if (error) throw error;
    const chunk = data || [];
    rows.push(...chunk);
    if (chunk.length < SUPABASE_PAGE_SIZE) break;
    from += SUPABASE_PAGE_SIZE;
  }
  return rows;
}

function chunkIds(ids, size = IN_QUERY_CHUNK) {
  const list = [...ids];
  const chunks = [];
  for (let i = 0; i < list.length; i += size) {
    chunks.push(list.slice(i, i + size));
  }
  return chunks;
}

async function fetchScopedRows(table, { order = 'created_at', ascending = false } = {}) {
  const gid = await gymId();
  return fetchAllPaged(() => {
  let query = supabase.from(table).select('*').eq('gym_id', gid);
  if (order) query = query.order(order, { ascending });
    // Stable secondary order so .range pages do not skip/dupe rows
    return query.order('id', { ascending: true });
  });
}

async function memberMapById(memberIds) {
  if (!memberIds.length) return new Map();
  const map = new Map();
  for (const chunk of chunkIds(memberIds)) {
    const { data } = await supabase
      .from('members')
      .select('id, full_name, gym_member_id')
      .in('id', chunk);
    (data || []).forEach((m) => map.set(m.id, m));
  }
  return map;
}

// ─── Members ───────────────────────────────────────────────

const MEMBER_LIST_COLUMNS = 'id, gym_id, gym_member_id, full_name, phone, email, gender, package, trainer, branch, joining_date, created_at, status, profile';

function sanitizeIlike(value) {
  // Keep dots (emails / "Dr. Name"). Strip LIKE wildcards and PostgREST .or() separators.
  return String(value || '')
    .replace(/[%_(),]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Quote branch names (spaces, &) for PostgREST `.or()` filter strings. */
function formatPostgrestFilterValue(value) {
  const text = String(value ?? '');
  if (!text) return text;
  if (/[,\s().&]/.test(text) || text.includes('"')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function applyMemberBranchOrFilter(query, branchFilter, { includeBlankBranch = true } = {}) {
  const branch = String(branchFilter || '').trim();
  if (!branch || branch === 'all') return query;
  if (includeBlankBranch) {
    return query.or(`branch.eq.${formatPostgrestFilterValue(branch)},branch.is.null`);
  }
  return query.eq('branch', branch);
}

/** Branch scope on memberships via members embed — avoids huge member_id.in(...) URLs. */
function applyMembershipMemberBranchFilter(query, branchFilter, { includeBlankBranch = true } = {}) {
  const branch = String(branchFilter || '').trim();
  if (!branch || branch === 'all') return query;
  if (includeBlankBranch) {
    return query.or(
      `branch.eq.${formatPostgrestFilterValue(branch)},branch.is.null`,
      { foreignTable: 'members' },
    );
  }
  return query.eq('members.branch', branch);
}

function activeColumnFilter(value) {
  const v = String(value ?? '').trim();
  if (!v || v === 'all' || v === '—') return '';
  return v;
}

function applyIlikeColumn(query, column, raw) {
  const needle = sanitizeIlike(raw);
  if (!needle) return query;
  return query.ilike(column, `%${needle}%`);
}

function applyDateColumnEq(query, column, raw) {
  const iso = normalizeDateFilterToIso(raw);
  if (!iso) return query;
  return query.eq(column, iso);
}

function applyPaidAtDayEq(query, raw) {
  const iso = normalizeDateFilterToIso(raw);
  if (!iso) return query;
  return query.gte('paid_at', `${iso}T00:00:00`).lte('paid_at', `${iso}T23:59:59.999`);
}

function applyNumericTextColumn(query, column, raw) {
  const needle = sanitizeIlike(raw);
  if (!needle) return query;
  return query.filter(`${column}::text`, 'ilike', `%${needle}%`);
}

/** Member ids matching serial and/or name — searches every member, not the current table page. */
async function findMemberIdsForTableFilters(gid, {
  serialNeedle = '',
  nameNeedle = '',
  branchFilter = '',
} = {}, limit = 1000) {
  const serial = sanitizeIlike(serialNeedle);
  const name = sanitizeIlike(nameNeedle);
  if (!serial && !name) return null;

  let q = supabase.from('members').select('id').eq('gym_id', gid).limit(limit);
  if (branchFilter && branchFilter !== 'all') q = q.eq('branch', branchFilter);
  if (name) q = q.ilike('full_name', `%${name}%`);
  if (serial) {
    const digits = serial.replace(/\D/g, '');
    const serialOr = numericGymMemberIdSearchOr(digits);
    if (serialOr) {
      q = q.or(serialOr);
    }
  }
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map((row) => row.id).filter(Boolean);
}

async function restrictQueryToMembers(query, memberIds, { emptyIfNone = true } = {}) {
  if (!memberIds) return { query, empty: false };
  if (!memberIds.length) return { query, empty: emptyIfNone };
  return { query: query.in('member_id', memberIds), empty: false };
}

async function applyPaymentColumnFilters(query, filters, gid, branchFilter) {
  const f = filters || {};
  const serial = activeColumnFilter(f.serialNo || f.gymId);
  const member = activeColumnFilter(f.member);
  const item = activeColumnFilter(f.item || f.renewal || f.package);
  const duration = activeColumnFilter(f.duration);
  const note = activeColumnFilter(f.note || f.paymentNote);
  const status = activeColumnFilter(f.status);
  const method = activeColumnFilter(f.method);
  const idNeedle = activeColumnFilter(f.id);
  const dateNeedle = activeColumnFilter(f.date);
  const startNeedle = activeColumnFilter(f.startDate || f.start);
  const endNeedle = activeColumnFilter(f.endDate || f.end || f.expiry);

  if (serial || member) {
    const ids = await findMemberIdsForTableFilters(gid, {
      serialNeedle: serial,
      nameNeedle: member,
      branchFilter,
    });
    if (serial) {
      const restricted = await restrictQueryToMembers(query, ids || [], { emptyIfNone: true });
      if (restricted.empty) return restricted;
      query = restricted.query;
    } else if (member) {
      const orParts = [`member_name.ilike.%${sanitizeIlike(member)}%`];
      if (ids?.length) orParts.push(`member_id.in.(${ids.join(',')})`);
      query = query.or(orParts.join(','));
    }
  }

  if (item) query = applyIlikeColumn(query, 'package_name', item);
  if (duration) query = applyIlikeColumn(query, 'duration', duration);
  if (note) query = applyIlikeColumn(query, 'payment_note', note);
  if (status) query = applyIlikeColumn(query, 'status', status);
  if (method) query = applyIlikeColumn(query, 'payment_method', method);
  if (idNeedle) {
    const safe = sanitizeIlike(idNeedle);
    query = query.or(
      [`payment_ref.ilike.%${safe}%`, `invoice_id.ilike.%${safe}%`].join(','),
    );
  }
  if (dateNeedle) query = applyPaidAtDayEq(query, dateNeedle);
  if (startNeedle) query = applyDateColumnEq(query, 'start_date', startNeedle);
  if (endNeedle) query = applyDateColumnEq(query, 'end_date', endNeedle);

  query = applyNumericTextColumn(query, 'package_price', activeColumnFilter(f.total));
  query = applyNumericTextColumn(query, 'amount', activeColumnFilter(f.totalPaid || f.amount));

  Object.entries(f).forEach(([key, value]) => {
    if (!key.startsWith('method_')) return;
    const needle = sanitizeIlike(value);
    if (!needle) return;
    query = query.filter('payment_splits::text', 'ilike', `%${needle}%`);
  });

  return { query, empty: false };
}

/**
 * Resolve membership keys (member_id|package) whose linked subscription payment
 * matches fee / method / note column filters. Used so Memberships filters are
 * server-wide, not page-local.
 */
async function findMembershipKeysMatchingPaymentFilters(gid, filters = {}) {
  const packageFeeF = activeColumnFilter(filters.packageFee);
  const registrationFeeF = activeColumnFilter(filters.registrationFee);
  const totalPaidF = activeColumnFilter(filters.totalPaid);
  const pendingF = activeColumnFilter(filters.pendingAmount);
  const noteF = activeColumnFilter(filters.paymentNote || filters.note);
  const methodEntries = Object.entries(filters || {}).filter(
    ([key, value]) => key.startsWith('method_') && activeColumnFilter(value),
  );

  if (
    !packageFeeF
    && !registrationFeeF
    && !totalPaidF
    && !pendingF
    && !noteF
    && !methodEntries.length
  ) {
    return null;
  }

  const { formatCurrency: formatMoney } = await import('../../pages/receptionist/receptionistData');
  const { splitAmountForMethod, PAYMENT_METHOD_OPTIONS } = await import('../../utils/splitPaymentUtils');

  let payQuery = supabase
    .from('payments')
    .select(
      'member_id, package_name, package_price, registration_fee, discount_amount, personal_training_fee, classes_fee, amount, payment_splits, payment_method, payment_note, source',
    )
    .eq('gym_id', gid)
    .limit(4000);

  // Prefer subscription-like sources; still allow null/legacy source rows
  payQuery = payQuery.or(
    'source.eq.subscription,source.eq.membership,source.eq.new_membership,source.eq.subscriptions,source.is.null',
  );

  if (noteF) {
    payQuery = applyIlikeColumn(payQuery, 'payment_note', noteF);
  }
  if (packageFeeF) {
    const digits = packageFeeF.replace(/[^\d.]/g, '');
    if (digits) payQuery = applyNumericTextColumn(payQuery, 'package_price', digits);
  }
  if (registrationFeeF) {
    const digits = registrationFeeF.replace(/[^\d.]/g, '');
    if (digits) payQuery = applyNumericTextColumn(payQuery, 'registration_fee', digits);
  }
  if (totalPaidF) {
    const digits = totalPaidF.replace(/[^\d.]/g, '');
    if (digits) payQuery = applyNumericTextColumn(payQuery, 'amount', digits);
  }
  methodEntries.forEach(([, value]) => {
    const needle = sanitizeIlike(String(value).replace(/[^\d.]/g, '') || value);
    if (needle) {
      payQuery = payQuery.filter('payment_splits::text', 'ilike', `%${needle}%`);
    }
  });

  const { data, error } = await payQuery;
  if (error) throw error;

  const keys = new Set();
  const memberIds = new Set();

  (data || []).forEach((pay) => {
    if (!pay?.member_id) return;
    if (isRenewalPaymentRow(pay)) return;

    const packageFee = Number(pay.package_price) || 0;
    const registrationFee = Number(pay.registration_fee) || 0;
    const discount = Number(pay.discount_amount) || 0;
    const pt = Number(pay.personal_training_fee) || 0;
    const classes = Number(pay.classes_fee) || 0;
    const totalPaid = Number(pay.amount) || 0;
    const totalDue = Math.max(0, packageFee - discount + registrationFee + pt + classes);
    const pendingAmount = Math.max(0, totalDue - totalPaid);

    if (packageFeeF && formatMoney(packageFee) !== String(packageFeeF)) return;
    if (registrationFeeF && formatMoney(registrationFee) !== String(registrationFeeF)) return;
    if (totalPaidF && formatMoney(totalPaid) !== String(totalPaidF)) return;
    if (pendingF && formatMoney(pendingAmount) !== String(pendingF)) return;
    if (noteF) {
      const note = String(pay.payment_note || '').toLowerCase();
      if (!note.includes(String(noteF).toLowerCase())) return;
    }

    const splitRow = {
      paymentSplits: pay.payment_splits,
      amount: totalPaid,
      method: pay.payment_method,
    };
    const methodsOk = PAYMENT_METHOD_OPTIONS.every(({ key }) => {
      const raw = filters[`method_${key}`];
      if (!activeColumnFilter(raw)) return true;
      const amt = splitAmountForMethod(splitRow, key);
      return formatMoney(amt) === String(raw)
        || String(amt).includes(String(raw).replace(/[^\d.]/g, ''));
    });
    if (!methodsOk) return;

    const pkg = String(pay.package_name || '').trim().toLowerCase();
    keys.add(`${pay.member_id}|${pkg}`);
    memberIds.add(pay.member_id);
  });

  return {
    keys,
    memberIds: [...memberIds],
  };
}

/** Resolve membership ids by serial digits — memberships.serial_no OR members.gym_member_id. */
async function findMembershipIdsBySerialContains(gid, rawNeedle, limit = 500) {
  const digits = String(rawNeedle || '').replace(/\D/g, '');
  if (!digits) return [];

  const ids = new Set();

  const { data, error } = await supabase
    .from('memberships')
    .select('id')
    .eq('gym_id', gid)
    .filter('serial_no::text', 'ilike', `%${digits}%`)
    .limit(limit);

  if (!error) {
    (data || []).forEach((row) => { if (row.id) ids.add(row.id); });
  } else if (/^\d+$/.test(digits)) {
    // Older PostgREST: fall back to exact serial match only
    const { data: exact } = await supabase
      .from('memberships')
      .select('id')
      .eq('gym_id', gid)
      .eq('serial_no', Number(digits))
      .limit(limit);
    (exact || []).forEach((row) => { if (row.id) ids.add(row.id); });
  }

  // Also match the member public serial (Members list ID) when it differs from memberships.serial_no
  let memberHits = null;
  {
    const exact = await supabase
      .from('members')
      .select('id')
      .eq('gym_id', gid)
      .or(`gym_member_id.eq.${digits},gym_member_id.eq.ID-${digits},gym_member_id.eq.id-${digits}`)
      .limit(limit);
    if (!exact.error && exact.data?.length) {
      memberHits = exact.data;
    } else {
      const fuzzy = await supabase
        .from('members')
        .select('id')
        .eq('gym_id', gid)
        .ilike('gym_member_id', `%${digits}%`)
        .limit(limit);
      if (!fuzzy.error) memberHits = fuzzy.data;
    }
  }

  const memberIds = (memberHits || []).map((m) => m.id).filter(Boolean);
  if (memberIds.length) {
    for (const chunk of chunkIds(memberIds)) {
      const { data: memRows } = await supabase
        .from('memberships')
        .select('id')
        .eq('gym_id', gid)
        .in('member_id', chunk)
        .limit(limit);
      (memRows || []).forEach((row) => { if (row.id) ids.add(row.id); });
    }
  }

  return [...ids];
}

/** Align memberships.serial_no with members.gym_member_id when they drifted apart. */
async function syncMembershipSerialsFromMemberEmbed(rows = []) {
  const patches = [];
  (rows || []).forEach((row) => {
    if (!row?.id) return;
    const linkedRaw = row.members;
    const linked = Array.isArray(linkedRaw) ? (linkedRaw[0] || {}) : (linkedRaw || {});
    const publicSerial = getMemberPublicSerial({
      gym_member_id: linked.gym_member_id,
      gymId: linked.gym_member_id,
    });
    if (publicSerial == null) return;
    if (Number(row.serial_no) === Number(publicSerial)) return;
    patches.push(
      supabase
        .from('memberships')
        .update({ serial_no: publicSerial })
        .eq('id', row.id)
        .then(({ error }) => {
          if (!error) {
            row.serial_no = publicSerial;
          } else if (!/serial_no/i.test(error.message || '')) {
            console.warn('Could not sync membership serial_no', error.message);
          }
        }),
    );
  });
  if (patches.length) await Promise.all(patches);
}

function formatCnicHyphenated(digits) {
  const d = String(digits || '').replace(/\D/g, '').slice(0, 13);
  if (!d) return '';
  if (d.length <= 5) return d;
  if (d.length <= 12) return `${d.slice(0, 5)}-${d.slice(5)}`;
  return `${d.slice(0, 5)}-${d.slice(5, 12)}-${d.slice(12)}`;
}

/** Match public serials: 2280, ID-2280, id_2280 — skip PENDING-/TMP-/GYM- placeholders. */
function numericGymMemberIdSearchOr(digits) {
  const d = String(digits || '').replace(/\D/g, '');
  if (!d) return '';
  return [
    `gym_member_id.eq.${d}`,
    `gym_member_id.eq.ID-${d}`,
    `gym_member_id.eq.id-${d}`,
    `gym_member_id.eq.ID${d}`,
    `gym_member_id.ilike.%${d}%`,
  ].join(',');
}

function memberNameAndSerialSearchOr(safe, extraParts = []) {
  const parts = [
    `full_name.ilike.%${safe}%`,
    numericGymMemberIdSearchOr(safe),
    ...extraParts,
  ].filter(Boolean);
  return parts.join(',');
}

/** OR filter for member text search: name, phone, CNIC, email, gym id. */
function buildMemberTextSearchOr(raw) {
  const safe = sanitizeIlike(raw);
  if (!safe) return '';
  const digits = safe.replace(/\D/g, '');
  const hyphenated = digits.length > 4
    ? `${digits.slice(0, 4)}-${digits.slice(4)}`
    : '';
  const cnicHyphenated = formatCnicHyphenated(digits);
  const parts = [
    `full_name.ilike.%${safe}%`,
    `phone.ilike.%${safe}%`,
    `email.ilike.%${safe}%`,
    `profile->>cnic.ilike.%${safe}%`,
  ];
  const serialOr = numericGymMemberIdSearchOr(digits);
  if (serialOr) {
    parts.push(serialOr);
    if (digits !== safe) {
      parts.push(`phone.ilike.%${digits}%`);
      parts.push(`profile->>cnic.ilike.%${digits}%`);
    }
  }
  if (hyphenated && hyphenated !== safe) {
    parts.push(`phone.ilike.%${hyphenated}%`);
  }
  if (cnicHyphenated && cnicHyphenated !== safe && cnicHyphenated !== digits) {
    parts.push(`profile->>cnic.ilike.%${cnicHyphenated}%`);
  }
  return parts.join(',');
}

function mapMemberRow(row) {
  const member = rowToMember(row);
  const rawId = String(row.gym_member_id || '').trim();
  // Only numeric gym_member_id values are public serials (legacy + allotted)
  if (/^\d+$/.test(rawId)) {
    return withMembershipSerial(member, Number(rawId));
  }
  // Clear placeholder so UI shows "—" until subscription allotment
  if (/^PENDING-/i.test(rawId) || /^GYM-\d+$/i.test(rawId) || /^TMP-/i.test(rawId)) {
    return { ...member, gymId: '', baseGymId: rawId };
  }
  return member;
}

function sortMembersBySerial(rows) {
  return [...(rows || [])].sort((a, b) => {
    const aSerial = Number(String(a.serialNo ?? a.gym_member_id ?? '').replace(/\D/g, '')) || Number.POSITIVE_INFINITY;
    const bSerial = Number(String(b.serialNo ?? b.gym_member_id ?? '').replace(/\D/g, '')) || Number.POSITIVE_INFINITY;
    if (aSerial !== bSerial) return aSerial - bSerial;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

function memberNameSearchRank(name, needle) {
  const n = String(name || '').trim().toLowerCase();
  const q = String(needle || '').trim().toLowerCase();
  if (!q) return 99;
  if (n === q) return 0;
  if (n.startsWith(q)) return 1;
  if (n.split(/\s+/).some((word) => word.startsWith(q))) return 2;
  if (n.includes(q)) return 3;
  return 4;
}

function isLikelyNameSearch(raw) {
  const s = sanitizeIlike(raw);
  if (!s) return false;
  if (numericGymMemberIdSearchOr(s)) return false;
  const digits = s.replace(/\D/g, '');
  if (digits.length >= 7) return false;
  return /[a-zA-Z]/.test(s);
}

/** Best name matches first — exact, then starts-with, then word-start, then contains. */
function sortMemberRowsByNameSearch(rows, needle) {
  const q = String(needle || '').trim();
  if (!q) return rows || [];
  return [...(rows || [])].sort((a, b) => {
    const aName = a.full_name || a.name || '';
    const bName = b.full_name || b.name || '';
    const rankDiff = memberNameSearchRank(aName, q) - memberNameSearchRank(bName, q);
    if (rankDiff !== 0) return rankDiff;
    return String(aName).localeCompare(String(bName), undefined, { sensitivity: 'base' });
  });
}

/**
 * Receptionist lists stay PK-today when search is empty.
 * A typed search lifts the date lock (admin + receptionist) so serial/phone/name
 * can find older rows instead of staying stuck in the selected date range.
 */
function resolveReceptionistListDates(filters = {}, { receptionist = false, search = '' } = {}) {
  const next = { ...filters };
  if (String(search || '').trim()) {
    next.rangeStart = '';
    next.rangeEnd = '';
    next.dateFrom = '';
    next.dateTo = '';
    return next;
  }
  if (!receptionist) return next;
  const today = pkTodayKey();
  next.rangeStart = today;
  next.rangeEnd = today;
  next.dateFrom = today;
  next.dateTo = today;
  return next;
}

/**
 * Fast Members table load — only one page (~50 rows) from Supabase.
 */
/**
 * Server-paged members. Prefer this over fetchMembers() for UI tables.
 * filters: { serialNo|gymId, member|name, phone|mobile, email, branch, trainer, joiningDate|createdAt, status }
 */
function resolveMembersPageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

export async function fetchMembersPage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  allMatching = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveMembersPageDates(filters, { receptionist, search });
  const branchFromFilters = String(scopedFilters.branch || '').trim();
  const branchFilter = String(
    branch
    || (branchFromFilters && branchFromFilters !== 'all' ? branchFromFilters : '')
    || getReceptionistBranchFilter()
    || '',
  ).trim();
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchMembersPageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      activity: receptionist && !String(search || '').trim(),
      allMatching,
    });
    if (rpc) {
      return {
        rows: (rpc.rows || []).map((row) => ({
          ...mapMemberRow(row),
          hasActiveSubscription: Boolean(row.has_active_subscription),
        })),
        total: rpc.total,
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Members page RPC failed, using fallback', err?.message || err);
  }

  return fetchMembersPageLegacy({
    page: safePage,
    pageSize: safeSize,
    search,
    branch: branchFilter,
    filters: scopedFilters,
    allMatching,
  });
}

/** Export / print: same filters as the table, no client-side full-table dump. */
export async function fetchMembersExport(params = {}) {
  return fetchMembersPage({ ...params, allMatching: true, page: 0, pageSize: 20000 });
}

async function fetchMembersPageLegacy({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  allMatching = false,
} = {}) {
  const gid = await gymId();
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = allMatching ? 20000 : Math.min(100, Math.max(10, Number(pageSize) || 50));
  const from = allMatching ? 0 : (safePage - 1) * safeSize;
  const to = allMatching ? safeSize - 1 : from + safeSize - 1;

  let query = supabase
    .from('members')
    .select(MEMBER_LIST_COLUMNS, { count: 'exact' })
    .eq('gym_id', gid);

  const branchFromFilters = String(filters.branch || '').trim();
  const branchFilter = String(
    branch
    || (branchFromFilters && branchFromFilters !== 'all' ? branchFromFilters : '')
    || getReceptionistBranchFilter()
    || '',
  ).trim();
  if (isReceptionistPanelScope()) {
    if (!branchFilter) {
      return { rows: [], total: 0, page: safePage, pageSize: safeSize };
    }
    query = query.eq('branch', branchFilter);
  } else if (branchFilter && branchFilter !== 'all') {
    query = query.eq('branch', branchFilter);
  }

  const serialNeedle = sanitizeIlike(filters.serialNo || filters.gymId);
  if (serialNeedle) {
    const serialOr = numericGymMemberIdSearchOr(serialNeedle);
    if (serialOr) {
      query = query.or(serialOr);
    }
  }

  const nameNeedle = sanitizeIlike(filters.member || filters.name);
  if (nameNeedle) {
    query = query.ilike('full_name', `%${nameNeedle}%`);
  }

  const phoneNeedleRaw = sanitizeIlike(filters.phone || filters.mobile);
  if (phoneNeedleRaw) {
    const digits = phoneNeedleRaw.replace(/\D/g, '');
    const hyphenated = digits.length > 4
      ? `${digits.slice(0, 4)}-${digits.slice(4)}`
      : digits;
    const phoneOr = [
      `phone.ilike.%${hyphenated}%`,
      digits && digits !== hyphenated ? `phone.ilike.%${digits}%` : null,
    ].filter(Boolean).join(',');
    query = query.or(phoneOr);
  }

  const emailNeedle = sanitizeIlike(filters.email);
  if (emailNeedle) {
    query = query.ilike('email', `%${emailNeedle}%`);
  }

  const trainerFilter = String(filters.trainer || '').trim();
  if (trainerFilter && trainerFilter !== 'all' && trainerFilter !== '—') {
    query = query.ilike('trainer', `%${sanitizeIlike(trainerFilter)}%`);
  }

  const joiningFilter = String(filters.joiningDate || filters.createdAt || '').trim();
  if (joiningFilter && joiningFilter !== 'all' && joiningFilter !== '—') {
    const joiningIso = normalizeDateFilterToIso(joiningFilter);
    // Only filter once a full valid date is entered (DD-MM-YYYY)
    if (joiningIso) {
      query = query.eq('joining_date', joiningIso);
    }
  }

  const rangeStart = String(filters.rangeStart || filters.dateFrom || '').trim().slice(0, 10);
  const rangeEnd = String(filters.rangeEnd || filters.dateTo || '').trim().slice(0, 10);
  const hasDateRange = (
    (rangeStart && /^\d{4}-\d{2}-\d{2}$/.test(rangeStart))
    || (rangeEnd && /^\d{4}-\d{2}-\d{2}$/.test(rangeEnd))
  );
  // Match display date: joining_date when set, otherwise created_at day.
  if (hasDateRange) {
    const start = (rangeStart && /^\d{4}-\d{2}-\d{2}$/.test(rangeStart)) ? rangeStart : '1900-01-01';
    const end = (rangeEnd && /^\d{4}-\d{2}-\d{2}$/.test(rangeEnd)) ? rangeEnd : '2100-12-31';
    query = query.or(
      [
        `and(joining_date.gte.${start},joining_date.lte.${end})`,
        `and(joining_date.is.null,created_at.gte.${start}T00:00:00,created_at.lte.${end}T23:59:59.999)`,
      ].join(','),
    );
  }

  const statusFilter = sanitizeIlike(
    filters.status && filters.status !== 'all' && filters.status !== '—'
      ? filters.status
      : '',
  );
  if (statusFilter) {
    query = query.ilike('status', `%${statusFilter}%`);
  }

  const needle = String(search || '').trim();
  if (needle) {
    const memberOr = buildMemberTextSearchOr(needle);
    if (memberOr) {
      query = query.or(memberOr);
    }
  }

  const nameSortNeedle = nameNeedle || (needle && isLikelyNameSearch(needle) ? sanitizeIlike(needle) : '');

  let orderedQuery;
  if (nameSortNeedle) {
    orderedQuery = query
      .order('full_name', { ascending: true, nullsFirst: false })
      .order('gym_member_id', { ascending: true });
  } else if (hasDateRange) {
    orderedQuery = query
      .order('joining_date', { ascending: true })
      .order('created_at', { ascending: true })
      .order('gym_member_id', { ascending: true });
  } else {
    orderedQuery = query.order('gym_member_id', { ascending: true });
  }

  const { data, error, count } = await orderedQuery.range(from, to);

  if (error) throw error;

  const sortedRows = nameSortNeedle ? sortMemberRowsByNameSearch(data || [], nameSortNeedle) : (data || []);

  return {
    rows: sortedRows.map(mapMemberRow),
    total: Number(count) || 0,
    page: safePage,
    pageSize: safeSize,
  };
}

/** Paged customer balances — only customers with pending balance (> 0).
 *  Walk-in is included only when it still has a pending balance.
 */
export async function fetchCustomerBalancesPage({
  page = 1,
  pageSize = 50,
  search = '',
  filters = {},
} = {}) {
  const gid = await gymId();
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.min(100, Math.max(10, Number(pageSize) || 50));

  const paymentSelectAttempts = [
    'id, member_id, package_name, amount, package_price, registration_fee, discount_amount, personal_training_fee, classes_fee, payment_method, payment_splits, payment_ref, invoice_id, status, paid_at, payment_note, start_date, end_date, source, created_at',
    'id, member_id, package_name, amount, package_price, registration_fee, discount_amount, personal_training_fee, classes_fee, payment_method, payment_splits, payment_ref, invoice_id, status, paid_at, payment_note, source, created_at',
    'id, member_id, package_name, amount, package_price, registration_fee, discount_amount, personal_training_fee, classes_fee, payment_method, payment_ref, invoice_id, status, paid_at, payment_note, source, created_at',
    'id, member_id, package_name, amount, package_price, registration_fee, discount_amount, payment_method, payment_ref, invoice_id, status, paid_at, source',
  ];

  let pendingPayRows = null;
  let lastError = null;
  for (const paymentSelect of paymentSelectAttempts) {
    try {
      const rows = await fetchAllPaged(() => (
        supabase
          .from('payments')
          .select(paymentSelect)
          .eq('gym_id', gid)
          .eq('status', 'pending')
          .order('paid_at', { ascending: false })
      ));
      pendingPayRows = rows || [];
      break;
    } catch (error) {
      lastError = error;
      if (!/column|schema cache|does not exist/i.test(error.message || '')) {
        throw error;
      }
    }
  }
  if (pendingPayRows == null && lastError) throw lastError;
  pendingPayRows = pendingPayRows || [];

  // Confirm remaining > 0 (status alone can be stale)
  const pendingMemberIds = [...new Set(
    pendingPayRows
      .map((row) => {
        const mapped = rowToPayment(row, null);
        return saleRowLeft(mapped) > 0 ? row.member_id : null;
      })
      .filter(Boolean),
  )];

  if (!pendingMemberIds.length) {
    return { rows: [], total: 0, page: safePage, pageSize: safeSize };
  }

  // Full payment history for those members (accurate debit / credit / balance)
  const allPayRows = await loadPaymentsForMembers(gid, pendingMemberIds).catch(() => []);

  const memberRows = [];
  for (const chunk of chunkIds(pendingMemberIds)) {
    const { data, error } = await supabase
      .from('members')
      .select(MEMBER_LIST_COLUMNS)
      .eq('gym_id', gid)
      .in('id', chunk);
    if (error) throw error;
    memberRows.push(...(data || []));
  }

  const members = dedupeMembers(memberRows.map(mapMemberRow));
  const memberById = new Map(members.map((m) => [m.id, m]));

  const payments = allPayRows.map((row) => {
    const member = memberById.get(row.member_id);
    return rowToPayment(row, member
      ? {
          full_name: member.name,
          gym_member_id: member.gymId || member.gym_member_id,
          branch: member.branch,
        }
      : null);
  });

  const paymentsByMemberId = new Map();
  payments.forEach((p) => {
    const mid = p.memberId || p.member_id;
    if (!mid) return;
    if (!paymentsByMemberId.has(mid)) paymentsByMemberId.set(mid, []);
    paymentsByMemberId.get(mid).push(p);
  });

  const needle = String(search || '').trim().toLowerCase();

  let rows = members
    .map((member) => {
      const memberPayments = paymentsByMemberId.get(member.id) || [];
      const credit = memberPayments.reduce((sum, row) => sum + paymentTotalDue(row), 0);
      const debit = memberPayments.reduce((sum, row) => sum + saleRowPaid(row), 0);
      const balance = Math.round((credit - debit) * 100) / 100;
      return {
        id: member.id,
        gymId: getGymMemberId(member) || '—',
        name: member.name || '—',
        phone: member.phone || member.mobile || '—',
        branch: member.branch || '—',
        salesCount: memberPayments.length,
        credit: Math.round(credit * 100) / 100,
        debit: Math.round(debit * 100) / 100,
        balance,
        isWalkIn: isWalkInMember(member),
      };
    })
    // Only pending balances; walk-in with zero due is hidden
    .filter((row) => row.balance > 0)
    .filter((row) => {
      if (!needle) return true;
      return (
        String(row.name).toLowerCase().includes(needle)
        || String(row.gymId).toLowerCase().includes(needle)
        || String(row.phone).toLowerCase().includes(needle)
        || String(row.branch).toLowerCase().includes(needle)
      );
    })
    .filter((row) => {
      const gymNeedle = activeColumnFilter(filters.gymId || filters.serialNo);
      if (gymNeedle) {
        const digits = gymNeedle.replace(/\D/g, '');
        const gymId = String(row.gymId || '').toLowerCase();
        const gymDigits = gymId.replace(/\D/g, '');
        if (!gymId.includes(gymNeedle.toLowerCase()) && !(digits && gymDigits.includes(digits))) {
          return false;
        }
      }
      const nameNeedle = activeColumnFilter(filters.name);
      if (nameNeedle && !String(row.name || '').toLowerCase().includes(nameNeedle.toLowerCase())) {
        return false;
      }
      const phoneNeedle = activeColumnFilter(filters.phone);
      if (phoneNeedle) {
        const phone = String(row.phone || '').toLowerCase();
        const phoneDigits = phone.replace(/\D/g, '');
        const needleDigits = phoneNeedle.replace(/\D/g, '');
        if (!phone.includes(phoneNeedle.toLowerCase()) && !(needleDigits && phoneDigits.includes(needleDigits))) {
          return false;
        }
      }
      const branchNeedle = activeColumnFilter(filters.branch);
      if (branchNeedle && !String(row.branch || '').toLowerCase().includes(branchNeedle.toLowerCase())) {
        return false;
      }
      const salesNeedle = activeColumnFilter(filters.salesCount);
      if (salesNeedle && !String(row.salesCount).includes(salesNeedle)) return false;
      const debitNeedle = activeColumnFilter(filters.debit);
      if (debitNeedle && !String(row.debit).includes(debitNeedle.replace(/,/g, ''))) return false;
      const creditNeedle = activeColumnFilter(filters.credit);
      if (creditNeedle && !String(row.credit).includes(creditNeedle.replace(/,/g, ''))) return false;
      const balanceNeedle = activeColumnFilter(filters.balance);
      if (balanceNeedle && !String(row.balance).includes(balanceNeedle.replace(/,/g, ''))) return false;
      return true;
    })
    .sort((a, b) => {
      if (b.balance !== a.balance) return b.balance - a.balance;
      return String(a.name).localeCompare(String(b.name));
    });

  const total = rows.length;
  const from = (safePage - 1) * safeSize;
  rows = rows.slice(from, from + safeSize);

  return {
    rows,
    total,
    page: safePage,
    pageSize: safeSize,
  };
}

/** Active subscription flags for the current page only. */
export async function fetchActiveSubscriptionMemberIdsFor(memberIds = []) {
  const ids = [...new Set((memberIds || []).filter(Boolean))];
  if (!ids.length) return new Set();
  const gid = await gymId();
  const active = new Set();

  for (const chunk of chunkIds(ids)) {
    const { data, error } = await supabase
      .from('memberships')
      .select('member_id, status, expiry_date, start_date, dates_tbc, frozen')
      .eq('gym_id', gid)
      .in('member_id', chunk);
    if (error) throw error;
    (data || []).forEach((row) => {
      if (!row.member_id) return;
      if (isMembershipUnexpired({
        status: row.status,
        expiry: row.expiry_date,
        start: row.start_date,
        datesTbc: row.dates_tbc,
        frozen: row.frozen,
      })) {
        active.add(row.member_id);
      }
    });
  }
  return active;
}

/** Full member list — export / rare use only. Prefer fetchMembersPage for UI. */
export async function fetchMembers() {
  const gid = await gymId();
  return cachedQuery(cacheKey(`gym:${gid}:members:v2`), async () => {
    const data = await fetchAllPaged(() => (
      supabase
        .from('members')
        .select(MEMBER_LIST_COLUMNS)
        .eq('gym_id', gid)
        .order('gym_member_id', { ascending: true })
    ));
    const mapped = dedupeMembers(data.map(mapMemberRow));
    return filterByReceptionistBranch(sortMembersBySerial(mapped), (m) => m.branch);
  });
}

export async function fetchMemberById(id) {
  const { data, error } = await supabase.from('members').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const member = rowToMember(data);
  const rawId = String(data.gym_member_id || '').trim();
  if (/^\d+$/.test(rawId)) {
    return withMembershipSerial(member, Number(rawId));
  }
  // Light lookup — only this member's latest serial (never scan the whole table)
  const { data: ms } = await supabase
    .from('memberships')
    .select('serial_no')
    .eq('member_id', id)
    .not('serial_no', 'is', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (ms?.serial_no != null && ms.serial_no !== '') {
    const num = Number(ms.serial_no);
    return withMembershipSerial(member, Number.isFinite(num) ? num : String(ms.serial_no).trim());
  }
  return withMembershipSerial(member, null);
}

/** True when member has no package serial yet (placeholder / old GYM- ids). */
export { isPendingGymMemberId } from '../../pages/admin/members/memberData';

function makePendingGymMemberId() {
  return `PENDING-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Create member without a public serial. Serial (ID-1101…) is allotted
 * when they start a package subscription (see createMembership).
 */
export async function createMember(form, gymMemberId) {
  const gid = await gymId();
  const branch = String(form.branch || getReceptionistBranch() || '').trim();
  const provided = String(gymMemberId || form.gymId || '').trim();
  // Only keep an explicit real serial if caller passes one; otherwise pending.
  let nextId = provided && !isPendingGymMemberId(provided) ? provided : makePendingGymMemberId();

  for (let attempt = 0; attempt < 12; attempt += 1) {
    const row = formToMemberRow({ ...form, gymId: nextId, branch }, gid);
    const { data, error } = await insertRowSkippingMissingColumns('members', row, '*');
    if (!error) {
  await bustGymCache();
  return rowToMember(data);
    }

    const message = error.message || '';
    const isDupGymId = /members_gym_member_id_uq|members_gym_branch_member_id_uq|duplicate key|unique constraint/i.test(message);
    if (!isDupGymId) throw error;

    // Collision — new pending id (or bump numeric serial if one was forced)
    if (!isPendingGymMemberId(nextId) && /^\d+$/.test(String(nextId))) {
      nextId = String(Number(nextId) + 1);
    } else {
      nextId = makePendingGymMemberId();
    }
  }

  throw new Error('Could not create member with a unique placeholder ID. Please try again.');
}

/** Copy package serial onto members.gym_member_id (legacy/search display key). */
async function allotMemberPublicSerial(memberId, serialNo, { force = false } = {}) {
  if (!memberId || serialNo == null || serialNo === '') return false;
  const serialText = String(serialNo).trim();
  if (!serialText || !/^\d+$/.test(serialText)) return false;

  const { data, error } = await supabase
    .from('members')
    .select('gym_member_id, branch')
    .eq('id', memberId)
    .maybeSingle();
  if (error) {
    console.warn('Could not read member for serial allotment', error.message);
    return false;
  }

  const current = String(data?.gym_member_id || '').trim();
  if (current === serialText) return true;
  // Keep an existing real serial unless this is the first package allotment (force)
  if (!force && !isPendingGymMemberId(current) && current !== '') {
    return true;
  }

  const { error: updateError } = await supabase
    .from('members')
    .update({ gym_member_id: serialText })
    .eq('id', memberId);
  if (updateError) {
    if (/duplicate|unique/i.test(updateError.message || '')) {
      return false;
    }
    console.warn('Could not allot member serial', updateError.message);
    return false;
  }
  return true;
}

export async function updateMember(id, form) {
  const gid = await gymId();
  const existing = form.gymId ? form : { ...form };
  const row = formToMemberRow(existing, gid);
  delete row.gym_id;
  delete row.gym_member_id;
  const { data, error } = await updateRowSkippingMissingColumns('members', row, id, '*');
  if (error) throw error;

  // Keep payment.member_name in sync so reports don't show the previous name
  const liveName = String(data?.full_name || row.full_name || '').trim();
  if (liveName && id) {
    try {
      await supabase
        .from('payments')
        .update({ member_name: liveName })
        .eq('gym_id', gid)
        .eq('member_id', id);
    } catch {
      /* non-fatal — display prefers live member name anyway */
    }
  }

  await bustGymCache();
  return rowToMember(data);
}

export async function deleteMember(id) {
  if (!id) throw new Error('Member id is required');

  const { data: existing } = await supabase
    .from('members')
    .select('id, gym_member_id, full_name, profile')
    .eq('id', id)
    .maybeSingle();
  if (existing) {
    const { isWalkInMember } = await import('./walkInMember');
    if (isWalkInMember({
      gymId: existing.gym_member_id,
      name: existing.full_name,
      profile: existing.profile,
      isWalkIn: existing.profile?.is_walk_in,
    })) {
      throw new Error('The permanent Walk-In Customer (Serial 1) cannot be deleted.');
    }
  }

  // Clear related rows first in case some FKs are RESTRICT / not CASCADE in the live DB
  const relatedTables = [
    'membership_freezes',
    'memberships',
    'attendance_logs',
    'payments',
    'lockers',
    'guest_passes',
  ];
  for (const table of relatedTables) {
    const column = table === 'guest_passes' ? 'referred_by_member_id' : 'member_id';
    const { error: relatedError } = await deleteRowsQueued(table, { [column]: id });
    if (relatedError && !/does not exist|schema cache|column/i.test(relatedError.message || '')) {
      // Guest passes / lockers may use SET NULL — try nulling if delete failed
      if (table === 'guest_passes' || table === 'lockers' || table === 'payments') {
        const { error: nullError } = await supabase.from(table).update({ [column]: null }).eq(column, id);
        if (nullError && !/does not exist|schema cache|column/i.test(nullError.message || '')) {
          throw relatedError;
        }
      } else {
        throw relatedError;
      }
    }
  }

  const { error } = await deleteRowsQueued('members', { id });
  if (error) throw error;
  await bustGymCache();
}

export async function nextGymMemberId(branchName) {
  const gid = await gymId();
  const branch = String(branchName || getReceptionistBranch() || '').trim();

  const { data, error } = await supabase
    .from('members')
    .select('gym_member_id, branch, profile')
    .eq('gym_id', gid);
  if (error) throw error;

  const needle = normalizeBranchName(branch);
  const rows = (data || []).filter((row) => {
    // Per-branch sequence: only count members belonging to this branch
    if (!needle) return true; // no branch (admin/legacy) → gym-wide sequence
    const rowBranch = normalizeBranchName(row.branch || row.profile?.branch || '');
    return Boolean(rowBranch) && branchesMatch(rowBranch, branch);
  });

  let maxNum = 0;
  rows.forEach((row) => {
    const match = String(row.gym_member_id || '').match(/(\d+)\s*$/);
    if (match) maxNum = Math.max(maxNum, Number(match[1]) || 0);
  });

  return `GYM-${String(maxNum + 1).padStart(4, '0')}`;
}

export async function findMemberByName(name) {
  const gid = await gymId();
  const needle = String(name || '').trim();
  if (!needle) return null;

  const { data, error } = await supabase
    .from('members')
    .select('*')
    .eq('gym_id', gid)
    .ilike('full_name', needle)
    .limit(1);
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row ? rowToMember(row) : null;
}

export async function findMemberByGymMemberId(gymMemberId) {
  const gid = await gymId();
  const needle = String(gymMemberId || '').trim();
  if (!needle) return null;

  // Serial-only lookup (UI identifier after membership allotment), e.g. 42 or ID-42
  const serialText = needle.replace(/^#/, '').trim().replace(/^id[\s\-:_]*/i, '').trim();
  if (/^\d+$/.test(serialText)) {
    const serialNum = Number(serialText);

    // Prefer members.gym_member_id (set on allotment / legacy import)
    const { data: byGymId } = await supabase
      .from('members')
      .select('*')
      .eq('gym_id', gid)
      .eq('gym_member_id', serialText)
      .limit(1);
    const gymRow = Array.isArray(byGymId) ? byGymId[0] : byGymId;
    if (gymRow) {
      return withMembershipSerial(rowToMember(gymRow), serialNum);
    }

    const { data: membershipRows, error: serialErr } = await supabase
      .from('memberships')
      .select('member_id, serial_no')
      .eq('gym_id', gid)
      .eq('serial_no', serialNum)
      .order('created_at', { ascending: false })
      .limit(1);
    if (!serialErr && membershipRows?.[0]?.member_id) {
      return fetchMemberById(membershipRows[0].member_id);
    }
  }

  // Exact internal gym_member_id match (legacy / pre-allotment)
  const { data, error } = await supabase
    .from('members')
    .select('*')
    .eq('gym_id', gid)
    .eq('gym_member_id', needle)
    .limit(1);
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (row) return fetchMemberById(row.id);

  return null;
}

// ─── Memberships ───────────────────────────────────────────

const MEMBERSHIP_LIST_COLUMNS = 'id, gym_id, member_id, package_name, start_date, expiry_date, dates_tbc, status, frozen, auto_renewal, lifetime, serial_no, duration, complimentary, created_at';
const MEMBERSHIP_MEMBER_EMBED = 'members(id, full_name, gym_member_id, branch, phone, email, gender, dob, trainer, joining_date, profile, status)';

async function fetchMemberIdsForBranch(gid, branchFilter, { includeBlankBranch = true } = {}) {
  const branch = String(branchFilter || '').trim();
  if (!gid) return [];
  let query = supabase
    .from('members')
    .select('id')
    .eq('gym_id', gid)
    .limit(3000);
  if (branch && branch !== 'all') {
    query = applyMemberBranchOrFilter(query, branch, { includeBlankBranch });
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map((row) => row.id).filter(Boolean);
}

async function getActiveFrozenMemberIds() {
  const gid = await gymId();
  try {
    const data = await fetchAllPaged(() => (
      supabase
    .from('membership_freezes')
        .select('member_id, id')
    .eq('gym_id', gid)
        .eq('status', 'active')
        .order('id', { ascending: true })
    ));
    return new Set((data || []).map((row) => row.member_id).filter(Boolean));
  } catch (error) {
    console.warn('Could not load membership freezes for gym', gid, error.message);
    return new Set();
  }
}

async function getActiveFrozenMemberIdsFor(memberIds = []) {
  const ids = [...new Set((memberIds || []).filter(Boolean))];
  if (!ids.length) return new Set();
  const gid = await gymId();
  const frozen = new Set();
  try {
    for (const chunk of chunkIds(ids)) {
      const { data, error } = await supabase
      .from('membership_freezes')
      .select('member_id')
        .eq('gym_id', gid)
        .eq('status', 'active')
        .in('member_id', chunk);
      if (error) throw error;
      (data || []).forEach((row) => {
        if (row.member_id) frozen.add(row.member_id);
      });
    }
  } catch (error) {
    console.warn('Could not load membership freezes for page', error.message);
  }
  return frozen;
}

function normalizeMembershipPaymentSource(source) {
  return String(source || '').trim().toLowerCase().replace(/-/g, '_');
}

/** Payments that belong on Memberships (new package sale from Subscription page). */
function isNewSubscriptionPaymentSource(source) {
  const s = normalizeMembershipPaymentSource(source);
  if (!s) return true;
  return (
    s === 'subscription'
    || s === 'membership'
    || s === 'new_membership'
    || s === 'subscriptions'
  );
}

function isRenewalPaymentSource(source) {
  const s = normalizeMembershipPaymentSource(source);
  return s === 'renewal_sale' || s === 'renewal';
}

/** True when a payment row is a renewal (source or note from Renewal Sale). */
function isRenewalPaymentRow(pay) {
  if (!pay) return false;
  if (isRenewalPaymentSource(pay.source)) return true;
  const note = String(pay.payment_note || pay.paymentNote || '');
  return /(^|[·|]\s*)(Renewed|Complimentary renewal)\b/i.test(note);
}

function paymentsForMembershipRow(row, payRows = []) {
  const mid = row?.member_id || row?.memberId;
  if (!mid) return [];
  const pkg = String(row.package_name || row.package || '').trim().toLowerCase();
  return (payRows || []).filter((pay) => {
    if ((pay.member_id || pay.memberId) !== mid) return false;
    const payPkg = String(pay.package_name || pay.package || '').trim().toLowerCase();
    if (pkg && payPkg && pkg !== payPkg) return false;
    return true;
  });
}

/**
 * Renewal-only memberships belong on Renewal Sale history, not Memberships.
 * Keep rows that have a new-subscription payment, or no payments (legacy / complimentary new).
 */
function isRenewalOnlyMembership(row, payRows = []) {
  const related = paymentsForMembershipRow(row, payRows);
  if (!related.length) return false;
  const hasSubscription = related.some((pay) => (
    !isRenewalPaymentRow(pay) && isNewSubscriptionPaymentSource(pay.source)
  ));
  if (hasSubscription) return false;
  return related.some((pay) => isRenewalPaymentRow(pay));
}

function feesFromPaymentRow(pay, packagePriceByName = new Map()) {
  if (!pay) return {};
  const pkgNorm = String(pay.package_name || '').trim().toLowerCase();
  const paid = Number(pay.amount) || 0;
  const hasPackagePriceColumn = Object.prototype.hasOwnProperty.call(pay, 'package_price');
  const packagePriceRaw = pay.package_price;
  // Explicit 0 / blank-saved price must stay 0 — do not overwrite with catalog
  const packagePriceExplicit = hasPackagePriceColumn
    && packagePriceRaw != null
    && packagePriceRaw !== '';
  let packageFee = packagePriceExplicit ? (Number(packagePriceRaw) || 0) : 0;
  if (!packagePriceExplicit && packageFee <= 0 && pkgNorm) {
    packageFee = packagePriceByName.get(pkgNorm) || 0;
  }
  // Only treat collected amount as the full price when price was never stored and sale is completed
  if (
    !packagePriceExplicit
    && packageFee <= 0
    && paid > 0
    && String(pay.status || '').toLowerCase() === 'completed'
  ) {
    packageFee = paid;
  }

  const registrationFee = Number(pay.registration_fee) || 0;
  const discount = Number(pay.discount_amount) || 0;
  const pt = Number(pay.personal_training_fee) || 0;
  const classes = Number(pay.classes_fee) || 0;
  const totalDue = Math.max(0, packageFee - discount + registrationFee + pt + classes);
  let paymentSplits = [];
  if (Array.isArray(pay.payment_splits) && pay.payment_splits.length) {
    paymentSplits = normalizeSplitRows(pay.payment_splits);
  } else if (paid > 0 && pay.payment_method) {
    paymentSplits = normalizeSplitRows([
      { method: methodKeyFromLabel(pay.payment_method), amount: paid },
    ]);
  }
  const hasFeeBreakdown = packagePriceExplicit
    || packageFee > 0
    || registrationFee > 0
    || pt > 0
    || classes > 0;
  return {
    packageFee,
    registrationFee,
    discountAmount: discount,
    personalTrainingFee: pt,
    classesFee: classes,
    pendingAmount: hasFeeBreakdown ? Math.max(0, totalDue - paid) : 0,
    totalPaid: paid,
    paymentSplits,
    paymentDbId: pay.id || null,
    paymentRef: pay.payment_ref,
    invoiceId: pay.invoice_id,
    paymentStatus: pay.status || 'completed',
    paymentDate: pay.paid_at,
    paymentNote: pay.payment_note || '',
    startDate: pay.start_date,
    endDate: pay.end_date,
    paymentSource: pay.source || 'subscription',
    packagePriceExplicit,
  };
}

function buildMembershipFeeMaps(payRows, packagePriceByName, { subscriptionOnly = true } = {}) {
  const feeByMember = new Map();
  const latestPaymentByMemberId = new Map();

  const feeScore = (fees) => (
    (Number(fees.packageFee) || 0)
    + (Number(fees.registrationFee) || 0)
    + (Number(fees.totalPaid) || 0)
  );

  const sourceRank = (source) => {
    if (isNewSubscriptionPaymentSource(source)) return 2;
    if (isRenewalPaymentSource(source)) return 0;
    return 1;
  };

  const storeFees = (mapKey, fees) => {
    if (!mapKey.includes('::')) return;
    const existing = feeByMember.get(mapKey);
    if (!existing) {
      feeByMember.set(mapKey, fees);
      return;
    }
    const existingRank = sourceRank(existing.paymentSource);
    const nextRank = sourceRank(fees.paymentSource);
    if (nextRank > existingRank) {
      feeByMember.set(mapKey, fees);
      return;
    }
    if (nextRank === existingRank && feeScore(fees) >= feeScore(existing)) {
      feeByMember.set(mapKey, fees);
    }
  };

  (payRows || []).forEach((pay) => {
    if (!pay.member_id) return;
    // Memberships page: only new subscription sales — never renewal / ancillary
    if (subscriptionOnly) {
      if (isRenewalPaymentRow(pay)) return;
      if (!isNewSubscriptionPaymentSource(pay.source)) return;
    }

    const pkgNorm = String(pay.package_name || '').trim().toLowerCase();
    const key = pkgNorm ? `${pay.member_id}::${pkgNorm}` : `${pay.member_id}::__nopkg__`;
    const fees = feesFromPaymentRow(pay, packagePriceByName);
    storeFees(key, fees);

    const existingLatest = latestPaymentByMemberId.get(pay.member_id);
    if (!existingLatest) {
      latestPaymentByMemberId.set(pay.member_id, fees);
      return;
    }
    const existingRank = sourceRank(existingLatest.paymentSource);
    const nextRank = sourceRank(fees.paymentSource);
    if (nextRank > existingRank || (nextRank === existingRank && feeScore(fees) >= feeScore(existingLatest))) {
      latestPaymentByMemberId.set(pay.member_id, fees);
    }
  });

  return { feeByMember, latestPaymentByMemberId };
}

async function loadPackagePriceByName(gid) {
  const packagePriceByName = new Map();
  try {
    const { data: packageRows } = await supabase
      .from('packages')
      .select('name, price')
      .eq('gym_id', gid);
    (packageRows || []).forEach((pkg) => {
      const key = String(pkg.name || '').trim().toLowerCase();
      const price = Number(pkg.price) || 0;
      if (key && price > 0) packagePriceByName.set(key, price);
    });
  } catch {
    /* catalog fallback is optional */
  }
  return packagePriceByName;
}

async function loadPaymentsForMembers(gid, memberIds) {
  const ids = [...new Set((memberIds || []).filter(Boolean))];
  if (!ids.length) return [];

  const selectAttempts = [
    'id, member_id, package_name, amount, package_price, registration_fee, discount_amount, personal_training_fee, classes_fee, payment_method, payment_splits, payment_ref, invoice_id, status, paid_at, payment_note, start_date, end_date, source, created_at',
    'id, member_id, package_name, amount, package_price, registration_fee, discount_amount, personal_training_fee, classes_fee, payment_method, payment_splits, payment_ref, invoice_id, status, paid_at, payment_note, source, created_at',
    'id, member_id, package_name, amount, package_price, registration_fee, discount_amount, personal_training_fee, classes_fee, payment_method, payment_ref, invoice_id, status, paid_at, payment_note, source, created_at',
    'id, member_id, package_name, amount, package_price, registration_fee, discount_amount, payment_method, payment_ref, invoice_id, status, paid_at, source',
  ];

  const payRows = [];
  for (const chunk of chunkIds(ids)) {
    let chunkRows = null;
    let lastError = null;
    for (const paymentSelect of selectAttempts) {
      let query = supabase
        .from('payments')
        .select(paymentSelect)
        .eq('gym_id', gid)
        .in('member_id', chunk)
        .order('paid_at', { ascending: false });
      if (/\bcreated_at\b/.test(paymentSelect)) {
        query = query.order('created_at', { ascending: false });
      }
      const { data, error } = await query;
      if (!error) {
        chunkRows = data || [];
        break;
      }
      lastError = error;
      // Only retry when optional columns are missing
      if (!/column|schema cache|does not exist/i.test(error.message || '')) {
        throw error;
      }
    }
    if (chunkRows == null && lastError) throw lastError;
    payRows.push(...(chunkRows || []));
  }
  return payRows;
}

function mapMembershipPageRow(row, feeByMember, latestPaymentByMemberId, packagePriceByName, frozenMemberIds, payRows = []) {
  const linkedRaw = row.members;
  const linked = Array.isArray(linkedRaw) ? (linkedRaw[0] || {}) : (linkedRaw || {});
  const complimentary = Boolean(row.complimentary);
  const pkgNorm = String(row.package_name || '').trim().toLowerCase();
  const pkgKey = pkgNorm ? `${row.member_id}::${pkgNorm}` : `${row.member_id}::__nopkg__`;
  let feeInfo = complimentary
    ? {
      packageFee: 0,
      registrationFee: 0,
      discountAmount: 0,
      personalTrainingFee: 0,
      classesFee: 0,
      pendingAmount: 0,
      totalPaid: 0,
      paymentSplits: [],
      paymentStatus: 'complimentary',
      paymentSource: 'subscription',
    }
    : { ...(feeByMember.get(pkgKey) || feeByMember.get(`${row.member_id}::__nopkg__`) || {}) };

  // Prefer the payment for this membership start date (keeps real fees on date-filtered lists)
  if (!complimentary && payRows?.length) {
    const related = paymentsForMembershipRow(row, payRows).filter((pay) => (
      !isRenewalPaymentRow(pay) && isNewSubscriptionPaymentSource(pay.source)
    ));
    const start = String(row.start_date || '').slice(0, 10);
    const dated = start
      ? related.find((pay) => {
        const payStart = String(pay.start_date || '').slice(0, 10);
        const paidDay = String(pay.paid_at || '').slice(0, 10);
        return payStart === start || paidDay === start;
      })
      : null;
    const matched = dated || related[0];
    if (matched) {
      feeInfo = feesFromPaymentRow(matched, packagePriceByName);
    }
  }

  if (!complimentary) {
    const latest = row.member_id ? latestPaymentByMemberId.get(row.member_id) : null;
    const hasUsefulFees = (Number(feeInfo.packageFee) > 0)
      || (Number(feeInfo.registrationFee) > 0)
      || (Number(feeInfo.totalPaid) > 0)
      || Boolean(feeInfo.paymentDbId);
    if (latest?.paymentDbId && !hasUsefulFees) {
      feeInfo = { ...latest, ...feeInfo };
    } else if (
      latest?.paymentDbId
      && !feeInfo.packagePriceExplicit
      && !(Number(feeInfo.packageFee) > 0)
      && Number(latest.packageFee) > 0
    ) {
      feeInfo = {
        ...feeInfo,
        packageFee: latest.packageFee,
        registrationFee: feeInfo.registrationFee || latest.registrationFee,
        discountAmount: feeInfo.discountAmount || latest.discountAmount,
        totalPaid: feeInfo.totalPaid || latest.totalPaid,
        pendingAmount: feeInfo.pendingAmount || latest.pendingAmount,
        paymentDbId: feeInfo.paymentDbId || latest.paymentDbId,
        paymentRef: feeInfo.paymentRef || latest.paymentRef,
        invoiceId: feeInfo.invoiceId || latest.invoiceId,
        paymentSplits: feeInfo.paymentSplits?.length ? feeInfo.paymentSplits : latest.paymentSplits,
        paymentStatus: feeInfo.paymentStatus || latest.paymentStatus,
        paymentDate: feeInfo.paymentDate || latest.paymentDate,
      };
    }
    if (!(Number(feeInfo.packageFee) > 0) && pkgNorm && !feeInfo.paymentDbId && !feeInfo.packagePriceExplicit) {
      const catalogPrice = packagePriceByName.get(pkgNorm) || 0;
      if (catalogPrice > 0) feeInfo.packageFee = catalogPrice;
    }
  }
  const membership = rowToMembership(row, {
    ...linked,
    name: linked.full_name || linked.name,
    full_name: linked.full_name,
    gym_member_id: linked.gym_member_id,
    gymId: linked.gym_member_id,
    joining_date: linked.joining_date,
    joiningDate: linked.joining_date || linked.joiningDate || '',
    branch: linked.branch || row.branch || row.branch_name || '',
  }, feeInfo);
  if (frozenMemberIds.has(row.member_id)) {
    return { ...membership, frozen: true, status: 'inactive' };
  }
  return membership;
}

function mapMembershipRpcRow(row) {
  const members = row.members || {};
  const complimentary = Boolean(row.complimentary);
  const feeInfo = {
    packageFee: Number(row.package_fee) || 0,
    registrationFee: Number(row.registration_fee) || 0,
    discountAmount: Number(row.discount_amount) || 0,
    personalTrainingFee: Number(row.personal_training_fee) || 0,
    classesFee: Number(row.classes_fee) || 0,
    totalPaid: Number(row.total_paid) || 0,
    pendingAmount: Number(row.pending_amount) || 0,
    paymentSplits: Array.isArray(row.payment_splits) ? normalizeSplitRows(row.payment_splits) : [],
    paymentDbId: row.payment_id || null,
    paymentRef: row.payment_ref,
    invoiceId: row.invoice_id,
    paymentStatus: complimentary ? 'complimentary' : (row.payment_status || ''),
    paymentDate: row.paid_at || row.entry_day || '',
    paymentNote: row.payment_note || '',
    paymentSource: row.payment_source || 'subscription',
  };
  return rowToMembership({
    ...row,
    frozen: Boolean(row.frozen),
  }, {
    ...members,
    name: members.full_name || members.name,
    full_name: members.full_name,
    gym_member_id: members.gym_member_id,
    gymId: members.gym_member_id,
    joining_date: members.joining_date,
    joiningDate: members.joining_date || '',
    branch: members.branch || '',
    profile: members.profile || {},
  }, feeInfo);
}

function resolveMembershipsPageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

/** Paged memberships for UI. Prefer this over fetchMemberships(). */
export async function fetchMembershipsPage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  /** Allow larger pages for print/export of a filtered date range. */
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveMembershipsPageDates(filters, { receptionist, search });
  const branchFromFilters = String(scopedFilters.branch || '').trim();
  let branchFilter = String(
    branch
    || (branchFromFilters && branchFromFilters !== 'all' ? branchFromFilters : '')
    || getReceptionistBranchFilter()
    || '',
  ).trim();
  if (receptionist && (!branchFilter || branchFilter === 'all')) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* continue */
    }
  }
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchMembershipsPageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      return {
        rows: (rpc.rows || []).map(mapMembershipRpcRow),
        total: rpc.total,
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Memberships page RPC failed, using fallback', err?.message || err);
  }

  return fetchMembershipsPageLegacy({
    page: safePage,
    pageSize: safeSize,
    search,
    branch: branchFilter,
    filters: scopedFilters,
    forExport,
  });
}

async function fetchMembershipsPageLegacy({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(500, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;

  const branchFromFilters = String(filters.branch || '').trim();
  let branchFilter = String(
    branch
    || (branchFromFilters && branchFromFilters !== 'all' ? branchFromFilters : '')
    || getReceptionistBranchFilter()
    || '',
  ).trim();
  // Resolve branch from profile when session has not synced yet (avoids empty / Bad Request races).
  if (isReceptionistPanelScope() && (!branchFilter || branchFilter === 'all')) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* continue without hard-empty */
    }
  }

  let query = supabase
    .from('memberships')
    .select(`${MEMBERSHIP_LIST_COLUMNS}, ${MEMBERSHIP_MEMBER_EMBED}`, { count: 'exact' })
    .eq('gym_id', gid);

  const packageFilter = sanitizeIlike(
    filters.package && filters.package !== 'all' && filters.package !== '—'
      ? filters.package
      : '',
  );
  if (packageFilter) {
    query = query.ilike('package_name', `%${packageFilter}%`);
  }

  const durationFilterRaw = String(filters.duration || '').trim();
  if (durationFilterRaw && durationFilterRaw !== 'all' && durationFilterRaw !== '—') {
    if (/^tbc$/i.test(durationFilterRaw)) {
      query = query.eq('dates_tbc', true);
    } else {
      const durationFilter = sanitizeIlike(durationFilterRaw);
      if (durationFilter) {
        query = query.ilike('duration', `%${durationFilter}%`);
      }
    }
  }

  // Resolve member-column filters on `members` first (same as Members table),
  // then restrict memberships by member_id — embed filters are unreliable.
  const memberNeedle = sanitizeIlike(
    filters.member && filters.member !== 'all' ? filters.member : '',
  );
  const phoneNeedleRaw = sanitizeIlike(
    filters.phone && filters.phone !== 'all' ? filters.phone : '',
  );
  const emailNeedle = sanitizeIlike(
    filters.email && filters.email !== 'all' ? filters.email : '',
  );
  const genderFilter = sanitizeIlike(
    filters.gender && filters.gender !== 'all' && filters.gender !== '—'
      ? filters.gender
      : '',
  );
  const trainerFilter = sanitizeIlike(
    filters.trainer && filters.trainer !== 'all' && filters.trainer !== '—'
      ? filters.trainer
      : '',
  );
  const dobFilter = String(filters.dob || '').trim();
  const cnicNeedle = sanitizeIlike(
    filters.cnic && filters.cnic !== 'all' ? filters.cnic : '',
  );
  const serialNeedle = sanitizeIlike(filters.serialNo || filters.gymId);
  const applyBranchToMembers = Boolean(
    isReceptionistPanelScope() || (branchFilter && branchFilter !== 'all'),
  );

  const hasMemberColumnFilters = Boolean(
    memberNeedle
    || phoneNeedleRaw
    || emailNeedle
    || genderFilter
    || trainerFilter
    || (dobFilter && dobFilter !== 'all' && dobFilter !== '—')
    || cnicNeedle
    || serialNeedle,
  );

  // Resolve branch on members embed — member_id.in(thousands) exceeds PostgREST URL limits (400).
  if (!hasMemberColumnFilters && applyBranchToMembers) {
    query = applyMembershipMemberBranchFilter(query, branchFilter);
  }

  if (hasMemberColumnFilters) {
    let memberQuery = supabase
      .from('members')
      .select('id')
      .eq('gym_id', gid)
      .limit(IN_QUERY_CHUNK);

    if (applyBranchToMembers) {
      memberQuery = applyMemberBranchOrFilter(memberQuery, branchFilter);
    }
    if (memberNeedle) {
      memberQuery = memberQuery.ilike('full_name', `%${memberNeedle}%`);
    }
    if (emailNeedle) {
      memberQuery = memberQuery.ilike('email', `%${emailNeedle}%`);
    }
    if (genderFilter) {
      memberQuery = memberQuery.ilike('gender', `%${genderFilter}%`);
    }
    if (trainerFilter) {
      memberQuery = memberQuery.ilike('trainer', `%${trainerFilter}%`);
    }
    if (dobFilter && dobFilter !== 'all' && dobFilter !== '—') {
      const dobSafe = sanitizeIlike(dobFilter);
      if (/^\d{4}-\d{2}-\d{2}$/.test(dobFilter.slice(0, 10))) {
        memberQuery = memberQuery.eq('dob', dobFilter.slice(0, 10));
      } else if (dobSafe) {
        memberQuery = memberQuery.filter('dob::text', 'ilike', `%${dobSafe}%`);
      }
    }
    if (cnicNeedle) {
      memberQuery = memberQuery.filter('profile->>cnic', 'ilike', `%${cnicNeedle}%`);
    }
    if (phoneNeedleRaw) {
      const digits = phoneNeedleRaw.replace(/\D/g, '');
      const hyphenated = digits.length > 4
        ? `${digits.slice(0, 4)}-${digits.slice(4)}`
        : digits;
      memberQuery = memberQuery.or([
        `phone.ilike.%${hyphenated}%`,
        digits && digits !== hyphenated ? `phone.ilike.%${digits}%` : null,
        phoneNeedleRaw !== hyphenated ? `phone.ilike.%${phoneNeedleRaw}%` : null,
      ].filter(Boolean).join(','));
    }
    if (serialNeedle) {
      const serialOr = numericGymMemberIdSearchOr(serialNeedle);
      if (serialOr) {
        memberQuery = memberQuery.or(serialOr);
      }
    }

    const [{ data: memberHits }, serialMembershipIds] = await Promise.all([
      memberQuery,
      serialNeedle ? findMembershipIdsBySerialContains(gid, serialNeedle) : Promise.resolve([]),
    ]);
    const memberIds = (memberHits || []).map((m) => m.id).filter(Boolean);
    const cappedMemberIds = memberIds.slice(0, IN_QUERY_CHUNK);
    const cappedSerialIds = (serialMembershipIds || []).slice(0, IN_QUERY_CHUNK);

    if (serialNeedle) {
      const serialOrParts = [];
      if (cappedSerialIds.length) {
        serialOrParts.push(`id.in.(${cappedSerialIds.join(',')})`);
      }
      if (cappedMemberIds.length) {
        serialOrParts.push(`member_id.in.(${cappedMemberIds.join(',')})`);
      }
      if (!serialOrParts.length) {
        return { rows: [], total: 0, page: safePage, pageSize: safeSize };
      }
      query = query.or(serialOrParts.join(','));
    } else if (!cappedMemberIds.length) {
      return { rows: [], total: 0, page: safePage, pageSize: safeSize };
    } else {
      query = query.in('member_id', cappedMemberIds);
    }
  }

  const startFilter = String(filters.startDate || filters.start || '').trim();
  if (startFilter && startFilter !== 'all' && startFilter !== '—') {
    const startIso = normalizeDateFilterToIso(startFilter);
    if (startIso) {
      query = query.eq('start_date', startIso);
    }
  }

  const dateFilter = String(filters.date || filters.createdAt || '').trim();
  if (dateFilter && dateFilter !== 'all' && dateFilter !== '—') {
    const dateIso = normalizeDateFilterToIso(dateFilter);
    // Entry / sale date column — membership created day (same as payment day for new sales).
    if (dateIso) {
      query = query
        .gte('created_at', `${dateIso}T00:00:00`)
        .lte('created_at', `${dateIso}T23:59:59.999`);
    }
  }

  const rangeStart = String(filters.rangeStart || filters.dateFrom || '').trim().slice(0, 10);
  const rangeEnd = String(filters.rangeEnd || filters.dateTo || '').trim().slice(0, 10);
  const hasMembershipDateRange = (
    (rangeStart && /^\d{4}-\d{2}-\d{2}$/.test(rangeStart))
    || (rangeEnd && /^\d{4}-\d{2}-\d{2}$/.test(rangeEnd))
  );
  // Match Date column (start_date), not DB insert time — avoids empty / wrong-day results.
  if (hasMembershipDateRange) {
    const start = (rangeStart && /^\d{4}-\d{2}-\d{2}$/.test(rangeStart)) ? rangeStart : '1900-01-01';
    const end = (rangeEnd && /^\d{4}-\d{2}-\d{2}$/.test(rangeEnd)) ? rangeEnd : '2100-12-31';
    query = query.or(
      [
        `and(start_date.gte.${start},start_date.lte.${end})`,
        `and(start_date.is.null,created_at.gte.${start}T00:00:00,created_at.lte.${end}T23:59:59.999)`,
      ].join(','),
    );
  }

  const expiryFilter = String(filters.expiryDate || filters.expiry || '').trim();
  if (expiryFilter && expiryFilter !== 'all' && expiryFilter !== '—') {
    const expiryIso = normalizeDateFilterToIso(expiryFilter);
    if (expiryIso) {
      query = query.eq('expiry_date', expiryIso);
    }
  }

  const autoRenewal = String(filters.autoRenewal || '').trim().toLowerCase();
  if (autoRenewal === 'yes') query = query.eq('auto_renewal', true);
  if (autoRenewal === 'no') query = query.eq('auto_renewal', false);

  const statusFilter = String(filters.status || '').trim().toLowerCase();
  if (statusFilter && statusFilter !== 'all') {
    if (statusFilter === 'tbc') {
      query = query.eq('dates_tbc', true);
    } else if (statusFilter === 'lifetime') {
      query = query.eq('lifetime', true);
    } else if (statusFilter === 'inactive') {
      query = query.or('frozen.eq.true,status.eq.inactive');
    } else if (statusFilter === 'expired' || statusFilter === 'due') {
      query = query.eq('status', statusFilter === 'due' ? 'due' : 'expired');
    } else if (statusFilter === 'cancelled') {
      query = query.eq('status', 'cancelled');
    } else if (statusFilter === 'active') {
      query = query
        .or('dates_tbc.is.null,dates_tbc.eq.false')
        .or('lifetime.is.null,lifetime.eq.false')
        .or('frozen.is.null,frozen.eq.false')
        .neq('status', 'expired')
        .neq('status', 'cancelled')
        .neq('status', 'inactive');
    }
  }

  const needle = String(search || '').trim();
  if (needle) {
    const safe = sanitizeIlike(needle);
    const memberOr = buildMemberTextSearchOr(needle);
    if (safe || memberOr) {
      let memberQuery = supabase
        .from('members')
        .select('id')
        .eq('gym_id', gid)
        .limit(IN_QUERY_CHUNK);
      if (memberOr) {
        memberQuery = memberQuery.or(memberOr);
      }
      if (branchFilter && branchFilter !== 'all') {
        memberQuery = applyMemberBranchOrFilter(memberQuery, branchFilter);
      }
      const [{ data: memberHits }, serialMembershipIds] = await Promise.all([
        memberQuery,
        findMembershipIdsBySerialContains(gid, safe),
      ]);
      const memberIds = (memberHits || []).map((m) => m.id).filter(Boolean).slice(0, IN_QUERY_CHUNK);
      const cappedSerialIds = (serialMembershipIds || []).slice(0, IN_QUERY_CHUNK);

      const orParts = [];
      if (safe) {
        orParts.push(`package_name.ilike.%${safe}%`);
        orParts.push(`duration.ilike.%${safe}%`);
      }
      if (cappedSerialIds.length) {
        orParts.push(`id.in.(${cappedSerialIds.join(',')})`);
      }
      if (memberIds.length) {
        orParts.push(`member_id.in.(${memberIds.join(',')})`);
      }
      if (!orParts.length) {
        return { rows: [], total: 0, page: safePage, pageSize: safeSize };
      }
      query = query.or(orParts.join(','));
    }
  }

  const paymentFeeMatch = await findMembershipKeysMatchingPaymentFilters(gid, filters);
  if (paymentFeeMatch) {
    if (!paymentFeeMatch.memberIds.length) {
      return { rows: [], total: 0, page: safePage, pageSize: safeSize };
    }
    query = query.in('member_id', paymentFeeMatch.memberIds.slice(0, IN_QUERY_CHUNK));
  }

  // Fee/method/note filters need payment join data — paginate in memory after mapping.
  const usePaymentFeePagination = Boolean(paymentFeeMatch);

  let { data, error, count } = await (
    usePaymentFeePagination
      ? (
        hasMembershipDateRange
          ? query
            .order('start_date', { ascending: true, nullsFirst: false })
            .order('created_at', { ascending: true })
            .order('id', { ascending: true })
            .limit(2000)
          : query
            .order('serial_no', { ascending: true, nullsFirst: false })
            .order('id', { ascending: true })
            .limit(2000)
      )
      : (
        hasMembershipDateRange
          ? query
            .order('start_date', { ascending: true, nullsFirst: false })
            .order('created_at', { ascending: true })
            .order('id', { ascending: true })
          : query
            .order('serial_no', { ascending: true, nullsFirst: false })
            .order('id', { ascending: true })
      ).range(from, to)
  );

  // Older DBs may not have memberships.duration yet — retry without that column / duration filters
  if (error && /duration/i.test(error.message || '')) {
    const fallbackCols = MEMBERSHIP_LIST_COLUMNS.replace(', duration', '');
    let retryQuery = supabase
      .from('memberships')
      .select(`${fallbackCols}, ${MEMBERSHIP_MEMBER_EMBED}`, { count: 'exact' })
      .eq('gym_id', gid);

    if (branchFilter && branchFilter !== 'all') {
      retryQuery = applyMembershipMemberBranchFilter(retryQuery, branchFilter);
    }

    if (packageFilter) {
      retryQuery = retryQuery.ilike('package_name', `%${packageFilter}%`);
    }

    if (needle) {
      const safe = sanitizeIlike(needle);
      const memberOr = buildMemberTextSearchOr(needle);
      let memberQuery = supabase
        .from('members')
        .select('id')
        .eq('gym_id', gid)
        .limit(IN_QUERY_CHUNK);
      if (memberOr) memberQuery = memberQuery.or(memberOr);
      if (branchFilter && branchFilter !== 'all') {
        memberQuery = applyMemberBranchOrFilter(memberQuery, branchFilter);
      }
      const [{ data: memberHits }, serialMembershipIds] = await Promise.all([
        memberQuery,
        findMembershipIdsBySerialContains(gid, safe),
      ]);
      const memberIds = (memberHits || []).map((m) => m.id).filter(Boolean).slice(0, IN_QUERY_CHUNK);
      const cappedSerialIds = (serialMembershipIds || []).slice(0, IN_QUERY_CHUNK);
      const orParts = [];
      if (safe) orParts.push(`package_name.ilike.%${safe}%`);
      if (cappedSerialIds.length) orParts.push(`id.in.(${cappedSerialIds.join(',')})`);
      if (memberIds.length) orParts.push(`member_id.in.(${memberIds.join(',')})`);
      if (orParts.length) retryQuery = retryQuery.or(orParts.join(','));
    }

    const retry = await retryQuery
      .order('serial_no', { ascending: true, nullsFirst: false })
      .order('id', { ascending: true })
      .range(from, to);
    data = retry.data;
    error = retry.error;
    count = retry.count;
  }

  // Older DBs may not have memberships.complimentary yet
  if (error && /complimentary/i.test(error.message || '')) {
    const fallbackCols = MEMBERSHIP_LIST_COLUMNS
      .replace(', duration', '')
      .replace(', complimentary', '');
    const retry = await supabase
      .from('memberships')
      .select(`${fallbackCols}, ${MEMBERSHIP_MEMBER_EMBED}`, { count: 'exact' })
      .eq('gym_id', gid)
      .order('serial_no', { ascending: true, nullsFirst: false })
      .order('id', { ascending: true })
      .range(from, to);
    data = retry.data;
    error = retry.error;
    count = retry.count;
  }

  if (error) throw error;

  const rows = data || [];
  // Fix drifted serials (e.g. member ID-5421 but memberships.serial_no still 5426)
  await syncMembershipSerialsFromMemberEmbed(rows).catch(() => {});

  const pageMemberIds = rows.map((row) => row.member_id).filter(Boolean);
  const [packagePriceByName, frozenMemberIds, payRows] = await Promise.all([
    loadPackagePriceByName(gid),
    getActiveFrozenMemberIdsFor(pageMemberIds),
    loadPaymentsForMembers(gid, pageMemberIds).catch(() => []),
  ]);
  const { feeByMember, latestPaymentByMemberId } = buildMembershipFeeMaps(payRows, packagePriceByName, {
    subscriptionOnly: true,
  });

  // One subscription row per member + package (drop renewal/retry inserts)
  // Prefer start_date (table Date column) so range filters stay sorted correctly.
  const sortedRows = [...rows].sort((a, b) => {
    const aTime = String(a.start_date || a.created_at || '').slice(0, 10);
    const bTime = String(b.start_date || b.created_at || '').slice(0, 10);
    if (aTime !== bTime) return aTime.localeCompare(bTime);
    return String(a.id || '').localeCompare(String(b.id || ''));
  });
  const seenKeys = new Set();
  const uniqueRows = sortedRows.filter((row) => {
    const pkg = String(row.package_name || '').trim().toLowerCase();
    const key = row.member_id ? `${row.member_id}|${pkg}` : `row:${row.id}`;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });

  // Hide renewal-only cases (e.g. Renewal Sale for ID-3153) — they belong on Renewal history
  const subscriptionRows = uniqueRows.filter((row) => !isRenewalOnlyMembership(row, payRows));

  let mapped = subscriptionRows.map((row) => mapMembershipPageRow(
    row,
    feeByMember,
    latestPaymentByMemberId,
    packagePriceByName,
    frozenMemberIds,
    payRows,
  ));

  if (paymentFeeMatch?.keys) {
    mapped = mapped.filter((m) => {
      const pkg = String(m.package || m.package_name || '').trim().toLowerCase();
      const key = m.memberId ? `${m.memberId}|${pkg}` : '';
      const altKey = m.member_id ? `${m.member_id}|${pkg}` : '';
      return (key && paymentFeeMatch.keys.has(key))
        || (altKey && paymentFeeMatch.keys.has(altKey));
    });
    const totalFiltered = mapped.length;
    mapped = mapped.slice(from, to);
    return {
      rows: mapped,
      total: totalFiltered,
      page: safePage,
      pageSize: safeSize,
    };
  }

  return {
    rows: mapped,
    total: Number(count) || 0,
    page: safePage,
    pageSize: safeSize,
  };
}

/** Memberships for one member — subscription sale eligibility checks. */
export async function fetchMembershipsForMember(memberId) {
  if (!memberId) return [];
  const gid = await gymId();
  const { data, error } = await supabase
    .from('memberships')
    .select('id, gym_id, member_id, package_name, start_date, expiry_date, dates_tbc, status, frozen, auto_renewal, lifetime, serial_no, created_at')
    .eq('gym_id', gid)
    .eq('member_id', memberId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map((row) => rowToMembership(row, { id: memberId }, {}));
}

/** Full membership list — export / rare use only. Prefer fetchMembershipsPage for UI. */
export async function fetchMemberships() {
  const gid = await gymId();
  return cachedQuery(cacheKey(`gym:${gid}:memberships:v2`), async () => {
  // Members for this gym — used for Gym ID / branch
  const memberRows = await fetchAllPaged(() => (
    supabase
      .from('members')
      .select('id, full_name, gym_member_id, branch')
      .eq('gym_id', gid)
      .order('id', { ascending: true })
  ));

  const memberById = new Map((memberRows || []).map((m) => [m.id, m]));

  let data = await fetchAllPaged(() => (
    supabase
      .from('memberships')
      .select('id, gym_id, member_id, package_name, start_date, expiry_date, dates_tbc, status, frozen, auto_renewal, lifetime, serial_no, created_at')
      .eq('gym_id', gid)
      .order('id', { ascending: true })
  ));

  // Collapse duplicate DB rows for the same member + package — keep the original
  // (oldest) subscription row, not later renewal/retry inserts.
  data = [...(data || [])].sort((a, b) => {
    const aTime = String(a.created_at || a.start_date || '');
    const bTime = String(b.created_at || b.start_date || '');
    if (aTime !== bTime) return aTime.localeCompare(bTime);
    return String(a.id || '').localeCompare(String(b.id || ''));
  });
  const seenMembershipKeys = new Set();
  data = data.filter((row) => {
    const pkg = String(row.package_name || '').trim().toLowerCase();
    const linked = memberById.get(row.member_id);
    const gymMemberId = String(linked?.gym_member_id || '').trim().toLowerCase();
    const key = row.member_id
      ? `member:${row.member_id}|${pkg}`
      : gymMemberId
        ? `gym:${gymMemberId}|${pkg}`
        : `row:${row.id}`;
    if (seenMembershipKeys.has(key)) return false;
    seenMembershipKeys.add(key);
    return true;
  });

  const frozenMemberIds = await getActiveFrozenMemberIds();

  // Latest payment fees per member — new subscriptions only (never renewals)
  const packagePriceByName = new Map();
  try {
    const { data: packageRows } = await supabase
      .from('packages')
      .select('name, price')
      .eq('gym_id', gid);
    (packageRows || []).forEach((pkg) => {
      const key = String(pkg.name || '').trim().toLowerCase();
      const price = Number(pkg.price) || 0;
      if (key && price > 0) packagePriceByName.set(key, price);
    });
  } catch {
    /* catalog fallback is optional */
  }

  let feeByMember = new Map();
  let latestPaymentByMemberId = new Map();
  try {
    // Skip payment enrichment for very large gyms — keeps Memberships page usable
    if ((data || []).length > 1500) {
      throw new Error('skip-payments-for-large-list');
    }
    // Gym-scoped payments only (no orphan member_id scans — those freeze large gyms)
    const payRows = await fetchAllPaged(() => (
      supabase
        .from('payments')
        .select('id, member_id, package_name, amount, package_price, registration_fee, discount_amount, personal_training_fee, classes_fee, payment_method, payment_splits, payment_ref, invoice_id, status, paid_at, payment_note, start_date, end_date, source')
        .eq('gym_id', gid)
        .order('paid_at', { ascending: true })
        .order('id', { ascending: true })
    ));
    ({ feeByMember, latestPaymentByMemberId } = buildMembershipFeeMaps(payRows, packagePriceByName, {
      subscriptionOnly: true,
    }));
    // Drop memberships that only exist from Renewal Sale (keep them off Memberships list)
    data = data.filter((row) => !isRenewalOnlyMembership(row, payRows));
  } catch {
    /* fee enrichment is optional */
  }

  const mapped = data.map((row) => {
    const linked = memberById.get(row.member_id) || {};
    const pkgNorm = String(row.package_name || '').trim().toLowerCase();
    const pkgKey = pkgNorm ? `${row.member_id}::${pkgNorm}` : `${row.member_id}::__nopkg__`;
    let feeInfo = { ...(feeByMember.get(pkgKey) || feeByMember.get(`${row.member_id}::__nopkg__`) || {}) };
    if (!Boolean(row.complimentary)) {
      if (!feeInfo.paymentDbId && row.member_id) {
        const latest = latestPaymentByMemberId.get(row.member_id);
        if (latest?.paymentDbId) {
          feeInfo = { ...latest, ...feeInfo };
        }
      }
      if (!(Number(feeInfo.packageFee) > 0) && pkgNorm) {
        const catalogPrice = packagePriceByName.get(pkgNorm) || 0;
        if (catalogPrice > 0) feeInfo.packageFee = catalogPrice;
      }
    } else {
      feeInfo = {
        packageFee: 0,
        registrationFee: 0,
        discountAmount: 0,
        personalTrainingFee: 0,
        classesFee: 0,
        pendingAmount: 0,
        totalPaid: 0,
        paymentSplits: [],
        paymentStatus: 'complimentary',
        paymentSource: 'subscription',
      };
    }
    const membership = rowToMembership(row, {
      ...linked,
      name: linked.full_name || linked.name,
      full_name: linked.full_name,
      gym_member_id: linked.gym_member_id,
      gymId: linked.gym_member_id,
      branch: linked.branch || row.branch || row.branch_name || '',
    }, feeInfo);
    if (frozenMemberIds.has(row.member_id)) {
      return { ...membership, frozen: true, status: 'inactive' };
    }
    return membership;
  });

  // Show a membership whenever that member is visible to this receptionist
  const visibleMemberIds = new Set(
    filterByReceptionistBranch(
      (memberRows || []).map((m) => ({
        id: m.id,
        branch: m.branch || '',
      })),
      (m) => m.branch,
    ).map((m) => m.id),
  );

  if (!isReceptionistPanelScope()) return dedupeMemberships(mapped);
  return dedupeMemberships(mapped.filter((m) => !m.memberId || visibleMemberIds.has(m.memberId)));
  });
}

export async function updateMembership(id, patch) {
  const row = {};
  if (patch.package) row.package_name = patch.package;
  if (patch.start !== undefined) row.start_date = patch.start || null;
  if (patch.startDate !== undefined) row.start_date = patch.startDate || null;
  if (patch.expiry !== undefined) row.expiry_date = patch.expiry || null;
  if (patch.expiryDate !== undefined) row.expiry_date = patch.expiryDate || null;
  if (patch.status) row.status = patch.status;
  if (patch.frozen !== undefined) row.frozen = patch.frozen;
  if (patch.datesTbc !== undefined) row.dates_tbc = Boolean(patch.datesTbc);
  // Blank start date (and not TBC) → today
  if (row.dates_tbc === false && (row.start_date === null || row.start_date === '')) {
    row.start_date = todayDate();
  }
  if (patch.duration !== undefined) {
    const durationLabel = String(patch.duration || '').trim();
    if (durationLabel) row.duration = durationLabel;
  }
  if (patch.complimentary !== undefined) {
    row.complimentary = Boolean(patch.complimentary);
  }

  const { error } = await updateRowSkippingMissingColumns('memberships', row, id, 'id');
  if (error) throw error;
  await bustGymCache();
}

/**
 * Update membership package/dates and linked payment amount + payment methods.
 */
export async function updateMembershipSale(membershipId, {
  packageName,
  start,
  expiry,
  datesTbc,
  packageFee,
  paymentSplits,
  paymentDbId,
  registrationFee = 0,
  discountAmount = 0,
  personalTrainingFee = 0,
  classesFee = 0,
  entryDate = '',
  duration = '',
}) {
  if (!membershipId) throw new Error('Membership not found.');

  const durationLabel = String(duration || '').trim();

  await updateMembership(membershipId, {
    package: packageName,
    start,
    expiry,
    datesTbc,
    ...(durationLabel ? { duration: durationLabel } : {}),
  });

  const gid = await gymId();
  const { data: membershipRow, error: membershipError } = await supabase
    .from('memberships')
    .select('id, member_id, package_name')
    .eq('id', membershipId)
    .maybeSingle();
  if (membershipError) throw membershipError;

  const paid = sumPaymentSplits(paymentSplits);
  const fee = Math.max(0, Number(packageFee) || 0);
  const reg = Math.max(0, Number(registrationFee) || 0);
  const discount = Math.max(0, Number(discountAmount) || 0);
  const pt = Math.max(0, Number(personalTrainingFee) || 0);
  const classes = Math.max(0, Number(classesFee) || 0);
  const totalDue = Math.max(0, fee - discount + reg + pt + classes);
  const splits = serializePaymentSplits(paymentSplits);
  const left = Math.max(0, totalDue - paid);
  const pkgName = String(packageName || membershipRow?.package_name || '').trim();
  const memberId = membershipRow?.member_id || null;
  const nextPaidAt = String(entryDate || '').trim().slice(0, 10);

  let payId = paymentDbId || null;

  // Resolve linked payment when edit form had no paymentDbId
  if (!payId && memberId) {
    const { data: payRows } = await supabase
      .from('payments')
      .select('id, package_name, member_id, paid_at, created_at')
      .eq('gym_id', gid)
      .eq('member_id', memberId)
      .order('paid_at', { ascending: false });

    const pkgNorm = pkgName.toLowerCase();
    const match = (payRows || []).find(
      (p) => String(p.package_name || '').trim().toLowerCase() === pkgNorm,
    ) || (payRows || [])[0];
    payId = match?.id || null;
  }

  const paymentPatch = {
    package_name: pkgName || undefined,
    package_price: fee,
    registration_fee: reg,
    discount_amount: discount,
    personal_training_fee: pt,
    classes_fee: classes,
    amount: paid,
    payment_splits: splits.length ? splits : undefined,
    payment_method: combineMethodLabels(paymentSplits),
    status: totalDue <= 0 || left <= 0 ? 'completed' : 'pending',
  };
  if (nextPaidAt) {
    paymentPatch.paid_at = nextPaidAt;
  }
  if (start) paymentPatch.start_date = start;
  if (expiry) paymentPatch.end_date = expiry;
  if (durationLabel) paymentPatch.duration = durationLabel;

  if (payId) {
    const { data, error } = await updateRowSkippingMissingColumns(
      'payments',
      paymentPatch,
      payId,
      '*',
    );
    if (error) throw error;
    await bustGymCache();
    return data;
  }

  // No payment row yet — create one so fee / paid amount persist
  if (!memberId) {
    await bustGymCache();
    return null;
  }

  const created = await createPayment({
    memberId,
    member: undefined,
    package: pkgName,
    amount: paid,
    method: combineMethodLabels(paymentSplits),
    date: nextPaidAt || todayDate(),
    status: paymentPatch.status,
    source: 'subscription',
    packagePrice: fee,
    discountAmount: discount,
    registrationFee: reg,
    personalTraining: pt,
    classesFee: classes,
    paymentSplits: splits,
    startDate: start || undefined,
    endDate: expiry || undefined,
    duration: durationLabel || undefined,
  });
  await bustGymCache();
  return created;
}

export async function deleteMembership(id) {
  const gid = await gymId();
  const membershipId = String(id || '').trim();
  if (!membershipId) throw new Error('Membership not found.');

  const { data: membership, error: fetchError } = await supabase
    .from('memberships')
    .select('id, member_id, package_name')
    .eq('id', membershipId)
    .maybeSingle();
  if (fetchError) throw fetchError;

  // Remove linked new-subscription payments so Remaining Payments does not keep a deleted package
  if (membership?.member_id) {
    const pkgNorm = String(membership.package_name || '').trim().toLowerCase();
    const { data: payRows, error: payFetchError } = await supabase
      .from('payments')
      .select('id, package_name, source, payment_note, member_id')
      .eq('gym_id', gid)
      .eq('member_id', membership.member_id);
    if (payFetchError) throw payFetchError;

    const linkedIds = (payRows || [])
      .filter((pay) => {
        if (!isNewSubscriptionPaymentSource(pay.source)) return false;
        if (isRenewalPaymentRow(pay)) return false;
        const payPkg = String(pay.package_name || '').trim().toLowerCase();
        if (pkgNorm && payPkg && pkgNorm !== payPkg) return false;
        return true;
      })
      .map((pay) => pay.id)
      .filter(Boolean);

    for (const chunk of chunkIds(linkedIds)) {
      const { error: payDeleteError } = await supabase
        .from('payments')
        .delete()
        .eq('gym_id', gid)
        .in('id', chunk);
      if (payDeleteError) throw payDeleteError;
    }
  }

  const { error } = await deleteRowsQueued('memberships', { id: membershipId });
  if (error) throw error;
  await bustGymCache();
}

/** Next serial for a package: starts at packages.serial_no_start, then max existing + 1. */
/** Next unused public serial for this gym (walk-in keeps 1). */
async function nextAvailablePublicSerial(gymIdValue) {
  let maxSerial = 1;
  const consider = (raw) => {
    const n = Math.floor(Number(raw));
    if (Number.isFinite(n) && n > 1) maxSerial = Math.max(maxSerial, n);
  };
  try {
    const { data: membershipRows } = await supabase
      .from('memberships')
      .select('serial_no')
      .eq('gym_id', gymIdValue)
      .not('serial_no', 'is', null)
      .order('serial_no', { ascending: false })
      .limit(1);
    if (membershipRows?.[0]) consider(membershipRows[0].serial_no);
  } catch {
    /* serial_no column may be missing */
  }
  try {
    const { data: memberRows } = await supabase
      .from('members')
      .select('gym_member_id')
      .eq('gym_id', gymIdValue)
      .not('gym_member_id', 'is', null)
      .limit(5000);
    (memberRows || []).forEach((row) => {
      const raw = String(row.gym_member_id || '').trim();
      if (/^\d+$/.test(raw)) consider(raw);
    });
  } catch {
    /* ignore */
  }
  return maxSerial + 1;
}

async function nextMembershipSerialNo(gymIdValue, packageName) {
  const name = String(packageName || '').trim();
  if (!gymIdValue || !name) return null;

  const { data: pkgRows, error: pkgError } = await supabase
    .from('packages')
    .select('serial_no_start, name')
    .eq('gym_id', gymIdValue);

  if (pkgError) {
    if (/serial_no_start/i.test(pkgError.message || '')) return null;
    throw pkgError;
  }

  const packages = (pkgRows || [])
    .map((row) => ({
      name: String(row.name || '').trim(),
      start: Number(row.serial_no_start),
    }))
    .filter((row) => row.name && Number.isFinite(row.start) && row.start > 0)
    .map((row) => ({ ...row, start: Math.floor(row.start) }))
    .sort((a, b) => a.start - b.start);

  const matched = packages.find(
    (row) => row.name.toLowerCase() === name.toLowerCase(),
  );

  // Package has no serial series — still allot the next free public ID (never 1 / walk-in)
  if (!matched) {
    return nextAvailablePublicSerial(gymIdValue);
  }

  const start = matched.start;
  // Band ends where the next package series begins (Standard 1101…5999, Ladies 6001…)
  const nextBand = packages.find((row) => row.start > start);
  const rangeEnd = nextBand ? nextBand.start - 1 : Number.MAX_SAFE_INTEGER;

  let maxSerial = start - 1;

  const consider = (raw) => {
    const value = Number(raw);
    if (!Number.isFinite(value)) return;
    const n = Math.floor(value);
    if (n >= start && n <= rangeEnd) {
      maxSerial = Math.max(maxSerial, n);
    }
  };

  try {
    // Highest serial on memberships for this package name
    const safeName = name.replace(/[%_,]/g, '');
    const { data: byNameRows, error: byNameError } = await supabase
      .from('memberships')
      .select('serial_no, package_name')
      .eq('gym_id', gymIdValue)
      .ilike('package_name', safeName)
      .not('serial_no', 'is', null)
      .order('serial_no', { ascending: false })
      .limit(50);

    if (byNameError) {
      if (!/serial_no/i.test(byNameError.message || '')) throw byNameError;
    } else {
      (byNameRows || []).forEach((row) => {
        if (String(row.package_name || '').trim().toLowerCase() !== name.toLowerCase()) return;
        consider(row.serial_no);
      });
    }

    // Also scan this package's numeric band (covers renamed packages / drifted names)
    const { data: bandRows, error: bandError } = await supabase
      .from('memberships')
      .select('serial_no')
      .eq('gym_id', gymIdValue)
      .not('serial_no', 'is', null)
      .gte('serial_no', start)
      .lte('serial_no', rangeEnd === Number.MAX_SAFE_INTEGER ? start + 1000000 : rangeEnd)
      .order('serial_no', { ascending: false })
      .limit(1);

    if (bandError) {
      if (!/serial_no/i.test(bandError.message || '')) throw bandError;
    } else if (bandRows?.[0]) {
      consider(bandRows[0].serial_no);
    }

    // Public member IDs in the same band (avoids colliding with allotted gym_member_id)
    const { data: memberRows, error: memberError } = await supabase
      .from('members')
      .select('gym_member_id')
      .eq('gym_id', gymIdValue)
      .not('gym_member_id', 'is', null)
      .limit(5000);

    if (!memberError) {
      (memberRows || []).forEach((row) => {
        const raw = String(row.gym_member_id || '').trim();
        if (/^\d+$/.test(raw)) consider(raw);
      });
    }
  } catch (membershipError) {
    if (/serial_no/i.test(membershipError.message || '')) return start;
    throw membershipError;
  }

  return maxSerial + 1;
}

/** Public helper: next serial for a package (start series, then +1). */
export async function peekNextMembershipSerialNo(packageName, explicitGymId) {
  const gid = explicitGymId || await gymId();
  if (!gid || !packageName) return null;
  return nextMembershipSerialNo(gid, packageName);
}

/**
 * First package sale → next serial from the selected package's series (start, then +1).
 * Later sales for the same member → reuse their existing membership serial.
 */
async function resolveMembershipSerialNo(gid, memberId, packageName) {
  if (memberId) {
    const { data: existingMs, error } = await supabase
      .from('memberships')
      .select('serial_no')
      .eq('member_id', memberId)
      .not('serial_no', 'is', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!error && existingMs?.serial_no != null && existingMs.serial_no !== '') {
      const num = Number(existingMs.serial_no);
      if (Number.isFinite(num) && num > 0) return Math.floor(num);
    }
  }

  return nextMembershipSerialNo(gid, packageName);
}

export async function createMembership({
  memberId,
  packageName,
  expiryDate,
  startDate = todayDate(),
  status = 'active',
  datesTbc = false,
  skipCacheBust = false,
  duration = '',
  complimentary = false,
  autoRenewal = false,
  lifetime = false,
}) {
  const gid = await gymId();
  const durationLabel = String(duration || '').trim();
  const isComplimentary = Boolean(complimentary);
  const payload = {
      gym_id: gid,
      member_id: memberId,
      package_name: packageName,
      status,
      frozen: false,
    complimentary: isComplimentary,
    // NOT NULL on self-hosted DBs — never omit / null
    auto_renewal: Boolean(autoRenewal),
    lifetime: Boolean(lifetime),
    dates_tbc: Boolean(datesTbc),
  };
  if (durationLabel) {
    payload.duration = durationLabel;
  }

  if (datesTbc) {
    payload.start_date = null;
    payload.expiry_date = null;
  } else {
    // Blank start date → today
    payload.start_date = String(startDate || '').slice(0, 10) || todayDate();
    payload.expiry_date = expiryDate;
  }

  let serialNo = await resolveMembershipSerialNo(gid, memberId, packageName);
  if (serialNo == null) {
    serialNo = await nextAvailablePublicSerial(gid);
  }
  if (serialNo != null) {
    payload.serial_no = serialNo;
  }

  let { data, error } = await insertRowSkippingMissingColumns(
    'memberships',
    payload,
    '*',
    {
      preserveColumns: [
        'gym_id',
        'member_id',
        ...(serialNo != null ? ['serial_no'] : []),
        ...(durationLabel ? ['duration'] : []),
        ...(isComplimentary ? ['complimentary'] : []),
      ],
    },
  );
  if (error) {
    if (/expiry_date|null value/i.test(error.message || '') && datesTbc) {
      throw new Error(
        'TBC membership requires nullable start/expiry dates. Run supabase/fix_memberships_tbc_dates.sql in Supabase SQL Editor, then try again.',
      );
    }
    if (serialNo != null && /serial_no/i.test(error.message || '')) {
      delete payload.serial_no;
      ({ data, error } = await insertRowSkippingMissingColumns(
        'memberships',
        payload,
        '*',
        {
          preserveColumns: [
            'gym_id',
            'member_id',
            ...(durationLabel ? ['duration'] : []),
            ...(isComplimentary ? ['complimentary'] : []),
          ],
        },
      ));
    }
    if (error && durationLabel && /duration/i.test(error.message || '')) {
      throw new Error(
        'Memberships table is missing the duration column. Run supabase/fix_memberships_duration.sql in Supabase SQL Editor (or the SQL below), then try again.',
      );
    }
    if (error && isComplimentary && /complimentary/i.test(error.message || '')) {
      throw new Error(
        'Memberships table is missing the complimentary column. Run supabase/fix_memberships_complimentary.sql in Supabase SQL Editor, then try again.',
      );
    }
  if (error) throw error;
  }

  let row = data && !data.__insertedHidden ? data : null;

  // One quick re-fetch only when SELECT was hidden by RLS (no sleep loops)
  if (!row?.id && memberId) {
    const { data: found } = await supabase
      .from('memberships')
      .select('*')
      .eq('member_id', memberId)
      .eq('package_name', packageName)
      .order('created_at', { ascending: false })
      .limit(1);
    row = found?.[0] || null;
  }

  if (!row?.id) {
    throw new Error(
      'Membership was not saved to the database. Check Supabase memberships table / RLS, then try again.',
    );
  }

  // Parallel: fix gym_id / serial on row + allot public member serial + duration
  const patches = [];
  if (!row.gym_id || row.gym_id !== gid) {
    patches.push(
      supabase.from('memberships').update({ gym_id: gid }).eq('id', row.id).then(() => {
        row = { ...row, gym_id: gid };
      }),
    );
  }
  if (serialNo != null && Number(row.serial_no) !== Number(serialNo)) {
    patches.push(
      supabase
        .from('memberships')
        .update({ serial_no: serialNo })
        .eq('id', row.id)
        .then(({ error: serialError }) => {
          if (!serialError) row = { ...row, serial_no: serialNo };
          else if (!/serial_no/i.test(serialError.message || '')) {
            console.warn('Could not set membership serial_no', serialError.message);
          }
        }),
    );
  }
  if (durationLabel && String(row.duration || '').trim() !== durationLabel) {
    patches.push(
      supabase
        .from('memberships')
        .update({ duration: durationLabel })
        .eq('id', row.id)
        .then(({ error: durationError }) => {
          if (!durationError) row = { ...row, duration: durationLabel };
          else if (!/duration/i.test(durationError.message || '')) {
            console.warn('Could not set membership duration', durationError.message);
          }
        }),
    );
  }
  if (isComplimentary && !row.complimentary) {
    patches.push(
      supabase
        .from('memberships')
        .update({ complimentary: true })
        .eq('id', row.id)
        .then(({ error: complimentaryError }) => {
          if (!complimentaryError) row = { ...row, complimentary: true };
          else if (!/complimentary/i.test(complimentaryError.message || '')) {
            console.warn('Could not set membership complimentary', complimentaryError.message);
          }
        }),
    );
  }
  if (patches.length) await Promise.all(patches);

  if (serialNo != null) {
    let forceAllot = true;
    try {
      const { count } = await supabase
        .from('memberships')
        .select('id', { count: 'exact', head: true })
        .eq('member_id', memberId);
      forceAllot = (Number(count) || 0) <= 1;
    } catch {
      forceAllot = true;
    }
    let allotted = await allotMemberPublicSerial(memberId, serialNo, { force: forceAllot });
    if (allotted === false) {
      const fallback = await nextAvailablePublicSerial(gid);
      if (fallback && Number(fallback) !== Number(serialNo)) {
        await supabase.from('memberships').update({ serial_no: fallback }).eq('id', row.id);
        allotted = await allotMemberPublicSerial(memberId, fallback, { force: true });
        if (allotted) {
          row = { ...row, serial_no: fallback };
          serialNo = fallback;
        }
      }
    }
    const receptionistBranch = String(getReceptionistBranch() || '').trim();
    if (receptionistBranch) {
      try {
        const { data: memberRow } = await supabase
          .from('members')
          .select('branch')
          .eq('id', memberId)
          .maybeSingle();
        if (!String(memberRow?.branch || '').trim()) {
          await supabase
            .from('members')
            .update({ branch: receptionistBranch })
            .eq('id', memberId);
        }
      } catch {
        /* branch stamp is best-effort so the memberships list can see this row */
      }
    }
  }

  if (!skipCacheBust) await bustGymCache();
  return rowToMembership(row, {
    id: memberId,
    gym_member_id: serialNo != null ? String(serialNo) : undefined,
  });
}

export async function recordSubscriptionSale({
  customerName,
  packageName,
  expiryDate,
  startDate,
  datesTbc = false,
  payment,
  gymMemberId = '',
  memberId: knownMemberId = '',
  duration = '',
  complimentary = false,
  branch = '',
}) {
  const membershipStart = datesTbc
    ? null
    : (String(startDate || payment?.date || '').slice(0, 10) || todayDate());
  let memberId = knownMemberId || null;
  const durationLabel = String(duration || payment?.duration || '').trim();

  const {
    ensureWalkInMember,
    isWalkInGymMemberId,
    isWalkInMemberName,
    WALK_IN_PAYMENT_NAME,
  } = await import('./walkInMember');

  const looksLikeWalkIn = (
    !knownMemberId
    && (
      isWalkInMemberName(customerName)
      || isWalkInGymMemberId(gymMemberId)
    )
  );

  let resolvedSerial = String(gymMemberId || '').trim();

  if (looksLikeWalkIn) {
    const walkIn = await ensureWalkInMember();
    memberId = walkIn.id;
    resolvedSerial = String(walkIn.gymId || walkIn.gym_member_id || gymMemberId || '').trim() || resolvedSerial;
  } else if (customerName && customerName !== 'Walk-In Customer') {
    // Prefer the already-selected member id from the sale form — skip heavy lookups
    if (!memberId) {
      let member = null;
      if (gymMemberId) {
        member = await findMemberByGymMemberId(gymMemberId);
      }
      if (!member) {
        member = await findMemberByName(customerName);
      }
      if (!member) {
        throw new Error(
          `Member "${customerName}" was not found. Search again by Serial No and select them from the list.`,
        );
      }
      memberId = member.id;
      if (!resolvedSerial) {
        resolvedSerial = String(getGymMemberId(member) || member.gymId || '').trim();
      }
    }

    const gid = await gymId();
    const { data: existingRows, error: existingErr } = await supabase
      .from('memberships')
      .select('id, package_name, status, frozen, expiry_date, start_date, dates_tbc, gym_id')
      .eq('member_id', memberId);
    if (existingErr) throw existingErr;

    const hasUnexpired = (existingRows || []).some((row) => isMembershipUnexpired({
      status: row.status,
      expiry: row.expiry_date,
      start: row.start_date,
      datesTbc: row.dates_tbc,
      frozen: row.frozen,
    }));

    if (hasUnexpired) {
      throw new Error(
        `${customerName} already has an active subscription. Wait until it expires before selling a new package.`,
      );
    }

    const safeExpiry = datesTbc
      ? null
      : (expiryDate || addMonthsToEndDate(membershipStart, 1));

    const createdMembership = await createMembership({
      memberId,
      packageName,
      expiryDate: safeExpiry,
      startDate: membershipStart,
      datesTbc,
      skipCacheBust: true,
      duration: durationLabel,
      complimentary,
    });

    const membershipSerial = usableMemberSerial(
      createdMembership?.serialNo
      ?? createdMembership?.gymId
      ?? createdMembership?.serial_no,
    );
    if (membershipSerial) {
      resolvedSerial = membershipSerial;
    }

    // Non-critical follow-ups — don't block invoice / success toast
    void (async () => {
      try {
        const { data: memberRows } = await supabase
          .from('members')
          .select('profile')
          .eq('id', memberId)
          .limit(1);
        const memberRow = Array.isArray(memberRows) ? memberRows[0] : memberRows;
        await supabase
          .from('members')
          .update({ profile: { ...(memberRow?.profile || {}), package: packageName } })
          .eq('id', memberId);
        await supabase
          .from('memberships')
          .update({ gym_id: gid })
          .eq('member_id', memberId)
          .is('gym_id', null);
      } catch {
        /* ignore background cleanup errors */
      }
    })();
  }

  resolvedSerial = usableMemberSerial(resolvedSerial);

  // Complimentary: membership only — do not create a payments row
  if (complimentary || !payment) {
    void bustGymCache();
    return {
      complimentary: true,
      memberId,
      package: packageName,
      amount: 0,
      method: 'Complimentary',
      status: 'complimentary',
      gymId: resolvedSerial || '',
      serialNo: resolvedSerial || '',
    };
  }

  const paymentMemberName = looksLikeWalkIn
    ? WALK_IN_PAYMENT_NAME
    : (payment?.member || customerName);

  const saved = await createPayment({
    ...payment,
    memberId,
    member: paymentMemberName,
    startDate: datesTbc ? undefined : membershipStart,
    endDate: datesTbc ? undefined : (expiryDate || payment?.endDate),
    registrationFee: Number(payment?.registrationFee) || 0,
    skipCacheBust: true,
    branch: payment?.branch || branch,
  });
  void bustGymCache();
  // createPayment maps without member → gymId is often "—"; never let that
  // wipe the serial allotted during createMembership (thermal slip ID).
  const paymentGymId = usableMemberSerial(saved?.gymId)
    || resolvedSerial
    || '';
  return {
    ...saved,
    gymId: paymentGymId,
    serialNo: paymentGymId,
  };
}

// ─── Freezes ───────────────────────────────────────────────

async function findMembershipIdForMember(memberId) {
  const gid = await gymId();
  let { data } = await supabase
    .from('memberships')
    .select('id')
    .eq('gym_id', gid)
    .eq('member_id', memberId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!data?.id) {
    ({ data } = await supabase
      .from('memberships')
      .select('id')
      .eq('member_id', memberId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle());
  }

  return data?.id || null;
}

async function setMemberAccountStatus(memberId, status) {
  const { data: memberRow } = await supabase
    .from('members')
    .select('profile, status')
    .eq('id', memberId)
    .maybeSingle();

  const profile = { ...(memberRow?.profile || {}), status };
  const row = { profile };
  if (memberRow && 'status' in memberRow) row.status = status;

  await supabase.from('members').update(row).eq('id', memberId);
}

async function applyFreezeToMember(memberId) {
  const membershipId = await findMembershipIdForMember(memberId);
  if (membershipId) {
    await updateMembership(membershipId, { frozen: true, status: 'inactive' });
  }
  await setMemberAccountStatus(memberId, 'inactive');
}

async function clearFreezeFromMember(memberId) {
  const gid = await gymId();
  const { data: freezes } = await supabase
    .from('membership_freezes')
    .select('id, status')
    .eq('gym_id', gid)
    .eq('member_id', memberId)
    .eq('status', 'active');

  if ((freezes || []).length > 0) return;

  const membershipId = await findMembershipIdForMember(memberId);
  if (membershipId) {
    await updateMembership(membershipId, { frozen: false, status: 'active' });
  }
  await setMemberAccountStatus(memberId, 'active');
}

export async function fetchFreezes() {
  const gid = await gymId();
  return cachedQuery(cacheKey(`gym:${gid}:freezes`), async () => {

  let { data, error } = await supabase
    .from('membership_freezes')
    .select('*, members(full_name, gym_member_id, branch)')
    .eq('gym_id', gid)
    .order('created_at', { ascending: false });

  if (error && (/branch/i.test(error.message || '') || isEmbedError(error.message || ''))) {
    ({ data, error } = await supabase
      .from('membership_freezes')
      .select('*, members(full_name, gym_member_id)')
      .eq('gym_id', gid)
      .order('created_at', { ascending: false }));
  }
  if (error) throw error;

  const members = await fetchMembers().catch(() => []);
  const memberById = new Map(members.map((m) => [m.id, m]));

  const mapped = dedupeFreezes((data || []).map((row) => {
    const fullMember = memberById.get(row.member_id);
    const embedded = row.members || {};
    return {
      ...rowToFreeze(row, {
        full_name: fullMember?.name || embedded.full_name,
        gym_member_id: fullMember?.baseGymId || fullMember?.gymId || embedded.gym_member_id,
        baseGymId: fullMember?.baseGymId || embedded.gym_member_id,
        serialNo: fullMember?.serialNo ?? fullMember?.membershipSerialNo,
        membershipSerialNo: fullMember?.membershipSerialNo ?? fullMember?.serialNo,
      }),
      branch: fullMember?.branch || embedded.branch || '',
    };
  }));
  return filterByReceptionistBranch(mapped, (f) => f.branch);
  });
}

/** Paged freezes for UI. Prefer over fetchFreezes(). */
export async function fetchFreezesPage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
} = {}) {
  const gid = await gymId();
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.min(100, Math.max(10, Number(pageSize) || 50));
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;
  let branchFilter = String(branch || getReceptionistBranchFilter() || '').trim();
  // Resolve branch from profile when session has not synced yet (avoids permanent empty freeze list).
  if (isReceptionistPanelScope() && (!branchFilter || branchFilter === 'all')) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* keep going — may show gym-wide freezes until branch is known */
    }
  }

  let query = supabase
    .from('membership_freezes')
    .select('*, members!inner(full_name, gym_member_id, branch)', { count: 'exact' })
    .eq('gym_id', gid);

  if (branchFilter && branchFilter !== 'all') {
    query = query.eq('members.branch', branchFilter);
  }

  const freezeSerial = activeColumnFilter(filters.gymId || filters.serialNo);
  const freezeMember = activeColumnFilter(filters.member);
  if (freezeSerial || freezeMember) {
    const memberIds = await findMemberIdsForTableFilters(gid, {
      serialNeedle: freezeSerial,
      nameNeedle: freezeMember,
      branchFilter,
    });
    if (!memberIds?.length) {
      return { rows: [], total: 0, page: safePage, pageSize: safeSize };
    }
    query = query.in('member_id', memberIds);
  }
  if (activeColumnFilter(filters.startDate)) {
    query = applyDateColumnEq(query, 'start_date', filters.startDate);
  }
  if (activeColumnFilter(filters.endDate)) {
    query = applyDateColumnEq(query, 'end_date', filters.endDate);
  }
  if (activeColumnFilter(filters.reason)) {
    query = applyIlikeColumn(query, 'reason', filters.reason);
  }
  const autoExtend = activeColumnFilter(filters.autoExtend);
  if (/^yes$/i.test(autoExtend)) query = query.eq('auto_extend', true);
  if (/^no$/i.test(autoExtend)) query = query.eq('auto_extend', false);
  const freezeStatus = activeColumnFilter(filters.status);
  if (/inactive/i.test(freezeStatus)) query = query.eq('status', 'active');
  else if (/^active$/i.test(freezeStatus)) query = query.neq('status', 'active');

  const needle = String(search || '').trim();
  if (needle) {
    const safe = needle.replace(/[%_,.()]/g, ' ').trim();
    if (safe) {
      let memberQuery = supabase
        .from('members')
        .select('id')
        .eq('gym_id', gid)
        .or(memberNameAndSerialSearchOr(safe))
        .limit(300);
      if (branchFilter && branchFilter !== 'all') {
        memberQuery = applyMemberBranchOrFilter(memberQuery, branchFilter);
      }
      const { data: hits } = await memberQuery;
      const ids = (hits || []).map((m) => m.id).filter(Boolean);
      const orParts = [`reason.ilike.%${safe}%`];
      if (ids.length) orParts.push(`member_id.in.(${ids.join(',')})`);
      query = query.or(orParts.join(','));
    }
  }

  const { data, error, count } = await query
    .order('created_at', { ascending: false })
    .order('id', { ascending: true })
    .range(from, to);
  if (error) throw error;

  const rows = (data || []).map((row) => {
    const embedded = row.members || {};
    return {
      ...rowToFreeze(row, {
        full_name: embedded.full_name,
        gym_member_id: embedded.gym_member_id,
        baseGymId: embedded.gym_member_id,
      }),
      branch: embedded.branch || '',
    };
  });

  return {
    rows: dedupeFreezes(rows),
    total: Number(count) || 0,
    page: safePage,
    pageSize: safeSize,
  };
}

export async function createFreeze(form, memberId) {
  const gid = await gymId();

  // One active freeze per member — close any previous so retries don't multiply rows
  await supabase
    .from('membership_freezes')
    .update({ status: 'inactive' })
    .eq('gym_id', gid)
    .eq('member_id', memberId)
    .eq('status', 'active');

  const { data, error } = await supabase
    .from('membership_freezes')
    .insert(stampCreatedAt({
      gym_id: gid,
      member_id: memberId,
      start_date: form.startDate,
      end_date: form.endDate,
      reason: form.reason,
      auto_extend: form.autoExtend,
      status: 'active',
    }))
    .select('*, members(full_name, gym_member_id)')
    .single();
  if (error) throw error;

  try {
  await applyFreezeToMember(memberId);
  } catch (err) {
    console.warn('Freeze saved but could not mark membership inactive', err);
  }
  await bustGymCache();
  return rowToFreeze(data, data.members);
}

export async function updateFreeze(id, form, memberId) {
  const { data, error } = await supabase
    .from('membership_freezes')
    .update({
      member_id: memberId,
      start_date: form.startDate,
      end_date: form.endDate,
      reason: form.reason,
      auto_extend: form.autoExtend,
    })
    .eq('id', id)
    .select('*, members(full_name, gym_member_id)')
    .single();
  if (error) throw error;
  await applyFreezeToMember(memberId);
  await bustGymCache();
  return rowToFreeze(data, data.members);
}

export async function deleteFreeze(id) {
  const { data: freeze } = await supabase
    .from('membership_freezes')
    .select('member_id')
    .eq('id', id)
    .maybeSingle();

  const { error } = await deleteRowsQueued('membership_freezes', { id });
  if (error) throw error;

  if (freeze?.member_id) {
    await clearFreezeFromMember(freeze.member_id);
  }
  await bustGymCache();
}

// ─── Attendance ────────────────────────────────────────────

/** Paged attendance for UI. Prefer over fetchAttendance(). */
export async function fetchAttendancePage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  date = '',
  dateFrom = '',
  dateTo = '',
  filters = {},
} = {}) {
  const gid = await gymId();
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.min(100, Math.max(10, Number(pageSize) || 50));
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;
  const branchFilter = String(branch || getReceptionistBranchFilter() || '').trim();
  if (isReceptionistPanelScope() && !branchFilter) {
    return { rows: [], total: 0, page: safePage, pageSize: safeSize };
  }

  const selectWithBranch = '*, members(full_name, gym_member_id, branch), trainers(full_name, branch)';
  let query = supabase
    .from('attendance_logs')
    .select(selectWithBranch, { count: 'exact' })
    .eq('gym_id', gid);

  const day = String(date || '').slice(0, 10);
  const fromDay = String(dateFrom || '').trim().slice(0, 10);
  const toDay = String(dateTo || '').trim().slice(0, 10);
  if (day) {
    query = query.or(`attendance_date.eq.${day},date.eq.${day}`);
  } else {
    if (fromDay && /^\d{4}-\d{2}-\d{2}$/.test(fromDay)) {
      query = query.gte('attendance_date', fromDay);
    }
    if (toDay && /^\d{4}-\d{2}-\d{2}$/.test(toDay)) {
      query = query.lte('attendance_date', toDay);
    }
  }

  const typeFilter = activeColumnFilter(filters.type);
  if (typeFilter) {
    query = query.eq('person_type', /^trainer$/i.test(typeFilter) ? 'trainer' : 'member');
  }
  const attendanceSerial = activeColumnFilter(filters.gymId || filters.serialNo);
  const attendanceName = activeColumnFilter(filters.name || filters.member);
  if (attendanceSerial || attendanceName) {
    const memberIds = await findMemberIdsForTableFilters(gid, {
      serialNeedle: attendanceSerial,
      nameNeedle: attendanceName,
      branchFilter,
    });
    if (attendanceSerial) {
      if (!memberIds?.length) {
        return { rows: [], total: 0, page: safePage, pageSize: safeSize };
      }
      query = query.in('member_id', memberIds);
    } else {
      const { data: trainerHits } = await supabase
        .from('trainers')
        .select('id')
        .eq('gym_id', gid)
        .ilike('full_name', `%${sanitizeIlike(attendanceName)}%`)
        .limit(200);
      const trainerIds = (trainerHits || []).map((row) => row.id).filter(Boolean);
      const orParts = [];
      if (memberIds?.length) orParts.push(`member_id.in.(${memberIds.join(',')})`);
      if (trainerIds.length) orParts.push(`trainer_id.in.(${trainerIds.join(',')})`);
      if (!orParts.length) {
        return { rows: [], total: 0, page: safePage, pageSize: safeSize };
      }
      query = query.or(orParts.join(','));
    }
  }
  const attendanceDate = activeColumnFilter(filters.date);
  if (attendanceDate) {
    const iso = normalizeDateFilterToIso(attendanceDate);
    if (iso) query = query.eq('attendance_date', iso);
  }
  if (activeColumnFilter(filters.branch)) {
    query = applyIlikeColumn(query, 'branch', filters.branch);
  }
  if (activeColumnFilter(filters.checkIn)) {
    query = applyIlikeColumn(query, 'check_in', filters.checkIn);
  }
  if (activeColumnFilter(filters.checkOut)) {
    query = applyIlikeColumn(query, 'check_out', filters.checkOut);
  }
  if (activeColumnFilter(filters.duration)) {
    query = applyIlikeColumn(query, 'duration', filters.duration);
  }
  const attendanceStatus = activeColumnFilter(filters.status);
  if (attendanceStatus) {
    if (/in\s*gym/i.test(attendanceStatus)) query = query.eq('status', 'in-gym');
    else if (/completed/i.test(attendanceStatus)) query = query.eq('status', 'completed');
    else query = applyIlikeColumn(query, 'status', attendanceStatus);
  }

  const needle = String(search || '').trim();
  if (needle) {
    const safe = needle.replace(/[%_,.()]/g, ' ').trim();
    if (safe) {
      const [{ data: memberHits }, { data: trainerHits }] = await Promise.all([
        supabase
          .from('members')
          .select('id')
          .eq('gym_id', gid)
          .or(memberNameAndSerialSearchOr(safe))
          .limit(200),
        supabase
          .from('trainers')
          .select('id')
          .eq('gym_id', gid)
          .ilike('full_name', `%${safe}%`)
          .limit(100),
      ]);
      const memberIds = (memberHits || []).map((m) => m.id).filter(Boolean);
      const trainerIds = (trainerHits || []).map((t) => t.id).filter(Boolean);
      const orParts = [];
      if (memberIds.length) orParts.push(`member_id.in.(${memberIds.join(',')})`);
      if (trainerIds.length) orParts.push(`trainer_id.in.(${trainerIds.join(',')})`);
      if (orParts.length) query = query.or(orParts.join(','));
      else return { rows: [], total: 0, page: safePage, pageSize: safeSize };
    }
  }

  let { data, error, count } = await query
    .order('attendance_date', { ascending: false })
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error && (/branch/i.test(error.message || '') || isEmbedError(error.message || ''))) {
    ({ data, error, count } = await supabase
      .from('attendance_logs')
      .select('*, members(full_name, gym_member_id), trainers(full_name)', { count: 'exact' })
      .eq('gym_id', gid)
      .order('created_at', { ascending: false })
      .range(from, to));
  }
  if (error) throw error;

  let rows = (data || []).map((row) => {
    const member = row.members
      ? {
          ...row.members,
          baseGymId: row.members.gym_member_id,
        }
      : null;
    const trainer = row.trainers || null;
    return rowToAttendance(row, member, trainer);
  });

  if (isReceptionistPanelScope() || (branchFilter && branchFilter !== 'all')) {
    rows = rows.filter((r) => branchesMatch(r.branch === '—' ? '' : r.branch, branchFilter));
  }

  // Serial ascending (numeric gym member id) for stable list order
  rows = [...rows].sort((a, b) => {
    const as = Number(String(a.gymId || a.serialNo || '').replace(/\D/g, '')) || Number.POSITIVE_INFINITY;
    const bs = Number(String(b.gymId || b.serialNo || '').replace(/\D/g, '')) || Number.POSITIVE_INFINITY;
    if (as !== bs) return as - bs;
    const byDate = String(b.date || b.attendanceDate || '').localeCompare(String(a.date || a.attendanceDate || ''));
    if (byDate !== 0) return byDate;
    return String(a.id || '').localeCompare(String(b.id || ''));
  });

  return {
    rows: dedupeAttendance(rows),
    total: Number(count) || rows.length,
    page: safePage,
    pageSize: safeSize,
  };
}

export async function fetchAttendance() {
  const gid = await gymId();
  return cachedQuery(cacheKey(`gym:${gid}:attendance`), async () => {

  const select = '*, members(full_name, gym_member_id, branch), trainers(full_name, branch)';
  let { data, error } = await supabase
    .from('attendance_logs')
    .select(select)
    .eq('gym_id', gid)
    .order('attendance_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error && (/branch/i.test(error.message) || isEmbedError(error.message))) {
    ({ data, error } = await supabase
      .from('attendance_logs')
      .select('*, members(full_name, gym_member_id), trainers(full_name)')
      .eq('gym_id', gid)
      .order('attendance_date', { ascending: false })
      .order('created_at', { ascending: false }));
  }

  if (error && (/attendance_date/i.test(error.message) || isEmbedError(error.message))) {
    ({ data, error } = await supabase
      .from('attendance_logs')
      .select('*')
      .eq('gym_id', gid)
      .order('created_at', { ascending: false }));
  }

  if (!error && !data?.length) {
    data = [];
  }

  if (error) throw error;

  const [members, trainers] = await Promise.all([
    fetchMembers(),
    fetchTrainers(),
  ]);
  const memberById = new Map(members.map((m) => [m.id, m]));
  const trainerById = new Map(trainers.map((t) => [t.id, t]));

  const rows = (data || []).slice().sort((a, b) => {
    const dateCompare = attendanceDateFromRow(b).localeCompare(attendanceDateFromRow(a));
    if (dateCompare !== 0) return dateCompare;
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });

  const mapped = dedupeAttendance(rows.map((row) => {
    const fullMember = memberById.get(row.member_id);
    const fullTrainer = trainerById.get(row.trainer_id);
    const member = row.members
      ? {
          ...row.members,
          branch: row.members.branch || fullMember?.branch || '',
          gym_member_id: row.members.gym_member_id || fullMember?.baseGymId || fullMember?.gymId || '',
          baseGymId: fullMember?.baseGymId || row.members.gym_member_id,
          serialNo: fullMember?.serialNo ?? fullMember?.membershipSerialNo,
          membershipSerialNo: fullMember?.membershipSerialNo ?? fullMember?.serialNo,
        }
      : memberEmbed(fullMember);
    const trainer = row.trainers
      ? {
          ...row.trainers,
          branch: row.trainers.branch || fullTrainer?.branch || '',
        }
      : (fullTrainer
        ? { full_name: fullTrainer.name, branch: fullTrainer.branch || '' }
      : null);
    return rowToAttendance(row, member, trainer);
  }));
  return filterByReceptionistBranch(mapped, (r) => (r.branch === '—' ? '' : r.branch));
  });
}

export async function createAttendance({ personType, memberId, trainerId, checkIn, status }) {
  const gid = await gymId();
  const today = todayDate();
  const row = {
    gym_id: gid,
    person_type: personType,
    member_id: memberId || null,
    trainer_id: trainerId || null,
    attendance_date: today,
    date: today,
    check_in: checkIn,
    check_out: null,
    status: status || 'in-gym',
  };

  const { data, error } = await insertRowSkippingMissingColumns(
    'attendance_logs',
    row,
    '*, members(full_name, gym_member_id), trainers(full_name)',
  );
  if (error) throw error;
  await bustGymCache();
  return rowToAttendance(data, data.members, data.trainers);
}

export async function checkoutAttendance(id, checkOut) {
  const { data, error } = await supabase
    .from('attendance_logs')
    .update({ check_out: checkOut, status: 'completed' })
    .eq('id', id)
    .select('*, members(full_name, gym_member_id), trainers(full_name)')
    .single();
  if (error) throw error;
  await bustGymCache();
  return rowToAttendance(data, data.members, data.trainers);
}

export async function findOpenAttendance(memberId, trainerId, personType) {
  const gid = await gymId();
  const today = todayDate();

  const buildQuery = () => {
    let q = supabase
      .from('attendance_logs')
      .select('id, attendance_date, date, created_at, status, person_type, member_id, trainer_id')
      .eq('gym_id', gid)
      .eq('status', 'in-gym')
      .eq('person_type', personType);

    if (personType === 'member') q = q.eq('member_id', memberId);
    else q = q.eq('trainer_id', trainerId);
    return q;
  };

  let { data, error } = await buildQuery().eq('attendance_date', today);

  if (error && /attendance_date/i.test(error.message)) {
    ({ data, error } = await buildQuery());
  }

  if (error) throw error;

  const match = (data || []).find((row) => attendanceDateFromRow(row) === today);
  return match || null;
}

// ─── Payments ──────────────────────────────────────────────

/** Remaining Clear installment rows for sale-module history panels. */
export async function fetchRemainingClearHistory({
  startDate = '',
  endDate = '',
} = {}) {
  const gid = await gymId();
  let branchFilter = String(getReceptionistBranchFilter() || '').trim();
  if (isReceptionistPanelScope() && (!branchFilter || branchFilter === 'all')) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* optional */
    }
  }

  let query = supabase
    .from('payments')
    .select('*, members(full_name, gym_member_id, branch)')
    .eq('gym_id', gid)
    .eq('source', 'remaining_clear')
    .neq('status', 'voided')
    .order('paid_at', { ascending: false })
    .limit(300);

  if (startDate) query = query.gte('paid_at', startDate);
  if (endDate) query = query.lte('paid_at', `${endDate}T23:59:59`);

  const { data, error } = await query;
  if (error) throw error;

  let rows = (data || []).map((row) => rowToPayment(row, row.members || null));

  if (isReceptionistPanelScope() || (branchFilter && branchFilter !== 'all')) {
    const withMember = filterByReceptionistBranch(
      rows.filter((p) => p.memberId),
      (p) => p.branch,
    );
    const walkIns = rows.filter((p) => !p.memberId);
    rows = [...withMember, ...walkIns];
  }

  return rows.sort((a, b) => String(b.date || b.paidAt || '').localeCompare(String(a.date || a.paidAt || '')));
}

/** Paged payments for UI. Prefer over fetchPayments(). */
export async function fetchPaymentsPage({
  page = 1,
  pageSize = 50,
  search = '',
  source = '',
  branch = '',
  pendingOnly = false,
  /** Payments list: only rows with money collected (hide pay-later amount = 0). */
  collectedOnly = false,
  dateFrom = '',
  dateTo = '',
  filters = {},
  /** Allow larger pages for print/export of a filtered date range. */
  forExport = false,
} = {}) {
  const gid = await gymId();
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(500, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;
  let branchFilter = String(branch || getReceptionistBranchFilter() || '').trim();
  // Resolve branch from profile when session has not synced yet (avoids permanent empty history).
  if (isReceptionistPanelScope() && (!branchFilter || branchFilter === 'all')) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* keep going — may show gym-wide rows until branch is known */
    }
  }

  let query = supabase
    .from('payments')
    .select('*, members(full_name, gym_member_id, branch)', { count: 'exact' })
    .eq('gym_id', gid)
    // Soft-voided sales keep their CR No. but stay out of active lists/reports
    .neq('status', 'voided');

  const sourceFilter = String(source || '').trim();
  if (sourceFilter) {
    query = query.eq('source', sourceFilter);
  }

  if (pendingOnly) {
    query = query.eq('status', 'pending');
  }

  // Hide unpaid / pay-later sales from the Payments page (amount must be > 0)
  if (collectedOnly) {
    query = query.gt('amount', 0);
  }

  const fromDay = String(dateFrom || '').trim().slice(0, 10);
  const toDay = String(dateTo || '').trim().slice(0, 10);
  if (fromDay && /^\d{4}-\d{2}-\d{2}$/.test(fromDay)) {
    query = query.gte('paid_at', `${fromDay}T00:00:00`);
  }
  if (toDay && /^\d{4}-\d{2}-\d{2}$/.test(toDay)) {
    query = query.lte('paid_at', `${toDay}T23:59:59.999`);
  }

  const columnFilterResult = await applyPaymentColumnFilters(query, filters, gid, branchFilter);
  if (columnFilterResult.empty) {
    return { rows: [], total: 0, page: safePage, pageSize: safeSize };
  }
  query = columnFilterResult.query;

  const needle = String(search || '').trim();
  if (needle) {
    const safe = needle.replace(/[%_,.()]/g, ' ').trim();
    if (safe) {
      let memberQuery = supabase
        .from('members')
        .select('id')
        .eq('gym_id', gid)
        .or(memberNameAndSerialSearchOr(safe, [`phone.ilike.%${safe}%`]))
        .limit(300);
      if (isReceptionistPanelScope() || (branchFilter && branchFilter !== 'all')) {
        memberQuery = applyMemberBranchOrFilter(memberQuery, branchFilter);
      }
      const { data: hits } = await memberQuery;
      const ids = (hits || []).map((m) => m.id).filter(Boolean);
      const orParts = [
        `payment_ref.ilike.%${safe}%`,
        `member_name.ilike.%${safe}%`,
        `package_name.ilike.%${safe}%`,
        `invoice_id.ilike.%${safe}%`,
      ];
      if (ids.length) orParts.push(`member_id.in.(${ids.join(',')})`);
      query = query.or(orParts.join(','));
    }
  }

  const hasPaymentDateRange = (
    (fromDay && /^\d{4}-\d{2}-\d{2}$/.test(fromDay))
    || (toDay && /^\d{4}-\d{2}-\d{2}$/.test(toDay))
  );
  let { data, error, count } = await query
    .order('paid_at', { ascending: hasPaymentDateRange })
    .order('id', { ascending: true })
    .range(from, to);

  if (error && (/branch/i.test(error.message || '') || isEmbedError(error.message || ''))) {
    // Rebuild the same filters — never drop source/date (that caused empty/wrong history).
    let fallback = supabase
      .from('payments')
      .select('*, members(full_name, gym_member_id)', { count: 'exact' })
      .eq('gym_id', gid);
    if (sourceFilter) fallback = fallback.eq('source', sourceFilter);
    if (pendingOnly) fallback = fallback.eq('status', 'pending');
    if (collectedOnly) fallback = fallback.gt('amount', 0);
    if (fromDay && /^\d{4}-\d{2}-\d{2}$/.test(fromDay)) {
      fallback = fallback.gte('paid_at', `${fromDay}T00:00:00`);
    }
    if (toDay && /^\d{4}-\d{2}-\d{2}$/.test(toDay)) {
      fallback = fallback.lte('paid_at', `${toDay}T23:59:59.999`);
    }
    ({ data, error, count } = await fallback
      .order('paid_at', { ascending: hasPaymentDateRange })
      .order('id', { ascending: true })
      .range(from, to));
  }
  if (error) throw error;

  let rows = (data || []).map((row) => {
    const embedded = row.members
      ? {
          ...row.members,
          baseGymId: row.members.gym_member_id,
        }
      : null;
    const payment = rowToPayment(row, embedded);
    return {
      ...payment,
      branch: embedded?.branch || row.branch_name || row.branch || '',
    };
  });

  // Prefer the resolved branchFilter (param / session / profile) — do NOT call
  // filterByReceptionistBranch here: that re-reads session and returns [] when
  // branch has not been written to sessionStorage yet (Guest Pass stuck at 0).
  if (branchFilter && branchFilter !== 'all') {
    rows = rows.filter((p) => {
      const rowBranch = String(p.branch || '').trim();
      if (!normalizeBranchName(rowBranch)) return true;
      return branchesMatch(rowBranch, branchFilter);
    });
  }

  return {
    rows,
    total: Number(count) || 0,
    page: safePage,
    pageSize: safeSize,
  };
}

function resolvePaymentsListPageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapPaymentsListRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
    trainer: members.trainer,
  });
  const left = Number(row.pending_amount);
  return {
    ...payment,
    date: row.sale_day || payment.date || row.created_at,
    trainer: members.trainer || row.trainer || payment.trainer || '',
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
    pendingAmount: Number.isFinite(left) ? left : saleRowLeft(payment),
  };
}

function inPaymentsListDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || row.createdAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * Payments page (all sources, collected only). RPC first.
 * Receptionist: PK today + branch. Fallback: fetchPaymentsPage(collectedOnly).
 */
export async function fetchPaymentsListPage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  dateFrom = '',
  dateTo = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolvePaymentsListPageDates({
    ...filters,
    dateFrom: dateFrom || filters.dateFrom || filters.rangeStart,
    dateTo: dateTo || filters.dateTo || filters.rangeEnd,
    rangeStart: dateFrom || filters.rangeStart || filters.dateFrom,
    rangeEnd: dateTo || filters.rangeEnd || filters.dateTo,
  }, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchPaymentsListPageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
      receptionistScope: receptionist,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapPaymentsListRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Payments list RPC failed, using fallback', err?.message || err);
  }

  const fromDay = String(scopedFilters.rangeStart || scopedFilters.dateFrom || '').slice(0, 10);
  const toDay = String(scopedFilters.rangeEnd || scopedFilters.dateTo || '').slice(0, 10);
  const fallback = await fetchPaymentsPage({
    page: safePage,
    pageSize: safeSize,
    search,
    branch: branchFilter,
    collectedOnly: true,
    dateFrom: fromDay,
    dateTo: toDay,
    filters: scopedFilters,
    forExport,
  });
  let rows = fallback.rows || [];
  if (receptionist) {
    rows = rows.filter((row) => inPaymentsListDateWindow(row, fromDay, toDay));
  }
  return {
    rows,
    total: receptionist ? rows.length : (Number(fallback.total) || rows.length),
    collected: rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
    page: fallback.page || safePage,
    pageSize: fallback.pageSize || safeSize,
    fromRpc: false,
  };
}

export async function fetchPayments() {
  const gid = await gymId();
  return cachedQuery(cacheKey(`gym:${gid}:payments:v3`), async () => {
    // Use embedded member fields only — never scan the full members table here
    let data = [];
    try {
      data = await fetchAllPaged(() => (
        supabase
          .from('payments')
          .select('*, members(full_name, gym_member_id, branch, trainer)')
          .eq('gym_id', gid)
          .neq('status', 'voided')
          .order('paid_at', { ascending: false })
          .order('id', { ascending: true })
      ));
    } catch (error) {
      if (/branch|trainer/i.test(error.message || '') || isEmbedError(error.message || '')) {
        data = await fetchAllPaged(() => (
          supabase
    .from('payments')
    .select('*, members(full_name, gym_member_id)')
    .eq('gym_id', gid)
            .neq('status', 'voided')
            .order('paid_at', { ascending: false })
            .order('id', { ascending: true })
        ));
      } else if (isEmbedError(error.message || '')) {
        data = await fetchAllPaged(() => (
          supabase
            .from('payments')
            .select('*')
            .eq('gym_id', gid)
            .neq('status', 'voided')
            .order('paid_at', { ascending: false })
            .order('id', { ascending: true })
        ));
      } else {
        throw error;
      }
    }

    const branch = getReceptionistBranchFilter();
    const mapped = (data || [])
      .filter((row) => String(row.status || '').toLowerCase() !== 'voided')
      .map((row) => {
      const embedded = row.members
        ? {
            ...row.members,
            baseGymId: row.members.gym_member_id,
          }
        : null;
      const payment = rowToPayment(row, embedded);
      return {
        ...payment,
        trainer: embedded?.trainer || row.trainer || '',
        branch: embedded?.branch || row.branch_name || row.branch || '',
      };
    });

    if (!isReceptionistPanelScope()) return mapped;
    // Same rules as members list: blank branch stays visible for this gym
    const withMember = filterByReceptionistBranch(
      mapped.filter((p) => p.memberId),
      (p) => p.branch,
    );
    const walkIns = mapped.filter((p) => !p.memberId);
    return [...withMember, ...walkIns];
  });
}

/**
 * Fresh payments for one calendar day (Z Report / Detailed Report).
 * Uses a ±1 day paid_at window then filters by local calendar day.
 * New memberships are kept on start_date (Memberships list), not paid_at alone.
 */
export async function fetchPaymentsForDate(date) {
  const day = String(date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return fetchPayments();

  const gid = await gymId();
  const shiftDay = (iso, delta) => {
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() + delta);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const fromDay = shiftDay(day, -1);
  const toDay = shiftDay(day, 1);

  const toLocalDay = (raw) => {
    const text = String(raw || '').trim();
    if (!text) return '';
    // Prefer calendar date prefix (matches Memberships table / DATE columns)
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const d = String(parsed.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return '';
  };

  const matchesDay = (payment) => {
    // Prefer collection day (paid_at) so Petty Cash / Z cash match the selected report date
    const primary = toLocalDay(payment.paidAt || payment.paid_at || payment.date);
    const source = String(payment.source || '').trim().toLowerCase().replace(/-/g, '_');
    // New memberships: keep on start_date (NEW MEMBERSHIP list) AND on paid_at
    // (CASH SALE / Petty Cash). Previously start_date-only dropped cash collected
    // on a different calendar day (e.g. paid 30-07, start 31-07).
    const membershipLike = source === 'subscription'
      || source === 'membership'
      || source === 'new_membership'
      || source === 'subscriptions';
    if (membershipLike) {
      const startRaw = String(payment.startDate || payment.start_date || payment.start || '').trim();
      const start = /^\d{4}-\d{2}-\d{2}/.test(startRaw)
        ? startRaw.slice(0, 10)
        : toLocalDay(startRaw);
      if (start === day) return true;
      return primary === day;
    }
    return primary === day;
  };

  let data = [];
  const selectWithTrainer = '*, members(full_name, gym_member_id, branch, trainer)';
  const selectWithMember = '*, members(full_name, gym_member_id)';
  // payments.start_date does not exist on this schema — filter by paid_at window only.
  // New memberships for the report day come from fetchMembershipSalesForDate (memberships.start_date).

  try {
    data = await fetchAllPaged(() => (
      supabase
        .from('payments')
        .select(selectWithTrainer)
        .eq('gym_id', gid)
        .neq('status', 'voided')
        .gte('paid_at', `${fromDay}T00:00:00`)
        .lte('paid_at', `${toDay}T23:59:59.999`)
        .order('paid_at', { ascending: false })
        .order('id', { ascending: true })
    ));
  } catch (error) {
    if (/branch|trainer/i.test(error.message || '') || isEmbedError(error.message || '')) {
      try {
        data = await fetchAllPaged(() => (
          supabase
            .from('payments')
            .select(selectWithMember)
            .eq('gym_id', gid)
            .neq('status', 'voided')
            .gte('paid_at', `${fromDay}T00:00:00`)
            .lte('paid_at', `${toDay}T23:59:59.999`)
            .order('paid_at', { ascending: false })
            .order('id', { ascending: true })
        ));
      } catch {
        data = await fetchAllPaged(() => (
          supabase
            .from('payments')
            .select('*')
            .eq('gym_id', gid)
            .neq('status', 'voided')
            .gte('paid_at', `${fromDay}T00:00:00`)
            .lte('paid_at', `${toDay}T23:59:59.999`)
            .order('paid_at', { ascending: false })
            .order('id', { ascending: true })
        ));
      }
    } else if (/date\/time|invalid input|paid_at/i.test(error.message || '')) {
      // DATE-typed paid_at: fall back to bare calendar bounds
      data = await fetchAllPaged(() => (
        supabase
          .from('payments')
          .select(selectWithMember)
          .eq('gym_id', gid)
          .neq('status', 'voided')
          .gte('paid_at', fromDay)
          .lte('paid_at', toDay)
          .order('paid_at', { ascending: false })
          .order('id', { ascending: true })
      ));
    } else {
      throw error;
    }
  }

  const mapRow = (row) => {
    const embedded = row.members
      ? {
          ...row.members,
          baseGymId: row.members.gym_member_id,
        }
      : null;
    const payment = rowToPayment(row, embedded);
    return {
      ...payment,
      createdAt: row.created_at || payment.createdAt || '',
      trainer: embedded?.trainer || row.trainer || '',
      branch: embedded?.branch || row.branch_name || row.branch || '',
    };
  };

  let mapped = (data || [])
    .filter((row) => String(row.status || '').toLowerCase() !== 'voided')
    .map(mapRow)
    .filter(matchesDay);

  if (!isReceptionistPanelScope()) return mapped;
  const branch = getReceptionistBranchFilter();
  return mapped.filter((p) => (
    !p.memberId
    || branchesMatch(p.branch, branch)
    || !branch
  ));
}

/**
 * Payments whose local calendar day falls in [dateFrom, dateTo] (inclusive).
 * Used by Daily Z TOTAL SALE SUMMARY date-range selector.
 */
export async function fetchPaymentsForDateRange(dateFrom, dateTo) {
  const from = String(dateFrom || '').slice(0, 10);
  const to = String(dateTo || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return [];
  const start = from <= to ? from : to;
  const end = from <= to ? to : from;

  const gid = await gymId();
  const shiftDay = (iso, delta) => {
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() + delta);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const windowFrom = shiftDay(start, -1);
  const windowTo = shiftDay(end, 1);

  const toLocalDay = (raw) => {
    const text = String(raw || '').trim();
    if (!text) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const d = String(parsed.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return '';
  };

  const inRange = (day) => day && day >= start && day <= end;

  const matchesRange = (payment) => {
    const primary = toLocalDay(payment.date || payment.paidAt || payment.paid_at);
    const source = String(payment.source || '').trim().toLowerCase().replace(/-/g, '_');
    const membershipLike = source === 'subscription'
      || source === 'membership'
      || source === 'new_membership'
      || source === 'subscriptions';
    if (membershipLike) {
      const startRaw = String(payment.startDate || payment.start_date || payment.start || '').trim();
      const memStart = /^\d{4}-\d{2}-\d{2}/.test(startRaw)
        ? startRaw.slice(0, 10)
        : toLocalDay(startRaw);
      if (inRange(memStart)) return true;
      return inRange(primary);
    }
    return inRange(primary);
  };

  let data = [];
  const selectWithTrainer = '*, members(full_name, gym_member_id, branch, trainer)';
  const selectWithMember = '*, members(full_name, gym_member_id)';
  try {
    data = await fetchAllPaged(() => (
      supabase
        .from('payments')
        .select(selectWithTrainer)
        .eq('gym_id', gid)
        .neq('status', 'voided')
        .gte('paid_at', windowFrom)
        .lte('paid_at', `${windowTo}T23:59:59.999`)
        .order('paid_at', { ascending: false })
        .order('id', { ascending: true })
    ));
  } catch (error) {
    if (/branch|trainer/i.test(error.message || '') || isEmbedError(error.message || '')) {
      try {
        data = await fetchAllPaged(() => (
          supabase
            .from('payments')
            .select(selectWithMember)
            .eq('gym_id', gid)
            .neq('status', 'voided')
            .gte('paid_at', windowFrom)
            .lte('paid_at', `${windowTo}T23:59:59.999`)
            .order('paid_at', { ascending: false })
            .order('id', { ascending: true })
        ));
      } catch {
        data = [];
      }
    } else {
      data = [];
    }
  }

  const mapRow = (row) => {
    const embedded = row.members
      ? {
          ...row.members,
          baseGymId: row.members.gym_member_id,
        }
      : null;
    const payment = rowToPayment(row, embedded);
    return {
      ...payment,
      createdAt: row.created_at || payment.createdAt || '',
      trainer: embedded?.trainer || row.trainer || '',
      branch: embedded?.branch || row.branch_name || row.branch || '',
    };
  };

  let mapped = (data || [])
    .filter((row) => String(row.status || '').toLowerCase() !== 'voided')
    .map(mapRow)
    .filter(matchesRange);
  if (!isReceptionistPanelScope()) return mapped;
  const branch = getReceptionistBranchFilter();
  return mapped.filter((p) => (
    !p.memberId
    || branchesMatch(p.branch, branch)
    || !branch
  ));
}

/**
 * Memberships with start_date on a calendar day → sale rows for
 * Detailed Report / Z Report New Memberships (matches Memberships list date filter).
 * Covers cases where the linked payment has a different paid_at (or is missing).
 */
export async function fetchMembershipSalesForDate(date) {
  const day = String(date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return [];

  const gid = await gymId();

  const selectEmbed = 'id, gym_id, member_id, package_name, start_date, expiry_date, dates_tbc, status, frozen, serial_no, duration, complimentary, created_at, members(full_name, gym_member_id, branch, trainer)';
  const selectPlain = 'id, gym_id, member_id, package_name, start_date, expiry_date, dates_tbc, status, frozen, serial_no, duration, complimentary, created_at';

  let membershipRows = [];
  try {
    membershipRows = await fetchAllPaged(() => (
      supabase
        .from('memberships')
        .select(selectEmbed)
        .eq('gym_id', gid)
        .eq('start_date', day)
        .order('start_date', { ascending: true })
        .order('id', { ascending: true })
    ));
  } catch (error) {
    try {
      membershipRows = await fetchAllPaged(() => (
        supabase
          .from('memberships')
          .select(selectPlain)
          .eq('gym_id', gid)
          .eq('start_date', day)
          .order('start_date', { ascending: true })
          .order('id', { ascending: true })
      ));
    } catch {
      if (/complimentary|duration|serial_no/i.test(error?.message || '')) {
        membershipRows = await fetchAllPaged(() => (
          supabase
            .from('memberships')
            .select('id, gym_id, member_id, package_name, start_date, expiry_date, status, frozen, created_at, members(full_name, gym_member_id, branch, trainer)')
            .eq('gym_id', gid)
            .eq('start_date', day)
            .order('id', { ascending: true })
        )).catch(() => []);
      } else {
        membershipRows = [];
      }
    }
  }

  if (!(membershipRows || []).length) return [];

  const memberIds = [...new Set(membershipRows.map((r) => r.member_id).filter(Boolean))];
  let payRows = [];
  if (memberIds.length) {
    try {
      for (const chunk of chunkIds(memberIds)) {
        const batch = await fetchAllPaged(() => (
          supabase
            .from('payments')
            .select('*')
            .eq('gym_id', gid)
            .in('member_id', chunk)
            .order('paid_at', { ascending: false })
            .order('id', { ascending: true })
        ));
        payRows.push(...(batch || []));
      }
    } catch {
      payRows = [];
    }
  }

  return attachMembershipSalesFromRows(day, membershipRows, payRows);
}

/**
 * Membership start_date in [dateFrom, dateTo] → New Membership sale rows for Z sale summary range.
 */
export async function fetchMembershipSalesForDateRange(dateFrom, dateTo) {
  const from = String(dateFrom || '').slice(0, 10);
  const to = String(dateTo || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return [];
  const start = from <= to ? from : to;
  const end = from <= to ? to : from;

  // Day-walk keeps pickPayment scoring identical to single-day Z Report logic.
  const days = [];
  const cursor = new Date(`${start}T12:00:00`);
  const last = new Date(`${end}T12:00:00`);
  while (cursor <= last) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const d = String(cursor.getDate()).padStart(2, '0');
    days.push(`${y}-${m}-${d}`);
    cursor.setDate(cursor.getDate() + 1);
    if (days.length > 93) break; // ~3 months cap
  }

  const all = [];
  const seen = new Set();
  for (const day of days) {
    const rows = await fetchMembershipSalesForDate(day).catch(() => []);
    (rows || []).forEach((row) => {
      const key = String(row.membershipId || row.rawId || row.id || '');
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      all.push(row);
    });
  }
  return all;
}

/** Payments for one member only — customer ledger / remaining balances. */
export async function fetchPaymentsForMember(memberId) {
  if (!memberId) return [];
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const branchFilter = String(getReceptionistBranchFilter() || getReceptionistBranch() || '').trim();

  try {
    const rpc = await fetchCustomerLedgerPageRpc({
      gymId: gid,
      memberId,
      branch: receptionist ? branchFilter : '',
      receptionistScope: receptionist,
    });
    if (rpc) {
      return rpc.payments || [];
    }
  } catch (err) {
    console.warn('Customer ledger RPC failed, using fallback', err?.message || err);
  }

  const [payRows, memberResult] = await Promise.all([
    loadPaymentsForMembers(gid, [memberId]),
    supabase
      .from('members')
      .select('id, full_name, gym_member_id, branch, trainer')
      .eq('id', memberId)
      .maybeSingle(),
  ]);
  const memberEmbed = memberResult?.data
    ? {
        ...memberResult.data,
        baseGymId: memberResult.data.gym_member_id,
      }
    : null;
  let mapped = (payRows || []).map((row) => rowToPayment(row, memberEmbed));
  if (receptionist) {
    const today = pkTodayKey();
    mapped = mapped.filter((row) => {
      const day = String(row.paidAt || row.date || row.createdAt || '').slice(0, 10);
      return day === today;
    });
  }
  return mapped;
}

/**
 * Daily Z — TOTAL ACTIVE MEMBER block.
 * Month = calendar month of reportDate through that day.
 */
export async function fetchZReportActiveMemberStats(reportDate) {
  const gid = await gymId();
  const day = String(reportDate || todayDate()).slice(0, 10);
  const monthStart = `${day.slice(0, 7)}-01`;
  const empty = {
    membership_sale_month: 0,
    pt_sale_month: 0,
    active_men: 0,
    active_women: 0,
  };
  if (!gid || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return empty;

  const branchFilter = String(getReceptionistBranchFilter() || '').trim();
  const genderOf = (member) => {
    const raw = String(
      member?.gender
      || member?.profile?.gender
      || '',
    ).trim().toLowerCase();
    if (!raw) return 'unknown';
    if (raw.startsWith('f') || raw.includes('female') || raw.includes('women') || raw === 'w') {
      return 'female';
    }
    if (raw.startsWith('m') || raw.includes('male') || raw.includes('men') || raw === 'm') {
      return 'male';
    }
    return 'unknown';
  };

  let activeMen = 0;
  let activeWomen = 0;
  try {
    const useBranch = isReceptionistPanelScope() && branchFilter && branchFilter !== 'all';
    const select = useBranch
      ? 'member_id, status, frozen, members!inner(gender, profile, branch)'
      : 'member_id, status, frozen, members(gender, profile, branch)';
    let rows = [];
    try {
      rows = await fetchAllPaged(() => {
        let q = supabase
          .from('memberships')
          .select(select)
          .eq('gym_id', gid)
          .eq('status', 'active');
        if (useBranch) q = q.eq('members.branch', branchFilter);
        return q;
      });
    } catch (err) {
      if (isEmbedError(err.message || '') || /branch|gender|profile/i.test(err.message || '')) {
        rows = await fetchAllPaged(() => (
          supabase
            .from('memberships')
            .select('member_id, status, frozen')
            .eq('gym_id', gid)
            .eq('status', 'active')
        ));
      } else {
        throw err;
      }
    }

    const seen = new Set();
    const needGenderIds = [];
    (rows || []).forEach((row) => {
      if (row?.frozen) return;
      const mid = String(row.member_id || '').trim();
      if (!mid || seen.has(mid)) return;
      seen.add(mid);
      const g = genderOf(row.members);
      if (g === 'female') activeWomen += 1;
      else if (g === 'male') activeMen += 1;
      else needGenderIds.push(mid);
    });

    // Fallback gender lookup when embed lacked gender
    if (needGenderIds.length) {
      const chunkSize = 200;
      for (let i = 0; i < needGenderIds.length; i += chunkSize) {
        const chunk = needGenderIds.slice(i, i + chunkSize);
        let mq = supabase
          .from('members')
          .select('id, gender, profile')
          .eq('gym_id', gid)
          .in('id', chunk);
        if (useBranch) mq = applyMemberBranchOrFilter(mq, branchFilter);
        const { data: members } = await mq;
        (members || []).forEach((m) => {
          const g = genderOf(m);
          if (g === 'female') activeWomen += 1;
          else activeMen += 1;
        });
      }
    }
  } catch (err) {
    console.warn('Z active member gender counts failed', err?.message || err);
  }

  let membershipSaleMonth = 0;
  let ptSaleMonth = 0;
  try {
    const monthPays = await fetchPaymentsForDateRange(monthStart, day).catch(() => []);
    (monthPays || []).forEach((p) => {
      const src = String(p?.source || '').toLowerCase().replace(/-/g, '_');
      const note = String(p?.paymentNote || p?.payment_note || '');
      const isRenewal = src === 'renewal_sale' || src === 'renewal'
        || /^(Renewed|Complimentary renewal)/i.test(note.split(' · ')[0] || '');
      if (src === 'pt_sale') {
        ptSaleMonth += 1;
        return;
      }
      if (isRenewal) return;
      if (
        !src
        || src === 'subscription'
        || src === 'membership'
        || src === 'new_membership'
        || src === 'subscriptions'
      ) {
        membershipSaleMonth += 1;
      }
    });
  } catch (err) {
    console.warn('Z month sale counts failed', err?.message || err);
  }

  return {
    membership_sale_month: membershipSaleMonth,
    pt_sale_month: ptSaleMonth,
    active_men: activeMen,
    active_women: activeWomen,
  };
}

function mapRemainingPaymentRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
    trainer: members.trainer,
  });
  const isWalkInRow = (
    isWalkInMemberName(payment.member)
    || isWalkInGymMemberId(payment.gymId)
    || isWalkInGymMemberId(members.gym_member_id)
    || Boolean(row.is_walk_in)
  );
  const withFlags = {
    ...payment,
    trainer: members.trainer || row.trainer || payment.trainer || '',
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
    isWalkIn: isWalkInRow,
  };
  const left = Number(row.pending_amount);
  return {
    ...withFlags,
    pendingAmount: Number.isFinite(left) ? left : saleRowLeft(withFlags),
    isWalkIn: isWalkInRow,
  };
}

/**
 * Fresh remaining balances. RPC first (open leftovers only).
 * Receptionist: assigned branch + blank. Admin: gym-wide. No PK-today slice.
 * Fallback: fetchAllPaged payments + client remaining/membership filter.
 */
export async function fetchRemainingPayments({ memberId = null } = {}) {
  const gid = await gymId();
  const mid = memberId ? String(memberId).trim() : '';
  const receptionist = isReceptionistPanelScope();
  let branchFilter = String(
    getReceptionistBranchFilter() || getReceptionistBranch() || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  if (receptionist && !branchFilter) {
    return [];
  }

  try {
    const rpc = await fetchRemainingPaymentsPageRpc({
      gymId: gid,
      branch: receptionist ? branchFilter : '',
      memberId: mid || null,
      allMatching: true,
    });
    if (rpc) {
      return (rpc.rows || [])
        .map(mapRemainingPaymentRpcRow)
        .filter((row) => saleRowHasRemainingBalance(row))
        .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    }
  } catch (err) {
    console.warn('Remaining Payments RPC failed, using fallback', err?.message || err);
  }

  return fetchRemainingPaymentsLegacy({ memberId: mid });
}

async function fetchRemainingPaymentsLegacy({ memberId = null } = {}) {
  const gid = await gymId();
  const mid = memberId ? String(memberId).trim() : '';

  const buildPaymentQuery = (selectCols) => () => {
    let query = supabase
      .from('payments')
      .select(selectCols)
      .eq('gym_id', gid)
      .order('paid_at', { ascending: false })
      .order('id', { ascending: true });
    if (mid) query = query.eq('member_id', mid);
    return query;
  };

  let data = [];
  const selectWithTrainer = '*, members(full_name, gym_member_id, branch, trainer)';
  const selectWithMember = '*, members(full_name, gym_member_id, branch)';
  try {
    data = await fetchAllPaged(buildPaymentQuery(selectWithTrainer));
  } catch (error) {
    if (/branch|trainer/i.test(error.message || '') || isEmbedError(error.message || '')) {
      try {
        data = await fetchAllPaged(buildPaymentQuery(selectWithMember));
      } catch (inner) {
        if (isEmbedError(inner.message || '')) {
          data = await fetchAllPaged(buildPaymentQuery('*'));
        } else {
          throw inner;
        }
      }
    } else {
      throw error;
    }
  }

  // Catalog fallback for legacy rows missing package_price
  let packagePriceByName = new Map();
  try {
    packagePriceByName = await loadPackagePriceByName(gid);
  } catch {
    packagePriceByName = new Map();
  }

  let mapped = (data || []).map((row) => {
    const embedded = row.members
      ? {
          ...row.members,
          baseGymId: row.members.gym_member_id,
        }
      : null;
    let payment = rowToPayment(row, embedded);
    payment = {
      ...payment,
      trainer: embedded?.trainer || row.trainer || '',
      branch: embedded?.branch || row.branch_name || row.branch || '',
    };

    const status = String(payment.status || '').toLowerCase();
    const source = String(payment.source || '').toLowerCase().replace(/-/g, '_');
    const isSubscriptionSource = (
      source === 'subscription'
      || source === 'membership'
      || source === 'new_membership'
      || source === 'subscriptions'
    );
    const isWalkInRow = (
      isWalkInMemberName(payment.member)
      || isWalkInGymMemberId(payment.gymId)
      || isWalkInGymMemberId(embedded?.gym_member_id)
      || isWalkInMember(embedded ? {
        name: embedded.full_name,
        gymId: embedded.gym_member_id,
        gym_member_id: embedded.gym_member_id,
        isWalkIn: embedded.is_walk_in,
        profile: embedded,
      } : null)
    );

    // Mark walk-in before totals so remaining math ignores phantom fees
    payment = { ...payment, isWalkIn: isWalkInRow };

    // Catalog price backfill ONLY for real membership subscriptions missing package_price.
    // Never invent dues for walk-ins, guest passes, or ancillary sales (causes false "remaining").
    const storedPackagePrice = Number(row.package_price);
    const packagePriceMissing = !Object.prototype.hasOwnProperty.call(row, 'package_price')
      || row.package_price == null
      || row.package_price === '';
    if (
      isSubscriptionSource
      && !isWalkInRow
      && !isPendingGymMemberId(embedded?.gym_member_id || payment.gymId)
      && packagePriceMissing
      && !(storedPackagePrice > 0)
      && status !== 'complimentary'
      && status !== 'completed'
    ) {
      const rawPkg = String(payment.package || '').trim().toLowerCase();
      const catalog = packagePriceByName.get(rawPkg) || 0;
      if (catalog > 0) {
        payment = {
          ...payment,
          packagePrice: catalog,
        };
      }
    }

    const left = saleRowLeft(payment);
    return {
      ...payment,
      pendingAmount: left,
      isWalkIn: isWalkInRow,
    };
  });

  // Branch scope for receptionist (blank member branch still visible)
  if (isReceptionistPanelScope() && !mid) {
    const withMember = filterByReceptionistBranch(
      mapped.filter((p) => p.memberId),
      (p) => p.branch,
    );
    const walkIns = mapped.filter((p) => !p.memberId);
    mapped = [...withMember, ...walkIns];
  }

  // Active memberships — hide subscription balances when that membership was never created / was deleted
  const membershipPackageKeys = new Set();
  let membershipsLoaded = false;
  try {
    const membershipMemberIds = [...new Set(
      mapped
        .filter((row) => {
          const source = String(row.source || '').toLowerCase().replace(/-/g, '_');
          return (
            row.memberId
            && (
              source === 'subscription'
              || source === 'membership'
              || source === 'new_membership'
              || source === 'subscriptions'
              || !source
            )
          );
        })
        .map((row) => row.memberId),
    )];
    for (const chunk of chunkIds(membershipMemberIds)) {
      const { data: membershipRows, error: membershipError } = await supabase
        .from('memberships')
        .select('member_id, package_name')
        .eq('gym_id', gid)
        .in('member_id', chunk);
      if (membershipError) throw membershipError;
      (membershipRows || []).forEach((row) => {
        if (!row.member_id) return;
        const pkg = String(row.package_name || '').trim().toLowerCase();
        membershipPackageKeys.add(`${row.member_id}::${pkg}`);
        membershipPackageKeys.add(`${row.member_id}::`);
      });
    }
    membershipsLoaded = true;
  } catch {
    // If memberships cannot be loaded, keep payment-based filter only
    membershipPackageKeys.clear();
    membershipsLoaded = false;
  }

  return mapped
    .filter((row) => {
      if (!saleRowHasRemainingBalance(row)) return false;

      // Membership dues only when that member still has the matching membership
      const source = String(row.source || '').toLowerCase().replace(/-/g, '_');
      const isSubscriptionSource = (
        source === 'subscription'
        || source === 'membership'
        || source === 'new_membership'
        || source === 'subscriptions'
        || !source
      );
      if (
        isSubscriptionSource
        && !row.isWalkIn
        && row.memberId
        && membershipsLoaded
      ) {
        const pkg = String(row.package || '').trim().toLowerCase();
        const exactKey = `${row.memberId}::${pkg}`;
        const anyKey = `${row.memberId}::`;
        if (pkg) {
          if (!membershipPackageKeys.has(exactKey)) return false;
        } else if (!membershipPackageKeys.has(anyKey)) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

export async function createPayment(record) {
  const gid = await gymId();
  let memberId = record.memberId || null;
  if (!memberId && record.member && record.member !== 'Walk-In Customer') {
    const m = await findMemberByName(record.member);
    memberId = m?.id || null;
  }

  const packagePrice = Number(record.packagePrice) || 0;
  const registrationFee = Number(record.registrationFee) || 0;
  const discountAmount = Number(record.discountAmount) || 0;
  const personalTraining = Number(record.personalTraining) || 0;
  const classesFee = Number(record.classesFee) || 0;
  const amount = Number(record.amount) || 0;
  // Online: blank invoice_id → DB trigger assigns Rec NO in same INSERT txn (no burn on fail).
  let invoiceId = await resolveInvoiceId(record.invoiceId, gid);
  const methodRaw = String(record.method || '').trim();
  const paymentMethod = (!methodRaw || methodRaw === '—' || methodRaw === '-')
    ? 'cash'
    : methodRaw;
  const paidAtDate = dateOrNull(record.date) || todayDate();
  const createdAt = new Date().toISOString();

  const row = {
    gym_id: gid,
    member_id: memberId,
    payment_ref: record.paymentId || (String(record.id || '').startsWith('PAY-') ? record.id : null) || null,
    member_name: record.member,
    package_name: record.package,
    amount,
    payment_method: paymentMethod,
    status: record.status || 'completed',
    paid_at: paidAtDate,
    created_at: createdAt,
    package_price: packagePrice,
    discount_percent: Number(record.discountPercent) || 0,
    payment_note: formatPaymentNote(record.member, record.paymentNote, memberId),
    source: record.source || 'subscription',
    payment_splits: record.paymentSplits?.length ? record.paymentSplits : undefined,
    start_date: dateOrNull(record.startDate) || undefined,
    end_date: dateOrNull(record.endDate) || undefined,
    duration: record.duration || undefined,
  };
  if (invoiceId) {
    row.invoice_id = invoiceId;
  } else {
    row.invoice_id = null;
  }
  // Only send fee columns when non-zero so missing DB columns can be skipped safely
  if (discountAmount > 0) row.discount_amount = discountAmount;
  if (registrationFee > 0) row.registration_fee = registrationFee;
  if (personalTraining > 0) row.personal_training_fee = personalTraining;
  if (classesFee > 0) row.classes_fee = classesFee;
  // payments.id is uuid — only set it when caller passes a real uuid (never PAY-/INV- labels)
  const maybeUuid = String(record.id || '').trim();
  if (isUuidValue(maybeUuid)) {
    row.id = maybeUuid;
  }
  Object.assign(row, stampPaymentBranchFields({}, record.branch));
  const expectedSource = String(record.source || 'subscription').trim() || 'subscription';

  // Only hard-preserve columns that must exist for a valid sale.
  // Fee / optional columns (registration_fee, splits, etc.) are skipped when the DB
  // schema is missing them — preserving 0-valued fee cols was breaking ALL sale modules.
  const preserveColumns = [
    'gym_id',
    'amount',
    'paid_at',
    'created_at',
    'status',
    'payment_method',
  ];
  if (memberId) preserveColumns.push('member_id');
  if (row.package_name) preserveColumns.push('package_name');
  if (packagePrice > 0) preserveColumns.push('package_price');
  if (registrationFee > 0) preserveColumns.push('registration_fee');
  if (discountAmount > 0) preserveColumns.push('discount_amount');
  if (personalTraining > 0) preserveColumns.push('personal_training_fee');
  if (classesFee > 0) preserveColumns.push('classes_fee');
  if (row.payment_ref) preserveColumns.push('payment_ref');
  if (row.invoice_id) preserveColumns.push('invoice_id');
  if (expectedSource) preserveColumns.push('source');

  let data = null;
  let error = null;
  for (let attempt = 0; attempt < 6; attempt += 1) {
    if (invoiceId) row.invoice_id = invoiceId;
    else {
      row.invoice_id = null;
      // Ensure preserve list does not force a blank explicit id
      const idx = preserveColumns.indexOf('invoice_id');
      if (idx >= 0) preserveColumns.splice(idx, 1);
    }
    // eslint-disable-next-line no-await-in-loop
    ({ data, error } = await insertRowSkippingMissingColumns(
      'payments',
      row,
      '*',
      { preserveColumns },
    ));
    if (!error) break;
    const detail = String(error.message || error.details || error.hint || '');
    const duplicateInvoice = /duplicate|unique|payments_gym_paper_invoice/i.test(detail);
    if (duplicateInvoice && attempt < 5) {
      // Clear id so trigger assigns a fresh Rec NO on retry (no client pre-allocate burn)
      invoiceId = '';
      continue;
    }
    throw new Error(detail || 'Payment insert failed');
  }

  let saved = data && !data.__insertedHidden ? data : null;

  // If SELECT was hidden, find the row we just wrote
  if (!saved?.id && (row.invoice_id || row.payment_ref || memberId)) {
    let lookup = supabase.from('payments').select('*').eq('gym_id', gid);
    if (row.invoice_id) lookup = lookup.eq('invoice_id', row.invoice_id);
    else if (row.payment_ref) lookup = lookup.eq('payment_ref', row.payment_ref);
    else lookup = lookup.eq('member_id', memberId);
    const { data: found } = await lookup.order('created_at', { ascending: false }).limit(1);
    saved = found?.[0] || null;
  }

  // Guarantee fee columns + sale source persist (some DBs drop them on insert/RLS quirks)
  if (saved?.id && (packagePrice > 0 || registrationFee > 0 || amount > 0 || expectedSource)) {
    const needsFeePatch = (
      Number(saved.package_price) !== packagePrice
      || (registrationFee > 0 && Number(saved.registration_fee) !== registrationFee)
      || (discountAmount > 0 && Number(saved.discount_amount) !== discountAmount)
      || Number(saved.amount) !== amount
      || (memberId && saved.member_id !== memberId)
      || (row.package_name && saved.package_name !== row.package_name)
      || String(saved.source || '') !== expectedSource
    );
    if (needsFeePatch) {
      const patch = {
        member_id: memberId,
        package_name: row.package_name,
        amount,
        package_price: packagePrice,
        payment_splits: row.payment_splits,
        payment_method: row.payment_method,
        status: row.status,
        source: expectedSource,
        paid_at: row.paid_at,
      };
      if (registrationFee > 0) patch.registration_fee = registrationFee;
      if (discountAmount > 0) patch.discount_amount = discountAmount;
      if (personalTraining > 0) patch.personal_training_fee = personalTraining;
      if (classesFee > 0) patch.classes_fee = classesFee;
      const { data: patched, error: patchError } = await updateRowSkippingMissingColumns(
        'payments',
        patch,
        saved.id,
        '*',
      );
      if (!patchError && patched) saved = patched;
    }
  }

  if (!record.skipCacheBust) await bustGymCache();
  return rowToPayment(saved || data || row, null);
}

/** Update an existing sale payment (ancillary / guest pass / renewal / payments list). */
export async function updatePaymentSale(paymentDbId, {
  memberName,
  gymMemberId,
  packageName,
  duration = '',
  startDate = '',
  endDate = '',
  date = '',
  packageFee,
  registrationFee,
  discountAmount,
  personalTrainingFee,
  classesFee,
  paymentSplits,
  paymentNote,
} = {}) {
  if (!paymentDbId) throw new Error('Payment record not found.');
  const id = String(paymentDbId).trim();
  if (id.startsWith('mem-')) {
    throw new Error('This row is not a payment record and cannot be edited here.');
  }

  const gid = await gymId();
  const { data: existing, error: fetchError } = await supabase
    .from('payments')
    .select('*, members(id, full_name, gym_member_id, profile)')
    .eq('gym_id', gid)
    .eq('id', id)
    .maybeSingle();
  if (fetchError) throw fetchError;
  if (!existing) throw new Error('Payment record not found.');

  const paid = sumPaymentSplits(paymentSplits);
  const fee = Math.max(0, Number(packageFee) || 0);
  const reg = registrationFee != null
    ? Math.max(0, Number(registrationFee) || 0)
    : Math.max(0, Number(existing.registration_fee) || 0);
  const discount = discountAmount != null
    ? Math.max(0, Number(discountAmount) || 0)
    : Math.max(0, Number(existing.discount_amount) || 0);
  const pt = personalTrainingFee != null
    ? Math.max(0, Number(personalTrainingFee) || 0)
    : Math.max(0, Number(existing.personal_training_fee) || 0);
  const classes = classesFee != null
    ? Math.max(0, Number(classesFee) || 0)
    : Math.max(0, Number(existing.classes_fee) || 0);
  const totalDue = Math.max(0, fee - discount + reg + pt + classes);
  const left = Math.max(0, totalDue - paid);
  const splits = serializePaymentSplits(paymentSplits);
  const durationLabel = String(duration || '').trim();
  const pkg = String(packageName || existing.package_name || '').trim();
  const nextMemberName = String(memberName ?? existing.member_name ?? '').trim();
  const nextSerialRaw = String(gymMemberId ?? '').trim();
  const nextSerial = nextSerialRaw.replace(/^id[\s\-:_]*/i, '').trim() || nextSerialRaw;

  const linkedMember = existing.members
    ? {
        id: existing.members.id || existing.member_id,
        name: existing.members.full_name,
        gymId: existing.members.gym_member_id,
        profile: existing.members.profile,
        isWalkIn: existing.members.profile?.is_walk_in,
      }
    : null;
  const walkInLinked = Boolean(
    linkedMember
    && (isWalkInMember(linkedMember) || isWalkInGymMemberId(linkedMember.gymId) || isWalkInMemberName(linkedMember.name)),
  );

  // Keep linked member name/serial in sync (except permanent walk-in master record)
  if (existing.member_id && !walkInLinked) {
    const memberPatch = {};
    if (nextMemberName) memberPatch.full_name = nextMemberName;
    if (nextSerial) memberPatch.gym_member_id = nextSerial;
    if (Object.keys(memberPatch).length) {
      const { error: memberError } = await supabase
        .from('members')
        .update(memberPatch)
        .eq('id', existing.member_id)
        .eq('gym_id', gid);
      if (memberError) {
        const isDup = /members_gym_member_id_uq|members_gym_branch_member_id_uq|duplicate key|unique constraint/i
          .test(memberError.message || '');
        if (isDup) throw new Error(`Serial No "${nextSerial}" is already used by another member.`);
        throw memberError;
      }
    }

    if (nextSerial && /^\d+$/.test(nextSerial)) {
      try {
        await supabase
          .from('memberships')
          .update({ serial_no: Number(nextSerial) })
          .eq('member_id', existing.member_id)
          .eq('gym_id', gid);
      } catch {
        /* non-fatal if serial_no column/type differs */
      }
    }

    if (nextMemberName) {
      try {
        await supabase
          .from('payments')
          .update({ member_name: nextMemberName })
          .eq('gym_id', gid)
          .eq('member_id', existing.member_id);
      } catch {
        /* non-fatal */
      }
    }
  }

  const userNote = String(paymentNote || '').trim();
  const existingNote = String(existing.payment_note || '');
  const existingParts = existingNote
    .split(' · ')
    .map((part) => part.trim())
    .filter(Boolean);
  const complimentaryRenewal = existingParts.some((p) => /^Complimentary renewal/i.test(p));
  const isRenewalSale = isRenewalPaymentSource(existing.source)
    || existingParts.some((p) => /^(Renewed|Complimentary renewal)/i.test(p));

  const oldRenewalPart = existingParts.find((p) => /^(Renewed|Complimentary renewal)/i.test(p)) || '';
  const oldMembershipTo = (oldRenewalPart.match(/membership to (\d{4}-\d{2}-\d{2})/i) || [])[1] || '';
  const oldDurationLabel = ((oldRenewalPart.match(/^(?:Renewed|Complimentary renewal)\s*:?\s*(.+?)\s*\(/i) || [])[1] || '').trim();
  const oldDurationKey = durationKeyFromLabel(oldDurationLabel);
  const newDurationKey = durationKeyFromLabel(durationLabel);
  const saleStart = startDate ? String(startDate).slice(0, 10) : '';
  const saleEnd = endDate ? String(endDate).slice(0, 10) : '';
  let membershipTo = '';
  if (isRenewalSale && newDurationKey) {
    const extendFrom = (oldMembershipTo && oldDurationKey)
      ? periodStartFromEndDate(oldMembershipTo, oldDurationKey)
      : saleStart;
    membershipTo = addDurationToDate(extendFrom || saleStart, newDurationKey) || oldMembershipTo;
  }

  // Keep user note + Phone; rewrite Duration / Renewed so edited duration actually shows
  const keptParts = existingParts.filter((part) => (
    /^Phone:/i.test(part)
    && !/^(Duration:|Renewed|Complimentary renewal)/i.test(part)
  ));
  let nextNote = existingNote;
  if (userNote) {
    nextNote = [userNote, ...keptParts].filter(Boolean).join(' · ');
  } else {
    const leftover = existingParts.filter((part) => (
      !/^(Duration:|Renewed|Complimentary renewal)/i.test(part)
    ));
    nextNote = leftover.join(' · ');
  }
  if (durationLabel && saleStart && saleEnd) {
    const parts = String(nextNote || '')
      .split(' · ')
      .map((part) => part.trim())
      .filter((part) => part && !/^(Duration:|Renewed|Complimentary renewal)/i.test(part));
    const periodLine = isRenewalSale
      ? `${complimentaryRenewal ? 'Complimentary renewal' : 'Renewed'} ${durationLabel} (${saleStart} to ${saleEnd}${membershipTo ? `, membership to ${membershipTo}` : ''})`
      : `Duration: ${durationLabel} (${saleStart} to ${saleEnd})`;
    parts.push(periodLine);
    nextNote = parts.filter(Boolean).join(' · ');
  }

  const patch = {
    member_name: nextMemberName || undefined,
    package_name: pkg || undefined,
    package_price: fee,
    registration_fee: reg,
    discount_amount: discount,
    personal_training_fee: pt,
    classes_fee: classes,
    amount: paid,
    payment_splits: splits.length ? splits : undefined,
    payment_method: combineMethodLabels(paymentSplits),
    status: totalDue <= 0 || left <= 0 ? 'completed' : 'pending',
    payment_note: nextNote || undefined,
    start_date: startDate ? String(startDate).slice(0, 10) : undefined,
    end_date: endDate ? String(endDate).slice(0, 10) : undefined,
    paid_at: date ? String(date).slice(0, 10) : undefined,
    duration: durationLabel || undefined,
  };

  const { data, error } = await updateRowSkippingMissingColumns(
    'payments',
    patch,
    id,
    '*, members(full_name, gym_member_id, branch, trainer)',
  );
  if (error) throw error;

  // Keep linked membership in sync. Renewal edits must not overwrite original start_date
  // or keep the old duration note — extend expiry + duration instead.
  if (existing.member_id && (isNewSubscriptionPaymentSource(existing.source) || isRenewalPaymentSource(existing.source))) {
    try {
      const pkgNorm = pkg.toLowerCase();
      let membershipQuery = supabase
        .from('memberships')
        .select('id, package_name')
        .eq('gym_id', gid)
        .eq('member_id', existing.member_id)
        .order('created_at', { ascending: false })
        .limit(8);
      const { data: membershipHits } = await membershipQuery;
      const match = (membershipHits || []).find(
        (m) => String(m.package_name || '').trim().toLowerCase() === pkgNorm,
      ) || (membershipHits || [])[0];
      if (match?.id) {
        const membershipPatch = isRenewalSale
          ? {
            package_name: pkg || undefined,
            expiry_date: membershipTo || saleEnd || undefined,
            duration: durationLabel || undefined,
            registration_fee: reg,
          }
          : {
            package_name: pkg || undefined,
            start_date: saleStart || undefined,
            expiry_date: saleEnd || undefined,
            duration: durationLabel || undefined,
            registration_fee: reg,
          };
        await updateRowSkippingMissingColumns(
          'memberships',
          membershipPatch,
          match.id,
          'id',
        );
      }
    } catch {
      /* non-fatal if memberships.registration_fee / package_name join differs */
    }
  }

  await bustGymCache();
  const embedded = data?.members
    ? { ...data.members, baseGymId: data.members.gym_member_id }
    : null;
  return rowToPayment(data || { ...existing, ...patch }, embedded);
}

/** Parse original sale ref from "Remaining clear · amount · REF". */
function remainingClearOriginalRef(noteOrRow) {
  const note = typeof noteOrRow === 'string'
    ? noteOrRow
    : String(noteOrRow?.paymentNote || noteOrRow?.payment_note || '');
  const match = note.match(/Remaining clear\s*·\s*[\d.]+\s*·\s*([^\s·]+)/i);
  return String(match?.[1] || '').trim();
}

function subtractPaymentSplits(existing, toRemove) {
  const totals = new Map();
  normalizeSplitRows(existing).forEach((row) => {
    totals.set(row.method, (totals.get(row.method) || 0) + row.amount);
  });
  let leftover = 0;
  normalizeSplitRows(toRemove).forEach((row) => {
    const cur = totals.get(row.method) || 0;
    if (cur >= row.amount) {
      totals.set(row.method, Math.round((cur - row.amount) * 100) / 100);
    } else {
      totals.set(row.method, 0);
      leftover += row.amount - cur;
    }
  });
  if (leftover > 0) {
    const order = ['cash', 'card', 'bank_transfer', 'qr', 'online'];
    for (const method of order) {
      if (leftover <= 0) break;
      const cur = totals.get(method) || 0;
      if (!(cur > 0)) continue;
      const take = Math.min(cur, leftover);
      totals.set(method, Math.round((cur - take) * 100) / 100);
      leftover = Math.round((leftover - take) * 100) / 100;
    }
    if (leftover > 0) {
      for (const [method, cur] of [...totals.entries()]) {
        if (leftover <= 0) break;
        if (!(cur > 0)) continue;
        const take = Math.min(cur, leftover);
        totals.set(method, Math.round((cur - take) * 100) / 100);
        leftover = Math.round((leftover - take) * 100) / 100;
      }
    }
  }
  return Array.from(totals.entries())
    .map(([method, amount]) => ({ method, amount: Math.round(amount * 100) / 100 }))
    .filter((row) => row.amount > 0);
}

function stripBalancePaymentNote(note, amount, day = '') {
  const targetAmt = Math.round((Number(amount) || 0) * 100) / 100;
  const targetDay = String(day || '').slice(0, 10);
  const parts = String(note || '')
    .split(' · ')
    .map((p) => p.trim())
    .filter(Boolean);
  let removed = false;
  const kept = parts.filter((part) => {
    if (removed) return true;
    const match = part.match(
      /^Balance payment on (\d{4}-\d{2}-\d{2})(?::[^·]*)?\s*·\s*([\d.]+)$/i,
    );
    if (!match) return true;
    const partDay = match[1];
    const partAmt = Math.round((Number(match[2]) || 0) * 100) / 100;
    if (Math.abs(partAmt - targetAmt) > 0.02) return true;
    if (targetDay && partDay !== targetDay) return true;
    removed = true;
    return false;
  });
  return kept.join(' · ');
}

async function findOriginalSaleForRemainingClear(gid, remainingRow) {
  const ref = remainingClearOriginalRef(remainingRow);
  if (!ref) return null;
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(ref);
  let query = supabase.from('payments').select('*').eq('gym_id', gid);
  if (isUuid) query = query.eq('id', ref);
  else query = query.or(`invoice_id.eq.${ref},payment_ref.eq.${ref}`);
  const { data, error } = await query.limit(3);
  if (error) throw error;
  const rows = (data || []).filter((row) => {
    const source = String(row.source || '').toLowerCase().replace(/-/g, '_');
    return source !== 'remaining_clear' && String(row.status || '').toLowerCase() !== 'voided';
  });
  return rows[0] || null;
}

async function reverseRemainingClearFromOriginal(gid, remainingRow) {
  const original = await findOriginalSaleForRemainingClear(gid, remainingRow);
  if (!original?.id) return null;

  const clearAmt = Math.round((
    Number(remainingRow.amount)
    || sumPaymentSplits(parsePaymentSplitsFromRow(remainingRow))
    || 0
  ) * 100) / 100;
  if (!(clearAmt > 0)) return original;

  const clearSplits = parsePaymentSplitsFromRow(remainingRow);
  const clearDay = String(remainingRow.paid_at || remainingRow.paidAt || remainingRow.date || '').slice(0, 10);
  const currentPaid = Number(original.amount) || 0;
  const nextPaid = Math.max(0, Math.round((currentPaid - clearAmt) * 100) / 100);
  const nextSplits = subtractPaymentSplits(
    parsePaymentSplitsFromRow(original),
    clearSplits.length ? clearSplits : [{ method: 'cash', amount: clearAmt }],
  );
  const mapped = rowToPayment(original, null);
  const totalDue = paymentTotalDue(mapped);
  const left = Math.max(0, totalDue - nextPaid);
  const nextNote = stripBalancePaymentNote(original.payment_note, clearAmt, clearDay);

  const patch = {
    amount: nextPaid,
    payment_splits: nextSplits.length ? nextSplits : [],
    payment_method: nextSplits.length ? combineMethodLabels(nextSplits) : (original.payment_method || 'cash'),
    status: left <= 0 && totalDue > 0 ? 'completed' : (totalDue > 0 ? 'pending' : (original.status || 'completed')),
    payment_note: nextNote || null,
  };
  const { error } = await updateRowSkippingMissingColumns('payments', patch, original.id, 'id');
  if (error) throw error;
  return original;
}

async function applyRemainingClearOntoOriginal(gid, originalId, {
  amount,
  splits,
  day,
  paymentNote = '',
}) {
  const { data: original, error: fetchError } = await supabase
    .from('payments')
    .select('*')
    .eq('gym_id', gid)
    .eq('id', originalId)
    .maybeSingle();
  if (fetchError) throw fetchError;
  if (!original) return null;

  const addAmt = Math.round((Number(amount) || 0) * 100) / 100;
  if (!(addAmt > 0)) return original;

  const addSplits = normalizeSplitRows(splits).length
    ? normalizeSplitRows(splits)
    : [{ method: 'cash', amount: addAmt }];
  const mapped = rowToPayment(original, null);
  const totalDue = paymentTotalDue(mapped);
  const currentPaid = Number(original.amount) || 0;
  const nextPaid = Math.round((currentPaid + addAmt) * 100) / 100;
  const nextSplits = mergePaymentSplits(parsePaymentSplitsFromRow(original), addSplits);
  const left = Math.max(0, totalDue - nextPaid);
  const balanceDay = String(day || todayDate()).slice(0, 10);
  const balanceNote = paymentNote
    ? `Balance payment on ${balanceDay}: ${paymentNote} · ${addAmt}`
    : `Balance payment on ${balanceDay} · ${addAmt}`;
  const combinedNote = [original.payment_note, balanceNote].filter(Boolean).join(' · ');

  const patch = {
    amount: nextPaid,
    payment_splits: nextSplits,
    payment_method: combineMethodLabels(nextSplits),
    status: left <= 0 ? 'completed' : 'pending',
    payment_note: combinedNote,
  };
  const { error } = await updateRowSkippingMissingColumns('payments', patch, original.id, 'id');
  if (error) throw error;
  return original;
}

/**
 * Void a sale/payment (keeps invoice / CR No. so the sequence has no silent gaps).
 * Hard-delete used to remove the row while gym_invoice_counters stayed advanced —
 * that is why CR numbers like 37211/37212 can appear "missing".
 * Remaining-clear voids also reverse the amount merged onto the original sale.
 */
export async function deletePayment(paymentDbId) {
  if (!paymentDbId) throw new Error('Payment record not found.');
  const id = String(paymentDbId).trim();
  if (id.startsWith('mem-')) {
    throw new Error('This row is not a payment record and cannot be deleted here.');
  }

  const gid = await gymId();
  const { data: existing, error: fetchError } = await supabase
    .from('payments')
    .select('id, status, payment_note, invoice_id, source, amount, payment_splits, payment_method, paid_at')
    .eq('gym_id', gid)
    .eq('id', id)
    .maybeSingle();
  if (fetchError) throw fetchError;
  if (!existing) throw new Error('Payment record not found.');
  if (String(existing.status || '').toLowerCase() === 'voided') {
    await bustGymCache();
    return;
  }

  const source = String(existing.source || '').toLowerCase().replace(/-/g, '_');
  if (source === 'remaining_clear') {
    try {
      await reverseRemainingClearFromOriginal(gid, existing);
    } catch (syncErr) {
      console.warn('Could not reverse remaining clear on original sale:', syncErr?.message || syncErr);
    }
  }

  const stamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const priorNote = String(existing.payment_note || '').trim();
  const voidNote = priorNote
    ? `${priorNote} · Voided ${stamp}`
    : `Voided ${stamp}`;
  const { error } = await updateRowSkippingMissingColumns(
    'payments',
    { status: 'voided', payment_note: voidNote },
    id,
    'id',
  );
  if (error) throw error;
  await bustGymCache();
}

/**
 * Edit a cleared Remaining Payment (amount / method / date).
 * Keeps the original sale in sync so Cash/Card ledgers stay correct.
 */
export async function updateRemainingClearPayment(paymentDbId, {
  amount,
  paymentSplits,
  date,
  paymentNote,
} = {}) {
  if (!paymentDbId) throw new Error('Remaining clear record not found.');
  const id = String(paymentDbId).trim();
  const gid = await gymId();

  const { data: existing, error: fetchError } = await supabase
    .from('payments')
    .select('*, members(full_name, gym_member_id, branch)')
    .eq('gym_id', gid)
    .eq('id', id)
    .maybeSingle();
  if (fetchError) throw fetchError;
  if (!existing) throw new Error('Remaining clear record not found.');

  const source = String(existing.source || '').toLowerCase().replace(/-/g, '_');
  if (source !== 'remaining_clear') {
    throw new Error('This is not a remaining clear payment.');
  }
  if (String(existing.status || '').toLowerCase() === 'voided') {
    throw new Error('This remaining clear was deleted.');
  }

  const splits = serializePaymentSplits(paymentSplits);
  const paid = sumPaymentSplits(splits);
  const nextAmount = paid > 0
    ? paid
    : Math.max(0, Number(amount) || 0);
  if (!(nextAmount > 0)) {
    throw new Error('Enter a valid cleared amount.');
  }
  const nextSplits = splits.length
    ? splits
    : [{ method: 'cash', amount: nextAmount }];
  const nextDay = String(date || existing.paid_at || todayDate()).slice(0, 10);
  const originalRef = remainingClearOriginalRef(existing)
    || existing.invoice_id
    || existing.payment_ref
    || '';
  const userNote = String(paymentNote ?? '').trim()
    .replace(/^Remaining clear\s*·\s*[\d.]+\s*·\s*[^\s·]+\s*·?\s*/i, '')
    .trim();
  // Keep "Remaining clear · amt · REF" so later edit/delete can find the original sale.
  const nextNote = userNote
    ? `Remaining clear · ${nextAmount} · ${originalRef || id} · ${userNote}`
    : `Remaining clear · ${nextAmount} · ${originalRef || id}`;

  const original = await findOriginalSaleForRemainingClear(gid, existing);
  if (original?.id) {
    await reverseRemainingClearFromOriginal(gid, existing);
    await applyRemainingClearOntoOriginal(gid, original.id, {
      amount: nextAmount,
      splits: nextSplits,
      day: nextDay,
      paymentNote: userNote,
    });
  }

  const patch = {
    amount: nextAmount,
    payment_splits: nextSplits,
    payment_method: combineMethodLabels(nextSplits),
    paid_at: nextDay,
    payment_note: nextNote,
    status: 'completed',
    package_price: 0,
  };
  const { data, error } = await updateRowSkippingMissingColumns(
    'payments',
    patch,
    id,
    '*, members(full_name, gym_member_id, branch)',
  );
  if (error) throw error;
  await bustGymCache();
  const embedded = data?.members || existing.members || null;
  return rowToPayment(data || { ...existing, ...patch }, embedded);
}

export async function applyPaymentBalance({
  paymentDbId,
  additionalSplits,
  date,
  paymentNote,
}) {
  if (!paymentDbId) throw new Error('Payment record not found.');

  const gid = await gymId();
  const { data: existing, error: fetchError } = await supabase
    .from('payments')
    .select('*')
    .eq('gym_id', gid)
    .eq('id', paymentDbId)
    .maybeSingle();

  if (fetchError) throw fetchError;
  if (!existing) throw new Error('Payment record not found.');

  const members = await fetchMembers().catch(() => []);
  const member = members.find((m) => m.id === existing.member_id);

  const currentRow = rowToPayment(existing, member);
  const totalDue = paymentTotalDue(currentRow);
  const currentPaid = saleRowPaid(currentRow);
  const additionalPaid = sumPaymentSplits(additionalSplits);

  if (!Number.isFinite(additionalPaid) || additionalPaid <= 0) {
    throw new Error('Enter a valid payment amount.');
  }

  const leftBefore = Math.max(0, totalDue - currentPaid);
  if (leftBefore <= 0) {
    throw new Error('This sale is already fully paid.');
  }
  if (additionalPaid > leftBefore) {
    throw new Error('Payment exceeds the remaining balance.');
  }

  const newPaid = currentPaid + additionalPaid;
  const mergedSplits = mergePaymentSplits(
    parsePaymentSplitsFromRow(currentRow),
    additionalSplits,
  );
  const leftAfter = Math.max(0, totalDue - newPaid);
  const status = leftAfter <= 0 ? 'completed' : 'pending';

  const balanceDay = String(date || todayDate()).slice(0, 10);
  const balanceNote = paymentNote
    ? `Balance payment on ${balanceDay}: ${paymentNote} · ${additionalPaid}`
    : `Balance payment on ${balanceDay} · ${additionalPaid}`;
  const combinedNote = [existing.payment_note, balanceNote].filter(Boolean).join(' · ');

  const row = {
    amount: newPaid,
    payment_method: combineMethodLabels(mergedSplits),
    payment_splits: mergedSplits,
    status,
    payment_note: combinedNote,
  };
  // Keep original paid_at — remaining clear is recorded as its own sale on balanceDay

  const { data, error } = await updateRowSkippingMissingColumns(
    'payments',
    row,
    paymentDbId,
    '*, members(full_name, gym_member_id, branch)',
  );
  if (error) throw error;

  const embedded = data?.members || memberEmbed(member);

  // Detailed Report "Remaining" — only the remaining amount paid
  let remainingClearInvoiceId = '';
  if (balanceDay && additionalPaid > 0) {
    try {
      const serial = String(
        embedded?.gym_member_id
        || member?.gymId
        || member?.gym_member_id
        || '',
      ).trim();
      const pkgLabel = String(existing.package_name || currentRow.package || 'Sale').trim();
      const stamp = String(Date.now()).slice(-6);
      const remainingPay = await createPayment({
        memberId: existing.member_id || member?.id || null,
        member: currentRow.member || existing.member_name || member?.full_name || '—',
        package: pkgLabel,
        amount: additionalPaid,
        method: combineMethodLabels(additionalSplits) || 'Cash',
        paymentSplits: additionalSplits?.length
          ? additionalSplits
          : [{ method: 'cash', amount: additionalPaid }],
        date: balanceDay,
        status: 'completed',
        source: 'remaining_clear',
        paymentNote: `Remaining clear · ${additionalPaid} · ${existing.invoice_id || existing.payment_ref || paymentDbId}`,
        paymentId: `PAY-RC${stamp}`,
        packagePrice: 0,
        registrationFee: 0,
        gymId: serial || undefined,
      });
      remainingClearInvoiceId = remainingPay?.invoiceId || '';
    } catch (clearErr) {
      console.warn('Remaining clear sale row not saved:', clearErr?.message || clearErr);
    }
  }

  await bustGymCache();
  const merged = { ...existing, ...(data || {}), ...row, id: paymentDbId };
  const updated = rowToPayment(merged, embedded);
  updated.pendingAmount = leftAfter;
  if (remainingClearInvoiceId) {
    updated.invoiceId = remainingClearInvoiceId;
  }
  return updated;
}

// ─── Guest passes ──────────────────────────────────────────

/** Paged guest passes for UI. Prefer over fetchGuestPasses(). */
export async function fetchGuestPassesPage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  dateFrom = '',
  dateTo = '',
  filters = {},
} = {}) {
  const gid = await gymId();
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.min(100, Math.max(10, Number(pageSize) || 50));
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;
  const branchFilter = String(branch || getReceptionistBranchFilter() || '').trim();
  if (isReceptionistPanelScope() && !branchFilter) {
    return { rows: [], total: 0, page: safePage, pageSize: safeSize };
  }

  let query = supabase
    .from('guest_passes')
    .select('*', { count: 'exact' })
    .eq('gym_id', gid);

  if (isReceptionistPanelScope() || (branchFilter && branchFilter !== 'all')) {
    const branch = formatPostgrestFilterValue(branchFilter);
    query = query.or(`branch_name.eq.${branch},branch.eq.${branch}`);
  }

  const fromDay = String(dateFrom || '').trim().slice(0, 10);
  const toDay = String(dateTo || '').trim().slice(0, 10);
  if (fromDay && /^\d{4}-\d{2}-\d{2}$/.test(fromDay)) {
    query = query.gte('start_date', fromDay);
  }
  if (toDay && /^\d{4}-\d{2}-\d{2}$/.test(toDay)) {
    query = query.lte('start_date', toDay);
  }

  if (activeColumnFilter(filters.name)) {
    const safe = sanitizeIlike(filters.name);
    query = query.or(`guest_name.ilike.%${safe}%,name.ilike.%${safe}%`);
  }
  if (activeColumnFilter(filters.phone)) query = applyIlikeColumn(query, 'phone', filters.phone);
  if (activeColumnFilter(filters.purpose)) query = applyIlikeColumn(query, 'pass_type', filters.purpose);
  if (activeColumnFilter(filters.branch)) {
    const safe = sanitizeIlike(filters.branch);
    query = query.or(`branch_name.ilike.%${safe}%,branch.ilike.%${safe}%`);
  }
  if (activeColumnFilter(filters.guestPass)) {
    query = applyIlikeColumn(query, 'pass_code', filters.guestPass);
  }
  const visitIso = normalizeDateFilterToIso(activeColumnFilter(filters.visitDate));
  if (visitIso) query = query.eq('start_date', visitIso);
  const followIso = normalizeDateFilterToIso(activeColumnFilter(filters.followUp));
  if (followIso) query = query.eq('follow_up_date', followIso);

  const needle = String(search || '').trim();
  if (needle) {
    const safe = needle.replace(/[%_,.()]/g, ' ').trim();
    if (safe) {
      query = query.or(
        `guest_name.ilike.%${safe}%,name.ilike.%${safe}%,phone.ilike.%${safe}%,pass_type.ilike.%${safe}%`,
      );
    }
  }

  const { data, error, count } = await query
    .order('created_at', { ascending: false })
    .order('id', { ascending: true })
    .range(from, to);
  if (error) throw error;

  const rows = dedupeGuestPasses((data || []).map((row) => ({
    ...rowToGuestPass(row),
    branch: row.branch_name || row.branch || '',
  })));

  return {
    rows,
    total: Number(count) || 0,
    page: safePage,
    pageSize: safeSize,
  };
}

export async function fetchGuestPasses() {
  const gid = await gymId();
  return cachedQuery(cacheKey(`gym:${gid}:guest_passes`), async () => {
    const data = await fetchScopedRows('guest_passes', { order: 'created_at', ascending: false });
    const members = await fetchMembers().catch(() => []);
    const memberIds = new Set(members.map((m) => m.id));
    const mapped = dedupeGuestPasses(data.map((row) => ({
      ...rowToGuestPass(row),
      branch: row.branch_name || row.branch || '',
    })));
    if (!isReceptionistPanelScope()) return mapped;
    const branch = getReceptionistBranchFilter();
    return mapped.filter((g) => (
      branchesMatch(g.branch, branch)
      || (g.referredByMemberId && memberIds.has(g.referredByMemberId))
    ));
  });
}

function resolveGuestPassPageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapGuestPassRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
  });
  return {
    ...payment,
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
  };
}

function inGuestPassDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * Guest Pass sale history from payments (source=guest_pass).
 * RPC first; receptionist always PK today. Fallback dumps then slices.
 */
export async function fetchGuestPassPaymentHistory({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveGuestPassPageDates(filters, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchGuestPassPageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapGuestPassRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Guest Pass page RPC failed, using fallback', err?.message || err);
  }

  return fetchGuestPassPaymentHistoryLegacy({
    page: safePage,
    pageSize: safeSize,
    search,
    branch: branchFilter,
    filters: scopedFilters,
    forExport,
  });
}

function resolveRenewalSalePageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapRenewalSaleRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
  });
  return {
    ...payment,
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
  };
}

function inRenewalSaleDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * Renewal Sale history. RPC first (dash_is_renewal); receptionist always PK today.
 * Fallback: fetchPaymentsPage(source=renewal_sale) then today-slice if receptionist.
 */
export async function fetchRenewalSalePage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveRenewalSalePageDates(filters, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchRenewalSalePageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapRenewalSaleRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Renewal Sale page RPC failed, using fallback', err?.message || err);
  }

  const dateFrom = String(scopedFilters.rangeStart || scopedFilters.dateFrom || '').slice(0, 10);
  const dateTo = String(scopedFilters.rangeEnd || scopedFilters.dateTo || '').slice(0, 10);
  const fallback = await fetchPaymentsPage({
    page: safePage,
    pageSize: safeSize,
    search,
    source: 'renewal_sale',
    branch: branchFilter,
    dateFrom,
    dateTo,
    filters: scopedFilters,
    forExport,
  });
  let rows = fallback.rows || [];
  if (receptionist) {
    rows = rows.filter((row) => inRenewalSaleDateWindow(row, dateFrom, dateTo));
  }
  return {
    rows,
    total: receptionist ? rows.length : (Number(fallback.total) || rows.length),
    collected: rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
    page: fallback.page || safePage,
    pageSize: fallback.pageSize || safeSize,
    fromRpc: false,
  };
}

function resolvePtSalePageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapPtSaleRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
  });
  return {
    ...payment,
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
  };
}

function inPtSaleDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * PT Sale history. RPC first (dash_is_pt_sale); receptionist always PK today.
 * Fallback: fetchPaymentsPage(source=pt_sale) then today-slice if receptionist.
 */
export async function fetchPtSalePage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolvePtSalePageDates(filters, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchPtSalePageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapPtSaleRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('PT Sale page RPC failed, using fallback', err?.message || err);
  }

  const dateFrom = String(scopedFilters.rangeStart || scopedFilters.dateFrom || '').slice(0, 10);
  const dateTo = String(scopedFilters.rangeEnd || scopedFilters.dateTo || '').slice(0, 10);
  const fallback = await fetchPaymentsPage({
    page: safePage,
    pageSize: safeSize,
    search,
    source: 'pt_sale',
    branch: branchFilter,
    dateFrom,
    dateTo,
    filters: scopedFilters,
    forExport,
  });
  let rows = fallback.rows || [];
  if (receptionist) {
    rows = rows.filter((row) => inPtSaleDateWindow(row, dateFrom, dateTo));
  }
  return {
    rows,
    total: receptionist ? rows.length : (Number(fallback.total) || rows.length),
    collected: rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
    page: fallback.page || safePage,
    pageSize: fallback.pageSize || safeSize,
    fromRpc: false,
  };
}

function resolveLockerSalePageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapLockerSaleRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
  });
  return {
    ...payment,
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
  };
}

function inLockerSaleDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * Locker Sale history. RPC first (dash_is_locker_sale); receptionist always PK today.
 * Fallback: fetchPaymentsPage(source=locker_sale) then today-slice if receptionist.
 */
export async function fetchLockerSalePage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveLockerSalePageDates(filters, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchLockerSalePageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapLockerSaleRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Locker Sale page RPC failed, using fallback', err?.message || err);
  }

  const dateFrom = String(scopedFilters.rangeStart || scopedFilters.dateFrom || '').slice(0, 10);
  const dateTo = String(scopedFilters.rangeEnd || scopedFilters.dateTo || '').slice(0, 10);
  const fallback = await fetchPaymentsPage({
    page: safePage,
    pageSize: safeSize,
    search,
    source: 'locker_sale',
    branch: branchFilter,
    dateFrom,
    dateTo,
    filters: scopedFilters,
    forExport,
  });
  let rows = fallback.rows || [];
  if (receptionist) {
    rows = rows.filter((row) => inLockerSaleDateWindow(row, dateFrom, dateTo));
  }
  return {
    rows,
    total: receptionist ? rows.length : (Number(fallback.total) || rows.length),
    collected: rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
    page: fallback.page || safePage,
    pageSize: fallback.pageSize || safeSize,
    fromRpc: false,
  };
}

function resolveSpaSalePageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapSpaSaleRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
  });
  return {
    ...payment,
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
  };
}

function inSpaSaleDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * Spa Sale history. RPC first (dash_is_spa_sale); receptionist always PK today.
 * Fallback: fetchPaymentsPage(source=spa_sale) then today-slice if receptionist.
 */
export async function fetchSpaSalePage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveSpaSalePageDates(filters, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchSpaSalePageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapSpaSaleRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Spa Sale page RPC failed, using fallback', err?.message || err);
  }

  const dateFrom = String(scopedFilters.rangeStart || scopedFilters.dateFrom || '').slice(0, 10);
  const dateTo = String(scopedFilters.rangeEnd || scopedFilters.dateTo || '').slice(0, 10);
  const fallback = await fetchPaymentsPage({
    page: safePage,
    pageSize: safeSize,
    search,
    source: 'spa_sale',
    branch: branchFilter,
    dateFrom,
    dateTo,
    filters: scopedFilters,
    forExport,
  });
  let rows = fallback.rows || [];
  if (receptionist) {
    rows = rows.filter((row) => inSpaSaleDateWindow(row, dateFrom, dateTo));
  }
  return {
    rows,
    total: receptionist ? rows.length : (Number(fallback.total) || rows.length),
    collected: rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
    page: fallback.page || safePage,
    pageSize: fallback.pageSize || safeSize,
    fromRpc: false,
  };
}

function resolveOxybarSalePageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapOxybarSaleRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
  });
  return {
    ...payment,
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
  };
}

function inOxybarSaleDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * Oxybar Sale history. RPC first (dash_is_oxybar_sale); receptionist always PK today.
 * Fallback: fetchPaymentsPage(source=oxybar_sale) then today-slice if receptionist.
 */
export async function fetchOxybarSalePage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveOxybarSalePageDates(filters, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchOxybarSalePageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapOxybarSaleRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Oxybar Sale page RPC failed, using fallback', err?.message || err);
  }

  const dateFrom = String(scopedFilters.rangeStart || scopedFilters.dateFrom || '').slice(0, 10);
  const dateTo = String(scopedFilters.rangeEnd || scopedFilters.dateTo || '').slice(0, 10);
  const fallback = await fetchPaymentsPage({
    page: safePage,
    pageSize: safeSize,
    search,
    source: 'oxybar_sale',
    branch: branchFilter,
    dateFrom,
    dateTo,
    filters: scopedFilters,
    forExport,
  });
  let rows = fallback.rows || [];
  if (receptionist) {
    rows = rows.filter((row) => inOxybarSaleDateWindow(row, dateFrom, dateTo));
  }
  return {
    rows,
    total: receptionist ? rows.length : (Number(fallback.total) || rows.length),
    collected: rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
    page: fallback.page || safePage,
    pageSize: fallback.pageSize || safeSize,
    fromRpc: false,
  };
}

function resolveZumbaSalePageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapZumbaSaleRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
  });
  return {
    ...payment,
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
  };
}

function inZumbaSaleDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * Zumba Sale history. RPC first (dash_is_zumba_sale); receptionist always PK today.
 * Fallback: fetchPaymentsPage(source=zumba_sale) then today-slice if receptionist.
 */
export async function fetchZumbaSalePage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveZumbaSalePageDates(filters, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchZumbaSalePageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapZumbaSaleRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Zumba Sale page RPC failed, using fallback', err?.message || err);
  }

  const dateFrom = String(scopedFilters.rangeStart || scopedFilters.dateFrom || '').slice(0, 10);
  const dateTo = String(scopedFilters.rangeEnd || scopedFilters.dateTo || '').slice(0, 10);
  const fallback = await fetchPaymentsPage({
    page: safePage,
    pageSize: safeSize,
    search,
    source: 'zumba_sale',
    branch: branchFilter,
    dateFrom,
    dateTo,
    filters: scopedFilters,
    forExport,
  });
  let rows = fallback.rows || [];
  if (receptionist) {
    rows = rows.filter((row) => inZumbaSaleDateWindow(row, dateFrom, dateTo));
  }
  return {
    rows,
    total: receptionist ? rows.length : (Number(fallback.total) || rows.length),
    collected: rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
    page: fallback.page || safePage,
    pageSize: fallback.pageSize || safeSize,
    fromRpc: false,
  };
}

function resolveYogaSalePageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapYogaSaleRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
  });
  return {
    ...payment,
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
  };
}

function inYogaSaleDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * Yoga Sale history. RPC first (dash_is_yoga_sale); receptionist always PK today.
 * Fallback: fetchPaymentsPage(source=yoga_sale) then today-slice if receptionist.
 */
export async function fetchYogaSalePage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveYogaSalePageDates(filters, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchYogaSalePageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapYogaSaleRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Yoga Sale page RPC failed, using fallback', err?.message || err);
  }

  const dateFrom = String(scopedFilters.rangeStart || scopedFilters.dateFrom || '').slice(0, 10);
  const dateTo = String(scopedFilters.rangeEnd || scopedFilters.dateTo || '').slice(0, 10);
  const fallback = await fetchPaymentsPage({
    page: safePage,
    pageSize: safeSize,
    search,
    source: 'yoga_sale',
    branch: branchFilter,
    dateFrom,
    dateTo,
    filters: scopedFilters,
    forExport,
  });
  let rows = fallback.rows || [];
  if (receptionist) {
    rows = rows.filter((row) => inYogaSaleDateWindow(row, dateFrom, dateTo));
  }
  return {
    rows,
    total: receptionist ? rows.length : (Number(fallback.total) || rows.length),
    collected: rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
    page: fallback.page || safePage,
    pageSize: fallback.pageSize || safeSize,
    fromRpc: false,
  };
}

function resolveGymApparelPageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapGymApparelRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
  });
  return {
    ...payment,
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
  };
}

function inGymApparelDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * Gym Apparel history. RPC first (dash_is_gym_apparel); receptionist always PK today.
 * Fallback: fetchPaymentsPage(source=gym_apparel) then today-slice if receptionist.
 */
export async function fetchGymApparelPage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveGymApparelPageDates(filters, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchGymApparelPageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapGymApparelRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Gym Apparel page RPC failed, using fallback', err?.message || err);
  }

  const dateFrom = String(scopedFilters.rangeStart || scopedFilters.dateFrom || '').slice(0, 10);
  const dateTo = String(scopedFilters.rangeEnd || scopedFilters.dateTo || '').slice(0, 10);
  const fallback = await fetchPaymentsPage({
    page: safePage,
    pageSize: safeSize,
    search,
    source: 'gym_apparel',
    branch: branchFilter,
    dateFrom,
    dateTo,
    filters: scopedFilters,
    forExport,
  });
  let rows = fallback.rows || [];
  if (receptionist) {
    rows = rows.filter((row) => inGymApparelDateWindow(row, dateFrom, dateTo));
  }
  return {
    rows,
    total: receptionist ? rows.length : (Number(fallback.total) || rows.length),
    collected: rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
    page: fallback.page || safePage,
    pageSize: fallback.pageSize || safeSize,
    fromRpc: false,
  };
}

function resolveMiscellaneousSalePageDates(filters = {}, opts = {}) {
  return resolveReceptionistListDates(filters, opts);
}

function mapMiscellaneousSaleRpcRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
  });
  return {
    ...payment,
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
  };
}

function inMiscellaneousSaleDateWindow(row, from, to) {
  if (!from && !to) return true;
  const day = String(row.date || row.paidAt || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/**
 * Miscellaneous Sale history. RPC first (dash_is_miscellaneous_sale); receptionist always PK today.
 * Fallback: fetchPaymentsPage(source=miscellaneous_sale) then today-slice if receptionist.
 */
export async function fetchMiscellaneousSalePage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const receptionist = isReceptionistPanelScope();
  const scopedFilters = resolveMiscellaneousSalePageDates(filters, { receptionist, search });
  let branchFilter = String(
    branch
    || scopedFilters.branch
    || getReceptionistBranchFilter()
    || getReceptionistBranch()
    || '',
  ).trim();
  if (branchFilter === 'all') branchFilter = '';

  if (receptionist && !branchFilter) {
    try {
      const profile = await fetchCurrentReceptionistProfile();
      const profileBranch = String(profile?.branch || '').trim();
      if (profileBranch) {
        branchFilter = profileBranch;
        const { setReceptionistBranch } = await import('../gym/branchContext');
        setReceptionistBranch(profileBranch);
      }
    } catch {
      /* no leak */
    }
  }

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = forExport
    ? Math.min(20000, Math.max(10, Number(pageSize) || 500))
    : Math.min(100, Math.max(10, Number(pageSize) || 50));

  if (receptionist && !branchFilter) {
    return { rows: [], total: 0, collected: 0, page: safePage, pageSize: safeSize, fromRpc: true };
  }

  try {
    const rpc = await fetchMiscellaneousSalePageRpc({
      gymId: gid,
      page: safePage,
      pageSize: safeSize,
      search,
      branch: branchFilter,
      filters: scopedFilters,
      allMatching: forExport,
    });
    if (rpc) {
      const rows = (rpc.rows || []).map(mapMiscellaneousSaleRpcRow);
      return {
        rows,
        total: rpc.total,
        collected: Number(rpc.collected) || rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
        page: rpc.page,
        pageSize: rpc.pageSize,
        fromRpc: true,
      };
    }
  } catch (err) {
    console.warn('Miscellaneous Sale page RPC failed, using fallback', err?.message || err);
  }

  const dateFrom = String(scopedFilters.rangeStart || scopedFilters.dateFrom || '').slice(0, 10);
  const dateTo = String(scopedFilters.rangeEnd || scopedFilters.dateTo || '').slice(0, 10);
  const fallback = await fetchPaymentsPage({
    page: safePage,
    pageSize: safeSize,
    search,
    source: 'miscellaneous_sale',
    branch: branchFilter,
    dateFrom,
    dateTo,
    filters: scopedFilters,
    forExport,
  });
  let rows = fallback.rows || [];
  if (receptionist) {
    rows = rows.filter((row) => inMiscellaneousSaleDateWindow(row, dateFrom, dateTo));
  }
  return {
    rows,
    total: receptionist ? rows.length : (Number(fallback.total) || rows.length),
    collected: rows.reduce((sum, row) => sum + saleRowPaid(row), 0),
    page: fallback.page || safePage,
    pageSize: fallback.pageSize || safeSize,
    fromRpc: false,
  };
}

async function fetchGuestPassPaymentHistoryLegacy({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
  forExport = false,
} = {}) {
  const gid = await gymId();
  const from = String(filters.rangeStart || filters.dateFrom || '').slice(0, 10);
  const to = String(filters.rangeEnd || filters.dateTo || '').slice(0, 10);
  const needle = String(search || '').trim().toLowerCase();

  let data = [];
  try {
    data = await fetchAllPaged(() => (
      supabase
        .from('payments')
        .select('*, members(full_name, gym_member_id, branch)')
        .eq('gym_id', gid)
        .eq('source', 'guest_pass')
        .order('paid_at', { ascending: false })
        .order('id', { ascending: true })
    ));
  } catch (error) {
    if (/branch/i.test(error.message || '') || isEmbedError(error.message || '')) {
      data = await fetchAllPaged(() => (
        supabase
          .from('payments')
          .select('*')
          .eq('gym_id', gid)
          .eq('source', 'guest_pass')
          .order('paid_at', { ascending: false })
          .order('id', { ascending: true })
      ));
    } else {
      throw error;
    }
  }

  let rows = (data || []).map((row) => {
    const embedded = row.members
      ? { ...row.members, baseGymId: row.members.gym_member_id }
      : null;
    const payment = rowToPayment(row, embedded);
    return {
      ...payment,
      branch: embedded?.branch || row.branch_name || row.branch || '',
    };
  });

  if (branch && branch !== 'all') {
    rows = rows.filter((p) => {
      const rowBranch = String(p.branch || '').trim();
      if (!normalizeBranchName(rowBranch)) return true;
      return branchesMatch(rowBranch, branch);
    });
  }

  rows = rows.filter((row) => inGuestPassDateWindow(row, from, to));
  if (needle) {
    rows = rows.filter((row) => {
      const blob = [
        row.member,
        row.gymId,
        row.package,
        row.paymentNote,
        row.duration,
      ].join(' ').toLowerCase();
      return blob.includes(needle);
    });
  }

  const collected = rows.reduce((sum, row) => sum + saleRowPaid(row), 0);
  const total = rows.length;
  if (!forExport) {
    const start = (Math.max(1, page) - 1) * pageSize;
    rows = rows.slice(start, start + pageSize);
  }
  return { rows, total, collected, page, pageSize, fromRpc: false };
}

export async function createGuestPass(form) {
  const gid = await gymId();
  if (!gid) {
    throw new Error(formatSupabaseError(null, 'Gym context is missing. Sign in again.'));
  }
  const duration = form.duration || '1d';
  const startDate = dateOrNull(form.startDate || form.start_date);
  if (!startDate) {
    throw new Error('Guest pass start date is required.');
  }
  const endDate = dateOrNull(form.endDate || form.end_date);
  const guestName = String(form.name || '').trim();
  const phone = String(form.phone || '').trim();
  if (!guestName) throw new Error('Guest name is required.');
  if (!phone) throw new Error('Phone number is required.');

  const referredMemberId = isUuidValue(form.referredByMemberId) ? form.referredByMemberId : null;

  // Do not stamp branch_* — guest_passes has no branch columns on this schema.
  const row = {
    gym_id: gid,
    guest_name: guestName,
    phone,
    pass_type: duration,
    duration,
    start_date: startDate,
    fee: Number(form.fee) || 0,
    referred_by_member_id: referredMemberId,
    referred_by_name: form.referredBy || null,
    referred_by_gym_id: form.referredByGymId || null,
    created_at: new Date().toISOString(),
  };
  if (endDate) row.end_date = endDate;

  const { data, error } = await insertRowSkippingMissingColumns('guest_passes', row, '*');
  if (error) throw new Error(formatSupabaseError(error, 'Failed to save guest pass.'));
  await bustGymCache();
  return rowToGuestPass(data);
}

export async function deleteGuestPass(id) {
  const { error } = await deleteRowsQueued('guest_passes', { id });
  if (error) throw error;
  await bustGymCache();
}

// ─── Lockers ───────────────────────────────────────────────

/** Paged lockers for UI. Prefer over fetchLockers(). */
export async function fetchLockersPage({
  page = 1,
  pageSize = 50,
  search = '',
  branch = '',
  filters = {},
} = {}) {
  const gid = await gymId();
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.min(100, Math.max(10, Number(pageSize) || 50));
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;
  const branchFilter = String(branch || getReceptionistBranchFilter() || '').trim();
  if (isReceptionistPanelScope() && !branchFilter) {
    return { rows: [], total: 0, page: safePage, pageSize: safeSize };
  }

  let query = supabase
    .from('lockers')
    .select('*', { count: 'exact' })
    .eq('gym_id', gid);

  if (isReceptionistPanelScope() || (branchFilter && branchFilter !== 'all')) {
    // Include unassigned/available lockers without a branch stamp
    const branch = formatPostgrestFilterValue(branchFilter);
    query = query.or(
      `branch_name.eq.${branch},branch.eq.${branch},and(status.eq.available,branch_name.is.null,branch.is.null)`,
    );
  }

  const lockerId = activeColumnFilter(filters.id);
  if (lockerId) query = applyIlikeColumn(query, 'locker_code', lockerId);
  const lockerMember = activeColumnFilter(filters.member);
  const lockerSerial = activeColumnFilter(filters.gymId || filters.serialNo);
  if (lockerMember || lockerSerial) {
    const memberIds = await findMemberIdsForTableFilters(gid, {
      serialNeedle: lockerSerial,
      nameNeedle: lockerMember,
      branchFilter,
    });
    if (lockerSerial && !memberIds?.length) {
      return { rows: [], total: 0, page: safePage, pageSize: safeSize };
    }
    if (lockerMember) {
      const orParts = [`member_name.ilike.%${sanitizeIlike(lockerMember)}%`];
      if (memberIds?.length) orParts.push(`member_id.in.(${memberIds.join(',')})`);
      query = query.or(orParts.join(','));
    } else if (memberIds?.length) {
      query = query.in('member_id', memberIds);
    }
  }
  if (activeColumnFilter(filters.size)) query = applyIlikeColumn(query, 'size', filters.size);
  if (activeColumnFilter(filters.expiry)) {
    query = applyDateColumnEq(query, 'expiry_date', filters.expiry);
  }
  const lockerStatus = activeColumnFilter(filters.status);
  if (lockerStatus) {
    const statusKey = /assigned/i.test(lockerStatus)
      ? 'assigned'
      : /available/i.test(lockerStatus)
        ? 'available'
        : lockerStatus.toLowerCase();
    query = query.eq('status', statusKey);
  }

  const needle = String(search || '').trim();
  if (needle) {
    const safe = needle.replace(/[%_,.()]/g, ' ').trim();
    if (safe) {
      let memberQuery = supabase
        .from('members')
        .select('id')
        .eq('gym_id', gid)
        .or(memberNameAndSerialSearchOr(safe))
        .limit(300);
      if (isReceptionistPanelScope() || (branchFilter && branchFilter !== 'all')) {
        memberQuery = applyMemberBranchOrFilter(memberQuery, branchFilter);
      }
      const { data: hits } = await memberQuery;
      const ids = (hits || []).map((m) => m.id).filter(Boolean);
      const orParts = [
        `locker_code.ilike.%${safe}%`,
        `member_name.ilike.%${safe}%`,
        `size.ilike.%${safe}%`,
      ];
      if (ids.length) orParts.push(`member_id.in.(${ids.join(',')})`);
      query = query.or(orParts.join(','));
    }
  }

  const { data, error, count } = await query
    .order('locker_code', { ascending: true })
    .order('id', { ascending: true })
    .range(from, to);
  if (error) throw error;

  const rows = dedupeLockers((data || []).map((row) => ({
    ...rowToLocker(row),
    branch: row.branch_name || row.branch || '',
  })));

  return {
    rows,
    total: Number(count) || 0,
    page: safePage,
    pageSize: safeSize,
  };
}

export async function fetchLockers() {
  const gid = await gymId();
  return cachedQuery(cacheKey(`gym:${gid}:lockers`), async () => {
    const data = await fetchScopedRows('lockers', { order: 'locker_code', ascending: true });
    const members = await fetchMembers().catch(() => []);
    const memberById = new Map(members.map((m) => [m.id, m]));
    const mapped = dedupeLockers(data.map((row) => {
      const locker = rowToLocker(row);
      return {
        ...locker,
        branch: row.branch_name || row.branch || memberById.get(row.member_id)?.branch || '',
      };
    }));
    if (!isReceptionistPanelScope()) return mapped;
    const branch = getReceptionistBranchFilter();
    return mapped.filter((l) => {
      if (branchesMatch(l.branch, branch)) return true;
      if (l.memberId && memberById.has(l.memberId)) return true;
      // Shared unassigned lockers (not tagged to another branch yet)
      if (l.status === 'available' && !String(l.branch || '').trim()) return true;
      return false;
    });
  });
}

export async function saveLocker(form, dbId) {
  const gid = await gymId();
  const row = stampBranchFields({
    gym_id: gid,
    locker_code: form.lockerId,
    size: form.size,
    member_name: form.member || null,
    member_id: form.memberId || null,
    expiry_date: form.expiry || null,
    status: form.member ? 'assigned' : 'available',
  });

  if (dbId) {
    const { data, error } = await updateRowSkippingMissingColumns('lockers', row, dbId, '*');
    if (error) throw error;
    await bustGymCache();
    return rowToLocker(data);
  }

  const { data, error } = await insertRowSkippingMissingColumns('lockers', row, '*');
  if (error) throw error;
  await bustGymCache();
  return rowToLocker(data);
}

export async function deleteLocker(dbId) {
  const { error } = await deleteRowsQueued('lockers', { id: dbId });
  if (error) throw error;
  await bustGymCache();
}

// ─── Equipment ─────────────────────────────────────────────

export async function fetchEquipments() {
  const gid = await gymId();
  return cachedQuery(`gym:${gid}:equipments`, async () => {
    const data = await fetchScopedRows('equipments', { order: 'name', ascending: true });
    return dedupeByName(data.map(rowToEquipment));
  });
}

function normalizeEquipmentStatus(status) {
  const value = String(status || 'available').toLowerCase().trim();
  const aliases = {
    'in_use': 'in-use',
    'in use': 'in-use',
    active: 'available',
    operational: 'available',
    inactive: 'maintenance',
    repair: 'maintenance',
    'out of service': 'maintenance',
  };

  if (['available', 'in-use', 'maintenance'].includes(value)) {
    return value;
  }

  return aliases[value] || 'available';
}

export async function saveEquipment(form, id) {
  const gid = await gymId();
  const quantity = Math.max(1, Number(form.quantity) || 1);
  const row = {
    gym_id: gid,
    name: form.name,
    company_name: String(form.companyName || '').trim() || null,
    category: form.category,
    location: form.location,
    quantity,
    status: normalizeEquipmentStatus(form.status),
    remarks: String(form.remarks || '').trim() || null,
  };

  if (id) {
    const { data, error } = await updateRowSkippingMissingColumns('equipments', row, id, '*');
    if (error) throw error;
    await bustGymCache();
    return rowToEquipment(data);
  }

  const { data, error } = await insertRowSkippingMissingColumns('equipments', row, '*');
  if (error) throw error;
  await bustGymCache();
  return rowToEquipment(data);
}

export async function deleteEquipment(id) {
  const { error } = await deleteRowsQueued('equipments', { id });
  if (error) throw error;
  await bustGymCache();
}

// ─── Classes ───────────────────────────────────────────────

export async function fetchClasses() {
  const gid = await gymId();
  return cachedQuery(`gym:${gid}:classes`, async () => {
    const data = await fetchScopedRows('classes', { order: 'name', ascending: true });
    return dedupeByName(data.map(rowToClass));
  });
}

export async function saveClass(form, id) {
  const gid = await gymId();
  const row = {
    gym_id: gid,
    name: form.name,
    start_date: form.startDate,
    end_date: form.endDate,
    description: form.description,
    fee: form.fee || 0,
  };

  if (id) {
    const { data, error } = await updateRowSkippingMissingColumns('classes', row, id, '*');
    if (error) throw error;
    await bustGymCache();
    return rowToClass(data);
  }

  const { data, error } = await insertRowSkippingMissingColumns('classes', row, '*');
  if (error) throw error;
  await bustGymCache();
  return rowToClass(data);
}

export async function deleteClass(id) {
  const { error } = await deleteRowsQueued('classes', { id });
  if (error) throw error;
  await bustGymCache();
}

// ─── Trainers ──────────────────────────────────────────────

export async function fetchTrainers() {
  const gid = await gymId();
  return cachedQuery(cacheKey(`gym:${gid}:trainers`), async () => {

  const { data, error } = await supabase
    .from('trainers')
    .select('*')
    .eq('gym_id', gid)
    .order('full_name', { ascending: true });
  if (error) throw error;
  const mapped = dedupePeople((data || []).map(rowToTrainer));
  return filterByReceptionistBranch(mapped, (t) => t.branch);
  });
}

export async function saveTrainer(form, id) {
  const gid = await gymId();
  const branchName = form.branch || null;
  const row = {
    gym_id: gid,
    full_name: form.name,
    email: form.email,
    phone: form.phone,
    specialization: form.specialization,
    branch: branchName,
    branch_name: branchName,
    salary: Number(form.salary) || 0,
    certifications: typeof form.certifications === 'string'
      ? form.certifications
      : (form.certifications || []).join('\n'),
    status: form.status || 'active',
  };

  if (id) {
    const { data, error } = await updateRowSkippingMissingColumns('trainers', row, id, '*');
    if (error) {
      if (/trainers.*schema cache|relation.*trainers.*does not exist/i.test(error.message)) {
        throw new Error(
          'Trainers table not found. Open Supabase → SQL Editor, run supabase/fix_trainers_columns.sql, then refresh.',
        );
      }
      if (/branch.*schema cache/i.test(error.message)) {
        throw new Error(
          'Trainers table is missing the branch column. Run supabase/fix_trainers_columns.sql in Supabase SQL Editor, then refresh.',
        );
      }
      throw error;
    }
    await bustGymCache();
    return rowToTrainer(data);
  }

  const { data, error } = await insertRowSkippingMissingColumns('trainers', row, '*');
  if (error) {
    if (/trainers.*schema cache|relation.*trainers.*does not exist/i.test(error.message)) {
      throw new Error(
        'Trainers table not found. Open Supabase → SQL Editor, run supabase/fix_trainers_columns.sql, then refresh.',
      );
    }
    if (/branch.*schema cache/i.test(error.message)) {
      throw new Error(
        'Trainers table is missing the branch column. Run supabase/fix_trainers_columns.sql in Supabase SQL Editor, then refresh.',
      );
    }
    throw error;
  }
  await bustGymCache();
  return rowToTrainer(data);
}

export async function deleteTrainer(id) {
  const { error } = await deleteRowsQueued('trainers', { id });
  if (error) throw error;
  await bustGymCache();
}

// ─── Receptionists ─────────────────────────────────────────

function throwIfReceptionistsTableMissing(error) {
  if (error && /receptionists.*schema cache|relation.*receptionists.*does not exist/i.test(error.message)) {
    throw new Error(
      'Receptionists table not found. Open Supabase → SQL Editor, run supabase/fix_receptionist_setup.sql, then refresh this page.',
    );
  }
}

async function fetchReceptionistRowById(id) {
  const { data, error } = await supabase
    .from('receptionists')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    throwIfReceptionistsTableMissing(error);
    throw error;
  }

  return data;
}

export async function fetchReceptionists() {
  const gid = await gymId();
  return cachedQuery(`gym:${gid}:receptionists`, async () => {
  let query = supabase.from('receptionists').select('*').eq('gym_id', gid).order('full_name', { ascending: true });
  const { data, error } = await query;
  if (error) {
    throwIfReceptionistsTableMissing(error);
    throw error;
  }
  if (data?.length) return dedupePeople(data.map(rowToReceptionist));
  return [];
  });
}

async function resolveBranchForForm(form) {
  const branchId = form.branchId?.trim?.() || form.branchId || '';
  const branchName = (form.branch || form.branchName || '').trim();

  if (!branchId && !branchName) {
    throw new Error('Please select a branch for this receptionist.');
  }

  const branches = await fetchBranches();

  let match = null;
  if (branchId) {
    match = branches.find((b) => b.id === branchId);
  }
  if (!match && branchName) {
    const needle = branchName.toLowerCase();
    match = branches.find((b) => (b.name || '').trim().toLowerCase() === needle);
  }

  if (!match && branchId && !branchName) {
    throw new Error('Selected branch was not found. Refresh the page and try again.');
  }

  return {
    branch_id: match?.id || (branchId || null),
    branch_name: match?.name || branchName || null,
  };
}

export async function saveReceptionist(form, id) {
  const gid = await gymId();
  const branch = await resolveBranchForForm(form);

  if (!branch.branch_name) {
    throw new Error('Please select a branch for this receptionist.');
  }

  const row = {
    gym_id: gid,
    full_name: form.name.trim(),
    email: form.email?.trim() || null,
    phone: form.phone?.trim() || null,
    branch_id: branch.branch_id,
    branch_name: branch.branch_name,
    shift: form.shift || null,
    status: form.status || 'active',
  };

  if (id) {
    const { data: updatedRows, error } = await supabase
      .from('receptionists')
      .update(row)
      .eq('id', id)
      .select('*');
    if (error) {
      throwIfReceptionistsTableMissing(error);
      if (/branch_id|branch_name|schema cache/i.test(error.message || '')) {
        throw new Error(
          'Receptionists table is missing branch columns. Run supabase/fix_receptionist_setup.sql in Supabase SQL Editor, then try again.',
        );
      }
      throw error;
    }
    const data = Array.isArray(updatedRows) ? updatedRows[0] : updatedRows;
    if (!data) {
      throw new Error('Receptionist update failed — no row returned. Check RLS policies on receptionists.');
    }

    let authWarning = null;
    let passwordChanged = false;
    if (form.password?.trim()) {
      const { createReceptionistAuth, updateReceptionistAuthPassword } = await import('../auth/authApi');
      const hasLogin = Boolean(data.auth_user_id || data.has_login);
      if (hasLogin) {
        await updateReceptionistAuthPassword({
          receptionistId: id,
          newPassword: form.password,
        });
        passwordChanged = true;
      } else {
        try {
          await createReceptionistAuth({
            email: form.email,
            password: form.password,
            fullName: form.name,
            gymId: gid,
            receptionistId: id,
          });
        } catch (authErr) {
          authWarning = authErr.message || 'Could not create receptionist login.';
        }
      }
    }

    await bustGymCache();
    return { ...rowToReceptionist(data), authWarning, passwordChanged };
  }

  const { data: insertedRows, error } = await (async () => {
    if (shouldQueueWrites()) {
      const queued = await queueInsert('receptionists', stampCreatedAt(row), { source: 'receptionist-helper' });
      return { data: queued.data ? [queued.data] : [], error: queued.error };
    }
    return supabase.from('receptionists').insert(stampCreatedAt(row)).select('*');
  })();
  if (error) {
    throwIfReceptionistsTableMissing(error);
    if (/multiple \(or no\) rows returned|JSON object requested/i.test(error.message || '')) {
      // Insert may have succeeded under RLS that hides SELECT — recover by email
    } else if (/branch_id|branch_name|schema cache/i.test(error.message || '')) {
      throw new Error(
        'Receptionists table is missing branch columns. Run supabase/fix_receptionist_setup.sql in Supabase SQL Editor, then try again.',
      );
    } else {
    throw error;
  }
  }

  let data = Array.isArray(insertedRows) ? insertedRows[0] : insertedRows;

  if (!data?.id && row.email) {
    const { data: found } = await supabase
      .from('receptionists')
      .select('*')
      .eq('gym_id', gid)
      .ilike('email', row.email)
      .order('created_at', { ascending: false })
      .limit(1);
    data = found?.[0] || null;
  }

  if (!data?.id) {
    throw new Error(
      'Receptionist could not be loaded after save. Refresh the Receptionists page — they may already be listed.',
    );
  }

  // If DB returned null branch fields, force-update (covers schema-cache / partial write edge cases)
  if (data && (!data.branch_name || !data.branch_id) && (row.branch_name || row.branch_id)) {
    const { data: patchedRows, error: patchError } = await supabase
      .from('receptionists')
      .update({
        branch_id: row.branch_id,
        branch_name: row.branch_name,
      })
      .eq('id', data.id)
      .select('*');
    const patched = Array.isArray(patchedRows) ? patchedRows[0] : patchedRows;
    if (!patchError && patched) {
      Object.assign(data, patched);
    }
  }

  let authWarning = null;
  if (form.password?.trim()) {
    try {
      const { createReceptionistAuth } = await import('../auth/authApi');
      await createReceptionistAuth({
        email: form.email,
        password: form.password,
        fullName: form.name,
        gymId: gid,
        receptionistId: data.id,
      });
    } catch (authErr) {
      authWarning = authErr.message || 'Could not create receptionist login.';
    }
  }

  await bustGymCache();
  return { ...rowToReceptionist(data), authWarning };
}

export async function deleteReceptionist(id) {
  if (!id) throw new Error('Receptionist id is required.');

  const existing = await fetchReceptionistRowById(id);
  if (!existing) {
    throw new Error('Receptionist not found.');
  }

  const authUserId = existing.auth_user_id || null;
  const email = String(existing.email || existing.login_email || '').trim();

  // Remove login account first so the email can be reused later
  if (authUserId) {
    await supabase.from('user_accounts').delete().eq('auth_user_id', authUserId);
  }

  // Also clear by staff link / email in case auth_user_id was missing on the receptionist row
  await supabase
    .from('user_accounts')
    .delete()
    .eq('role', 'receptionist')
    .eq('staff_id', id);

  if (email) {
    await supabase
      .from('user_accounts')
      .delete()
      .eq('role', 'receptionist')
      .ilike('email', email);
  }

  const { error } = await deleteRowsQueued('receptionists', { id });
  if (error) {
    throwIfReceptionistsTableMissing(error);
    throw error;
  }

  await bustGymCache();
}

export async function fetchCurrentReceptionistProfile() {
  const gid = await gymId();
  let receptionistRow = null;

  const { data: authData } = await supabase.auth.getUser();
  const authUser = authData?.user;

  if (authUser?.id) {
    const { data, error } = await supabase
      .from('receptionists')
      .select('*')
      .eq('auth_user_id', authUser.id)
      .maybeSingle();
    if (!error && data) receptionistRow = data;
  }

  if (!receptionistRow) {
    const preferredId = getCurrentReceptionistId();
  if (preferredId) {
    receptionistRow = await fetchReceptionistRowById(preferredId);
    }
  }

  if (!receptionistRow && authUser?.email) {
    const { data, error } = await supabase
      .from('receptionists')
      .select('*')
      .eq('gym_id', gid)
      .ilike('email', authUser.email.trim())
      .maybeSingle();
    if (!error && data) receptionistRow = data;
  }

  if (!receptionistRow) return null;

  let mapped = rowToReceptionist(receptionistRow);

  if (!mapped.branch && receptionistRow.branch_id) {
    const { data: branch } = await supabase
      .from('branches')
      .select('id, name')
      .eq('id', receptionistRow.branch_id)
      .maybeSingle();
    if (branch?.name) {
      mapped = { ...mapped, branch: branch.name, branchId: branch.id };
      await supabase
        .from('receptionists')
        .update({ branch_name: branch.name })
        .eq('id', receptionistRow.id);
    }
  }

  setCurrentReceptionistId(receptionistRow.id);
  return mapped;
}

export async function updateReceptionistProfile(updates) {
  const current = await fetchCurrentReceptionistProfile();
  if (!current?.id) {
    throw new Error('No receptionist profile found. Ask your administrator to add you in Receptionist Management.');
  }

  const row = {
    full_name: updates.name?.trim() || current.name,
    // Login email is fixed — never change it from the profile form
    email: current.email || null,
    phone: updates.phone?.trim() || null,
  };

  const { data, error } = await supabase
    .from('receptionists')
    .update(row)
    .eq('id', current.id)
    .select('*')
    .single();

  if (error) {
    throwIfReceptionistsTableMissing(error);
    throw error;
  }

  await bustGymCache();
  return rowToReceptionist(data);
}

export async function syncReceptionistBranchFromProfile() {
  const profile = await fetchCurrentReceptionistProfile();
  const { setReceptionistBranch, RECEPTIONIST_BRANCH_EVENT } = await import('../gym/branchContext');
  if (profile?.branch) {
    setReceptionistBranch(profile.branch);
  } else {
    setReceptionistBranch('');
  }
  // Always notify — setReceptionistBranch no-ops when value unchanged, which
  // left Guest Pass / sale pages stuck on "0 sales" after refresh.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(RECEPTIONIST_BRANCH_EVENT, {
      detail: { branch: profile?.branch || '', synced: true },
    }));
  }
  return profile;
}

// ─── Packages ──────────────────────────────────────────────

export async function fetchPackages(branchName) {
  const { fetchActivePackagesForBranch } = await import('../gym/catalogApi');
  return fetchActivePackagesForBranch(branchName || '');
}
